import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
// Версия: 2026-09-05 (роль Зарплата — ведомость по периодам актов)

const API = "";  // пустая строка = тот же домен

const api = axios.create({ baseURL: API });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Загружает фото ПО ОДНОМУ (не единым запросом), чтобы:
// 1) было видно живой прогресс — сколько из выбранных фото реально дошло,
//    и сколько процентов передалось по текущему файлу (важно на медленном 3G/4G);
// 2) при обрыве мобильного интернета посреди загрузки уже принятые сервером
//    фото не терялись — теряется максимум одно, текущее, а не вся пачка.
// onStatus(index, "uploading"|"done"|"error", percent) вызывается на каждом шаге;
// percent (0-100) актуален только пока status === "uploading".
async function uploadPhotosSequential(ticketId, files, photoType, onStatus) {
  let allOk = true;
  for (let i = 0; i < files.length; i++) {
    onStatus?.(i, "uploading", 0);
    try {
      const fd = new FormData();
      fd.append("photos", files[i]);
      fd.append("photoType", photoType);
      await api.post(`/api/tickets/${ticketId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          onStatus?.(i, "uploading", Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onStatus?.(i, "done", 100);
    } catch (e) {
      onStatus?.(i, "error", 0);
      allOk = false;
    }
  }
  if (!allOk) throw new Error("Не все фото удалось загрузить");
}

const uploadIcon = (status) => status === "done" ? "✅" : status === "error" ? "❌" : status === "uploading" ? "🔄" : "⏳";
// Компактная подпись под миниатюрой/в списке: "42%" во время загрузки, иначе просто иконка
const uploadLabel = (status, percent) => status === "uploading" ? `${percent ?? 0}%` : uploadIcon(status);

const STATUS_META = {
  unassigned:          { label: "Нераспределена",        color: "#f59e0b", bg: "#f59e0b18" },
  in_work:             { label: "В работе",               color: "#2563eb", bg: "#2563eb12" },
  awaiting_parts:      { label: "Ожидаем запчасти",        color: "#7c3aed", bg: "#7c3aed12" },
  parts_clarification: { label: "Уточнение запчастей",     color: "#b45309", bg: "#b4530918" },
  parts_ordered:       { label: "Запчасти заказаны",       color: "#0891b2", bg: "#0891b212" },
  approval:            { label: "Согласование",            color: "#dc2626", bg: "#dc262618" },
  approval_sent:       { label: "Отправлено на согласование", color: "#ca8a04", bg: "#ca8a0418" },
  approval_approved:   { label: "Согласовано",             color: "#16a34a", bg: "#16a34a18" },
  closed:              { label: "Закрыта",                 color: "#6b7280", bg: "#6b728014" },
  service_center:      { label: "Сервис центр",             color: "#2563eb", bg: "#2563eb12" },
};

const CLOSE_REASON_META = {
  not_approved: { label: "Не согласовано",              color: "#dc2626", bg: "#dc262618" },
  written_off:  { label: "На списание / нет запчастей", color: "#78716c", bg: "#78716c18" },
};

// Возвращает мета статуса с учётом причины закрытия (close_reason)
function getStatusMeta(ticket) {
  if (ticket.status === "closed" && ticket.close_reason && CLOSE_REASON_META[ticket.close_reason]) {
    return CLOSE_REASON_META[ticket.close_reason];
  }
  return STATUS_META[ticket.status] || STATUS_META.unassigned;
}

const H_COLORS = {
  created: "#9ca3af", assign: "#2563eb", "status-change": "#ef4444",
  comment: "#f59e0b", photo: "#7c3aed", parts: "#10b981",
  accepted: "#16a34a", reopen_new: "#0891b2", reopen_parts: "#0891b2",
  cash: "#16a34a", "superadmin-edit": "#9333ea",
};
const ACTION_COMMENT_TYPES = new Set(["comment", "close-comment", "service-comment"]);
function getActionComments(ticket) {
  return (ticket.history || []).filter(h => ACTION_COMMENT_TYPES.has(h.type));
}
function actionCommentMeta(type) {
  if (type === "service-comment") return { label: "🏢 Сервис центр", color: "#1d4ed8", bg: "#eff6ff", border: "#2563eb" };
  if (type === "close-comment") return { label: "✓ Закрытие", color: "#374151", bg: "#f3f4f6", border: "#6b7280" };
  return { label: "💬 Комментарий", color: "#92400e", bg: "#fffbeb", border: "#d97706" };
}

const ROLE_LABELS = {
  admin: "Администратор",
  mechanic: "Механик",
  parts: "Менеджер запчастей",
  acts: "Акты",
  payroll: "Зарплата",
};

// Превращает номера телефонов в тексте в кликабельные tel: ссылки
const PHONE_REGEX = /(\+7|8)[\s\-]?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}/g;
function linkifyPhones(text) {
  if (!text) return text;
  const parts = [];
  let lastIndex = 0, key = 0, match;
  PHONE_REGEX.lastIndex = 0;
  while ((match = PHONE_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const raw = match[0];
    const digits = raw.replace(/\D/g, "");
    const normalized = "7" + digits.slice(1);
    parts.push(
      <a key={`tel-${key++}`} href={`tel:+${normalized}`} className="phone-link"
        onClick={e => e.stopPropagation()}>{raw}</a>
    );
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" })
    + " " + d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

const daysAgo = (iso) => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (diff === 0) return "сегодня";
  if (diff === 1) return "1 день";
  if (diff < 5) return `${diff} дня`;
  return `${diff} дней`;
};

// ── CSS ───────────────────────────────────────────────────────────────────────
const css = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html, body { height: 100%; }
:root {
  --bg: #f5f5f7; --surface: #fff; --border: #e5e7eb; --border2: #d1d5db;
  --text: #111827; --muted: #6b7280; --muted2: #9ca3af;
  --red: #ef4444; --red-bg: #fef2f2;
  --blue: #2563eb; --blue-bg: #eff6ff;
  --radius: 10px;
}
[data-theme="dark"] {
  --bg: #0f1117; --surface: #1c1f26; --border: #2d3139; --border2: #3d4149;
  --text: #f0f2f5; --muted: #8b95a1; --muted2: #5c6370;
  --red: #f87171; --red-bg: #2d1a1a;
  --blue: #60a5fa; --blue-bg: #1a2535;
}
[data-theme="dark"] .search-input:focus { background: var(--surface); }
[data-theme="dark"] .form-input:focus, [data-theme="dark"] .form-select:focus, [data-theme="dark"] .form-textarea:focus { background: var(--surface); }
[data-theme="dark"] .overlay { background: rgba(0,0,0,0.65); }
[data-theme="dark"] .lightbox { background: rgba(0,0,0,0.92); }
[data-theme="dark"] .btn-outline { background: var(--surface); }
[data-theme="dark"] .desc-box { background: var(--bg); }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; background: var(--bg); color: var(--text); }
::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }
.app { min-height: 100%; display: flex; flex-direction: column; }
.filter-bar, .content { position: relative; z-index: 1; }
.war-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none; overflow: hidden; }
.war-bg .boom { position: absolute; width: 130px; height: 86px; border-radius: 50%; opacity: 0;
  background: radial-gradient(circle at 50% 62%, rgba(254,249,195,.95), rgba(249,115,22,.5) 34%, rgba(185,28,28,.18) 58%, transparent 72%);
  animation: napalm 9s ease-in-out infinite; }
.war-bg .b1 { left: 8%; top: 16%; }
.war-bg .b2 { left: 58%; top: 6%; animation-delay: 3s; }
.war-bg .b3 { left: 34%; top: 24%; width: 90px; animation-delay: 6s; }
@keyframes napalm { 0%,64% { transform: scale(.2); opacity: 0; } 74% { opacity: .5; transform: scale(1); } 100% { opacity: 0; transform: scale(1.8) translateY(-10px); } }
.war-bg .huey { position: absolute; top: 12%; left: -70px; width: 58px; height: 18px; animation: flyby 24s linear infinite; }
.war-bg .huey::before { content: ""; position: absolute; left: 10px; top: 7px; width: 34px; height: 8px; background: rgba(55,65,40,.4); border-radius: 7px 12px 3px 3px; }
.war-bg .huey::after { content: ""; position: absolute; left: 0; top: 3px; width: 58px; height: 2px; background: rgba(55,65,40,.35); animation: rotor .1s linear infinite; }
@keyframes flyby { to { transform: translateX(calc(100vw + 140px)); } }
@keyframes rotor { 50% { transform: scaleY(4); opacity: .3; } }
.walker { position: absolute; bottom: 16px; width: 22px; height: 36px; transform-origin: 50% 100%; }
.walker.walk .bob { display: block; height: 100%; position: relative; animation: bob .42s ease-in-out infinite; }
.walker .bob { display: block; height: 100%; position: relative; }
.walker .hat { position: absolute; top: 0; left: 2px; }
.walker.vc .hat { border-left: 9px solid transparent; border-right: 9px solid transparent; border-bottom: 7px solid rgba(68,46,18,.6); }
.walker.gi .hat { left: 4px; width: 14px; height: 7px; border-radius: 8px 8px 2px 2px; background: rgba(30,41,59,.5); }
.walker .body { position: absolute; top: 8px; left: 6px; width: 10px; height: 14px; border-radius: 3px; background: rgba(22,101,52,.55); }
.walker.gi .body { background: rgba(30,64,175,.42); }
.walker .gun { position: absolute; top: 15px; left: 13px; width: 13px; height: 2px; background: rgba(31,41,55,.4); }
.tracer { position: absolute; bottom: 30px; width: 9px; height: 3px; margin-left: -4px; border-radius: 2px; background: #fde68a; box-shadow: 0 0 6px #f59e0b; }
@keyframes bob { 50% { transform: translateY(-3px); } }
.treeline { position: absolute; left: 0; right: 0; bottom: 0; height: 42px;
  background:
    radial-gradient(circle at 7% 100%, rgba(21,128,61,.26) 0 16px, transparent 18px),
    radial-gradient(circle at 24% 100%, rgba(22,101,52,.2) 0 24px, transparent 26px),
    radial-gradient(circle at 46% 100%, rgba(21,128,61,.22) 0 14px, transparent 16px),
    radial-gradient(circle at 68% 100%, rgba(22,101,52,.18) 0 28px, transparent 30px),
    radial-gradient(circle at 88% 100%, rgba(21,128,61,.22) 0 18px, transparent 20px); }
[data-theme="dark"] .walker.vc .hat { border-bottom-color: rgba(253,224,71,.45); }
[data-theme="dark"] .walker .body { background: rgba(134,239,172,.4); }
[data-theme="dark"] .walker.gi .hat { background: rgba(226,232,240,.4); }
[data-theme="dark"] .walker.gi .body { background: rgba(147,197,253,.42); }
[data-theme="dark"] .war-bg .huey::before, [data-theme="dark"] .war-bg .huey::after { background: rgba(226,232,240,.35); }

/* login */
.login-screen { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: var(--bg); }
.login-box { background: var(--surface); border: 1.5px solid var(--border); border-radius: 14px; padding: 32px 28px; width: 100%; max-width: 360px; }
.login-logo { font-size: 20px; font-weight: 700; letter-spacing: -0.5px; margin-bottom: 4px; }
.login-logo .dot { color: var(--red); }
.login-sub { font-size: 13px; color: var(--muted); margin-bottom: 24px; }
.login-error { background: var(--red-bg); color: var(--red); font-size: 13px; padding: 8px 12px; border-radius: 7px; margin-bottom: 14px; }

/* topbar */
.app-chrome { position: sticky; top: 0; z-index: 100; background: var(--surface); flex-shrink: 0; }
.topbar { background: var(--surface); border-bottom: 1px solid var(--border); min-height: 50px; padding: 8px 12px; display: flex; align-items: center; gap: 8px; flex-wrap: nowrap; overflow: hidden; }
.topbar-logo { font-size: 14px; font-weight: 700; letter-spacing: -0.3px; white-space: nowrap; flex-shrink: 0; background: none; border: none; font-family: inherit; color: inherit; padding: 0; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.topbar-logo:hover { opacity: 0.82; }
.topbar-logo:disabled { cursor: wait; opacity: 1; }
.topbar-logo .dot { color: var(--red); display: inline-block; }
.topbar-logo.bang .dot { animation: bang-dot 0.7s ease-out; }
.app.bang { animation: bang-shake 0.75s ease-out; }
.app.bang::after { content: ""; position: fixed; inset: 0; background: #fff; opacity: 0; pointer-events: none; z-index: 9999; animation: bang-flash 0.32s ease-out; }
.bang-tag { font-size: 13px; font-weight: 800; letter-spacing: 0.1px; color: #fff; background: #dc2626; padding: 6px 11px; border-radius: 7px; animation: bang-tag 1.15s ease-out forwards; position: fixed; top: 52px; left: 12px; z-index: 10000; white-space: normal; max-width: min(360px, calc(100vw - 24px)); box-shadow: 0 6px 18px rgba(0,0,0,0.28); line-height: 1.25; }
@keyframes bang-shake {
  0% { transform: translate(0,0) rotate(0); }
  10% { transform: translate(-9px, 3px) rotate(-0.8deg); }
  20% { transform: translate(8px, -4px) rotate(0.7deg); }
  30% { transform: translate(-6px, 2px) rotate(-0.5deg); }
  42% { transform: translate(5px, 3px) rotate(0.4deg); }
  55% { transform: translate(-3px, -1px); }
  70% { transform: translate(2px, 1px); }
  100% { transform: none; }
}
@keyframes bang-dot {
  0% { transform: scale(1); }
  18% { transform: scale(2.6); color: #fbbf24; text-shadow: 0 0 14px #f59e0b, 0 0 4px #fff; }
  40% { transform: scale(0.7); }
  62% { transform: scale(1.9); color: #ef4444; text-shadow: 0 0 16px #ef4444; }
  100% { transform: scale(1); text-shadow: none; }
}
@keyframes bang-flash { 0% { opacity: 0.5; } 100% { opacity: 0; } }
@keyframes bang-tag {
  0% { opacity: 0; transform: scale(0.7) translateY(-3px); }
  18% { opacity: 1; transform: scale(1.08) translateY(0); }
  75% { opacity: 1; }
  100% { opacity: 0.85; }
}
.topbar-role { font-size: 10px; font-weight: 600; color: var(--blue); background: var(--blue-bg); padding: 3px 7px; border-radius: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px; }
.topbar-right { margin-left: auto; flex-shrink: 0; }
.topbar-switch { font-size: 12px; color: var(--muted); background: none; border: none; cursor: pointer; padding: 6px 8px; border-radius: 6px; transition: all 0.15s; font-family: inherit; white-space: nowrap; }
.topbar-switch:hover { background: var(--bg); color: var(--text); }

/* nav tabs */
.nav-tabs { background: var(--surface); border-bottom: 1px solid var(--border); display: flex; overflow-x: auto; padding: 0 12px; -webkit-overflow-scrolling: touch; flex-shrink: 0; }
.nav-tabs::-webkit-scrollbar { height: 0; }
.nav-tab { flex-shrink: 0; padding: 11px 13px; font-size: 13px; font-weight: 500; color: var(--muted); border: none; background: none; cursor: pointer; border-bottom: 2px solid transparent; transition: all 0.15s; display: flex; align-items: center; gap: 5px; font-family: inherit; white-space: nowrap; }
.nav-tab:hover { color: var(--text); }
.nav-tab.active { color: var(--blue); border-bottom-color: var(--blue); }
.nav-count { font-size: 10px; font-weight: 700; padding: 1px 5px; border-radius: 9px; min-width: 17px; text-align: center; }
.nav-count.red { background: var(--red); color: #fff; }
.nav-count.blue { background: var(--blue); color: #fff; }
.nav-count.muted { background: var(--border2); color: var(--muted); }

/* filter bar */
.filter-bar { background: var(--surface); border-bottom: 1px solid var(--border); padding: 10px 16px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.search-wrap { position: relative; flex: 1; min-width: 160px; }
.search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--muted2); font-size: 13px; pointer-events: none; }
.search-input { width: 100%; background: var(--bg); border: 1.5px solid var(--border); border-radius: 8px; padding: 7px 10px 7px 30px; font-size: 13px; font-family: inherit; outline: none; transition: border 0.15s; color: var(--text); }
.search-input:focus { border-color: var(--blue); background: #fff; }
.search-input::placeholder { color: var(--muted2); }
.search-clear { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--muted2); cursor: pointer; font-size: 14px; line-height: 1; padding: 2px; }
.filter-select { background: var(--bg); border: 1.5px solid var(--border); border-radius: 8px; padding: 7px 10px; font-size: 12px; font-family: inherit; color: var(--text); outline: none; cursor: pointer; transition: border 0.15s; flex-shrink: 0; }
.filter-select:focus { border-color: var(--blue); }
.filter-results { font-size: 12px; color: var(--muted); white-space: nowrap; }

/* content */
.content { flex: 1; padding: 14px 16px; max-width: 720px; margin: 0 auto; width: 100%; }
.page-hdr { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 8px; }
.page-hdr-title { font-size: 16px; font-weight: 700; }
.page-hdr-actions { display: flex; flex-direction: column; align-items: stretch; gap: 8px; width: 100%; max-width: 220px; }
.page-hdr-actions .btn { width: 100%; white-space: nowrap; }
@media (min-width: 560px) { .page-hdr-actions { flex-direction: row; align-items: center; width: auto; max-width: none; } .page-hdr-actions .btn { width: auto; } }
.page-hdr-actions.admin-hdr { flex-direction: row; align-items: center; width: auto; max-width: none; }
.page-hdr-actions.admin-hdr .btn { width: auto; }
.hdr-more { position: relative; display: flex; align-items: center; }
.hdr-more-toggle { position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none; }
.hdr-more-label { cursor: pointer; }
.hdr-more-backdrop { display: none; }
.hdr-more-toggle:checked ~ .hdr-more-backdrop {
  display: block; position: fixed; inset: 0; z-index: 39;
}
.hdr-more-menu { display: none; }
.hdr-more-toggle:checked ~ .hdr-more-menu {
  display: flex; flex-direction: column; gap: 6px;
  position: absolute; right: 0; top: calc(100% + 6px); z-index: 40;
  background: var(--surface); border: 1.5px solid var(--border); border-radius: 10px;
  padding: 6px; min-width: 220px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
}
.hdr-more-menu .btn { width: 100%; }

/* buttons */
.btn { display: inline-flex; align-items: center; gap: 5px; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; font-family: inherit; white-space: nowrap; }
.btn-primary { background: var(--blue); color: #fff; }
.btn-primary:hover { background: #1d4ed8; }
.btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-outline { background: var(--surface); color: var(--text); border: 1.5px solid var(--border2); }
.btn-outline:hover { border-color: var(--blue); color: var(--blue); }
.btn-red { background: var(--red); color: #fff; }
.btn-red:hover { background: #dc2626; }
.btn-ghost { background: none; color: var(--muted); border: 1.5px solid var(--border); }
.btn-ghost:hover { color: var(--text); border-color: var(--border2); }
.btn-green { background: #10b981; color: #fff; }
.btn-green:hover { background: #059669; }
.btn-sm { padding: 5px 10px; font-size: 12px; }

/* empty / loading */
.empty { text-align: center; padding: 56px 20px; color: var(--muted); }
.empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.3; }
.empty-text { font-size: 13px; }

/* ticket list */
.ticket-list { display: flex; flex-direction: column; gap: 7px; }
.ticket-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 12px 14px; cursor: pointer; transition: all 0.15s; }
.ticket-card:hover { border-color: var(--blue); box-shadow: 0 2px 10px rgba(37,99,235,0.07); }
.ticket-card.transferred { border-left: 3px solid #f59e0b; }
.ticket-card.urgent { border-left: 4px solid var(--red); background: var(--red-bg); }
.ticket-card.urgent:hover { border-color: var(--red); box-shadow: 0 2px 10px rgba(239,68,68,0.15); }
.urgent-toggle { border: none; background: transparent; cursor: pointer; font-size: 15px; line-height: 1;
  padding: 2px 4px; border-radius: 6px; opacity: 0.3; filter: grayscale(1); transition: all 0.15s; }
.urgent-toggle:hover { opacity: 0.7; }
.urgent-toggle.active { opacity: 1; filter: none; }
.urgent-switch { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border-radius: 10px;
  border: 1.5px solid var(--border); background: var(--surface); color: var(--muted); font-size: 14px;
  font-weight: 600; cursor: pointer; transition: all 0.15s; width: 100%; }
.urgent-switch-icon { font-size: 16px; filter: grayscale(1); opacity: 0.5; transition: all 0.15s; }
.urgent-switch.active { border-color: var(--red); background: var(--red-bg); color: var(--red); }
.urgent-switch.active .urgent-switch-icon { filter: none; opacity: 1; }
.cash-switch { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border-radius: 10px;
  border: 1.5px solid var(--border); background: var(--surface); color: var(--muted); font-size: 14px;
  font-weight: 600; cursor: pointer; transition: all 0.15s; width: 100%; }
.cash-switch-icon { font-size: 16px; filter: grayscale(1); opacity: 0.5; transition: all 0.15s; }
.cash-switch.active { border-color: #16a34a; background: #f0fdf4; color: #16a34a; }
.cash-switch.active .cash-switch-icon { filter: none; opacity: 1; }
[data-theme="dark"] .cash-switch.active { background: rgba(22,163,74,0.12); }
.badge-accepted { font-size: 13px; line-height: 1; }
.accept-btn { border: 1.5px solid #16a34a; background: #f0fdf4; color: #16a34a; font-size: 11px; font-weight: 700;
  padding: 3px 8px; border-radius: 999px; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.accept-btn:hover { background: #16a34a; color: #fff; }
[data-theme="dark"] .accept-btn { background: rgba(22,163,74,0.12); }
[data-theme="dark"] .accept-btn:hover { background: #16a34a; color: #fff; }
.unassign-btn { border: 1.5px solid var(--border); background: var(--surface); color: var(--muted); font-size: 13px;
  line-height: 1; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s; padding: 0; }
.unassign-btn:hover { border-color: var(--blue); color: var(--blue); background: var(--blue-bg); }
.tc-top { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 7px; flex-wrap: wrap; }
.tc-id { font-size: 11px; color: var(--muted2); font-weight: 500; flex-shrink: 0; margin-top: 2px; }
.tc-text { font-size: 13px; font-weight: 500; flex: 1; min-width: 120px; line-height: 1.4; color: var(--text); word-break: break-word; }
.tc-badges { display: flex; gap: 5px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; max-width: 100%; }
.badge { font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 5px; white-space: nowrap; }
.badge-transfer { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
.tc-meta { display: flex; flex-wrap: wrap; gap: 10px; font-size: 11px; color: var(--muted); }
.tc-meta-item { display: flex; align-items: center; gap: 3px; }

/* modal */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 200; display: flex; align-items: flex-end; justify-content: center; padding: max(12px, env(safe-area-inset-top, 0px)) 0 env(safe-area-inset-bottom, 0px); }
@media (min-width: 600px) { .overlay { align-items: center; padding: 20px; } }
.modal { background: var(--surface); border-radius: 16px 16px 0 0; width: 100%; max-width: 580px; max-height: 100%; overflow-y: auto; padding: 18px 18px 28px; position: relative; }
@media (min-width: 600px) { .modal { border-radius: 14px; max-height: 88vh; } }
.modal-handle { width: 34px; height: 4px; background: var(--border2); border-radius: 2px; margin: 0 auto 14px; }
@media (min-width: 600px) { .modal-handle { display: none; } }
.modal-close { position: absolute; top: 14px; right: 14px; background: var(--bg); border: none; color: var(--muted); width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 13px; transition: all 0.15s; }
.modal-close:hover { background: var(--border); color: var(--text); }
.modal-title { font-size: 16px; font-weight: 700; margin-bottom: 14px; }

/* form */
.form-group { margin-bottom: 12px; }
.form-label { display: block; font-size: 11px; font-weight: 600; color: var(--muted); margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.4px; }
.form-input, .form-select, .form-textarea { width: 100%; background: var(--bg); border: 1.5px solid var(--border); color: var(--text); border-radius: 8px; padding: 9px 12px; font-size: 13px; font-family: inherit; outline: none; transition: border 0.15s; }
.form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--blue); background: #fff; }
.form-textarea { resize: vertical; min-height: 100px; line-height: 1.5; }
.form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }

/* divider */
.divider { border: none; border-top: 1px solid var(--border); margin: 14px 0; }

/* detail */
.kv-row { display: flex; gap: 6px; margin-bottom: 6px; font-size: 12px; }
.kv-row label { color: var(--muted); flex-shrink: 0; min-width: 70px; }
.kv-row value { color: var(--text); font-weight: 500; }
.desc-box { background: var(--bg); border-radius: 8px; padding: 12px; font-size: 13px; line-height: 1.55; margin-bottom: 12px; white-space: pre-wrap; word-break: break-word; }
.phone-link { color: var(--blue); text-decoration: underline; text-decoration-style: dotted; font-weight: 600; }
.phone-link:hover { text-decoration-style: solid; }

/* history */
.sec-label { font-size: 10px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.7px; margin-bottom: 8px; }
.history { display: flex; flex-direction: column; gap: 5px; }
.h-item { display: flex; gap: 8px; align-items: flex-start; font-size: 12px; padding: 7px 10px; border-radius: 7px; background: var(--bg); }
.h-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; margin-top: 3px; }
.h-text { flex: 1; color: var(--text); line-height: 1.4; }
.h-time { font-size: 11px; color: var(--muted2); white-space: nowrap; }

/* photos */
.photo-grid { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 7px; }
.photo-thumb { width: 72px; height: 72px; object-fit: cover; border-radius: 7px; cursor: zoom-in; border: 1.5px solid var(--border); transition: border-color 0.15s; }
.photo-thumb:hover { border-color: var(--blue); }
.photo-thumb-wrap { position: relative; width: 72px; height: 72px; }
.photo-thumb-wrap .photo-thumb { width: 100%; height: 100%; }
.photo-remove-btn { position: absolute; top: -6px; right: -6px; width: 20px; height: 20px; border-radius: 50%;
  background: var(--red); color: #fff; border: 2px solid var(--card-bg, #fff); font-size: 12px; line-height: 1;
  display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
.photo-remove-btn:hover { opacity: 0.85; }
.upload-status-badge { position: absolute; bottom: -6px; right: -6px; min-width: 20px; height: 20px; border-radius: 10px;
  background: var(--card-bg, #fff); border: 1.5px solid var(--border); font-size: 10.5px; font-weight: 700; line-height: 1;
  display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0,0,0,0.15); padding: 0 3px; color: var(--blue); }
.pdf-chip { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  font-size: 18px; background: var(--bg-soft, #f4f4f4); text-align: center; padding: 4px; cursor: default; }
.pdf-chip span { font-size: 8px; line-height: 1.1; color: var(--muted); word-break: break-all;
  max-width: 64px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

/* upload */
.upload-zone { border: 1.5px dashed var(--border2); border-radius: 8px; padding: 14px; text-align: center; cursor: pointer; transition: all 0.15s; color: var(--muted); font-size: 13px; }
.upload-zone:hover { border-color: var(--blue); color: var(--blue); background: var(--blue-bg); }
.upload-zone input { display: none; }

/* action panel */
.action-panel { background: var(--bg); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 12px; margin-top: 12px; }
.action-panel-label { font-size: 10px; font-weight: 700; color: var(--blue); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
.action-btns { display: flex; flex-wrap: wrap; gap: 7px; }
.assign-row { display: flex; gap: 7px; align-items: center; }
.assign-row .form-select { flex: 1; }

/* lightbox */
.lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.9); z-index: 400; display: flex; align-items: center; justify-content: center; cursor: zoom-out; padding: 20px; overflow: hidden; }
.lightbox img { max-width: 100%; max-height: 100%; border-radius: 8px; object-fit: contain; touch-action: none; user-select: none; }
.lightbox-close { position: fixed; top: 16px; right: 16px; width: 38px; height: 38px; border-radius: 50%;
  background: rgba(255,255,255,0.15); color: #fff; border: none; font-size: 18px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; z-index: 401; backdrop-filter: blur(4px); }
.lightbox-close:hover { background: rgba(255,255,255,0.28); }

/* users page */
.user-list { display: flex; flex-direction: column; gap: 7px; }
.user-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 12px 14px; display: flex; align-items: center; gap: 10px; }
.user-card.archived { opacity: 0.55; background: var(--bg); }
.user-card.archived .user-name { color: var(--muted); }
.user-avatar { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
.user-info { flex: 1; min-width: 0; }
.user-name { font-size: 13px; font-weight: 600; color: var(--text); }
.user-login { font-size: 11px; color: var(--muted); margin-top: 1px; }
.user-role-badge { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 5px; white-space: nowrap; }
.user-actions { display: flex; gap: 6px; flex-shrink: 0; }
.btn-icon { background: none; border: 1.5px solid var(--border); border-radius: 7px; padding: 5px 8px; cursor: pointer; font-size: 13px; transition: all 0.15s; color: var(--muted); }
.btn-icon:hover { border-color: var(--blue); color: var(--blue); }
.btn-icon.danger:hover { border-color: var(--red); color: var(--red); }
.btn-icon:disabled { opacity: 0.3; cursor: not-allowed; }
.form-error { background: var(--red-bg); color: var(--red); font-size: 12px; padding: 7px 10px; border-radius: 7px; margin-bottom: 12px; }
.form-success { background: #f0fdf4; color: #15803d; font-size: 12px; padding: 7px 10px; border-radius: 7px; margin-bottom: 12px; }

.btn-icon { padding: 0 10px; font-size: 16px; background: var(--bg); border: 1.5px solid var(--border); border-radius: var(--radius); cursor: pointer; height: 38px; display: inline-flex; align-items: center; justify-content: center; transition: background 0.15s; }
.btn-icon:hover { background: var(--border); }
.btn-icon.copied { background: #dcfce7; border-color: #16a34a; }

.pdf-link { display: inline-flex; align-items: center; gap: 5px; background: #eff6ff; border: 1.5px solid #bfdbfe; border-radius: 7px; padding: 6px 10px; font-size: 12px; color: var(--blue); text-decoration: none; }
.pdf-link:hover { background: #dbeafe; }

/* parts comments on card */
.tc-parts-comments { margin-top: 7px; padding-top: 7px; border-top: 1px solid var(--border); }
.tc-parts-label { font-size: 11px; font-weight: 600; color: #7c3aed; display: block; margin-bottom: 4px; }
.tc-parts-comment { font-size: 12px; color: var(--text); line-height: 1.4; padding: 3px 0; border-bottom: 1px dashed var(--border); }
.tc-parts-comment:last-child { border-bottom: none; }

mark { background: #fef08a; color: inherit; border-radius: 2px; padding: 0 1px; }

.acts-entry { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 14px; margin-bottom: 14px; }
.acts-mech { max-width: 280px; margin: 0 0 10px; }
.acts-line {
  display: grid;
  grid-template-columns: 148px 138px minmax(180px, 2.6fr) 100px 100px 118px;
  gap: 8px;
  align-items: start;
}
.acts-line.cash { grid-template-columns: 148px 138px minmax(200px, 2.8fr) 100px 100px; }
.acts-ticket-row { display: flex; gap: 6px; align-items: center; }
.acts-ticket-row .form-input { flex: 1; min-width: 0; }
.acts-nal {
  display: inline-flex; align-items: center; gap: 5px;
  height: 38px; padding: 0 8px; border: 1.5px solid var(--border); border-radius: 8px;
  background: var(--bg); color: var(--muted); font-size: 11px; font-weight: 700;
  cursor: pointer; font-family: inherit; flex-shrink: 0; white-space: nowrap;
}
.acts-nal.on { background: #f0fdf4; border-color: #86efac; color: #15803d; }
[data-theme="dark"] .acts-nal.on { background: rgba(22,163,74,0.12); }
.acts-nal-track { width: 22px; height: 13px; border-radius: 99px; background: #d1d5db; position: relative; display: inline-block; flex-shrink: 0; }
.acts-nal.on .acts-nal-track { background: #16a34a; }
.acts-nal-knob { position: absolute; top: 1.5px; left: 1.5px; width: 10px; height: 10px; border-radius: 50%; background: #fff; transition: left .15s; }
.acts-nal.on .acts-nal-knob { left: 10.5px; }
.acts-nal-tag { display: inline-block; font-size: 10px; font-weight: 700; color: #15803d; background: #dcfce7; border-radius: 4px; padding: 1px 6px; }
@media (max-width: 860px) {
  .acts-line, .acts-line.cash { grid-template-columns: 1fr 1fr; }
  .acts-obj { grid-column: 1 / -1; }
}
.acts-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.acts-table th { text-align: left; font-size: 11px; color: var(--muted); padding: 6px 8px; border-bottom: 1.5px solid var(--border); white-space: nowrap; }
.acts-table td { padding: 7px 8px; border-bottom: 1px solid var(--border); vertical-align: top; }
.acts-sum { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 14px; margin-bottom: 24px; }
.pay-wrap { overflow-x: auto; border: 1.5px solid var(--border); border-radius: var(--radius); background: var(--surface); }
.pay-table { width: 100%; border-collapse: collapse; font-size: 11.5px; min-width: 1080px; }
.pay-table th { text-align: left; font-size: 10px; color: var(--muted); padding: 7px 5px; border-bottom: 1.5px solid var(--border); white-space: nowrap; background: var(--bg); }
.pay-table td { padding: 5px 4px; border-bottom: 1px solid var(--border); vertical-align: top; }
.pay-sticky { position: sticky; left: 0; background: var(--surface); z-index: 1; min-width: 168px; box-shadow: 4px 0 8px rgba(0,0,0,0.04); }
.pay-table th.pay-sticky { z-index: 2; background: var(--bg); }
.pay-right { position: sticky; right: 0; background: var(--surface); z-index: 1; box-shadow: -4px 0 8px rgba(0,0,0,0.04); white-space: nowrap; }
.pay-table th.pay-right { z-index: 2; background: var(--bg); }
.pay-name { font-weight: 600; font-size: 12.5px; }
.pay-sub { font-size: 10px; color: var(--muted); margin-top: 1px; }
.pay-input { width: 56px; background: var(--bg); border: 1px solid var(--border); color: var(--text); border-radius: 6px; padding: 5px 4px; font-size: 12px; font-family: inherit; }
.pay-input:focus { border-color: var(--blue); outline: none; background: #fff; }
.pay-ro { font-weight: 600; white-space: nowrap; padding-top: 10px !important; }
.pay-pink { background: #fdf2f8; }
.pay-green { background: #ecfdf5; color: #047857; font-weight: 700; }
.pay-total td { font-weight: 700; background: var(--bg); }
.pay-auto { display: block; margin-top: 2px; font-size: 9px; font-weight: 700; border: none; background: none; color: var(--muted2); cursor: pointer; padding: 0; font-family: inherit; }
.pay-auto.on { color: var(--blue); }
.pay-hide { display: inline-block; margin-top: 4px; font-size: 10px; font-weight: 600; border: none; background: none; color: var(--muted2); cursor: pointer; padding: 0; font-family: inherit; }
.pay-hide.warn { color: var(--red); }
.pay-who { display: block; font-size: 11px; line-height: 1; margin-top: 2px; }
.pay-issue {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 6px; padding: 3px 7px; border: 1.5px solid var(--border);
  border-radius: 8px; background: var(--bg); color: var(--muted);
  font-size: 10px; font-weight: 700; cursor: pointer; font-family: inherit;
}
.pay-issue.on { background: #f0fdf4; border-color: #86efac; color: #15803d; }
.pay-issue.warn { border-color: var(--red); color: var(--red); background: var(--red-bg); }
.pay-issued-tag { display: inline-block; font-size: 10px; font-weight: 700; color: #15803d; background: #dcfce7; border-radius: 4px; padding: 1px 6px; margin-top: 4px; }
.pay-drift {
  display: block; margin-top: 6px; font-size: 10px; font-weight: 700;
  color: #b45309; background: #fffbeb; border: 1px solid #fcd34d;
  border-radius: 6px; padding: 3px 7px; cursor: pointer; font-family: inherit; text-align: left;
}
.pay-drift-box {
  margin-top: 8px; font-size: 11px; line-height: 1.45; color: #92400e;
  background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 8px 10px;
  min-width: 220px; max-width: 280px; font-weight: 500;
}
.pay-drift-box b { display: block; margin-bottom: 4px; }
.acts-issued-warn {
  background: #fffbeb; border: 1.5px solid #fcd34d; border-radius: 10px;
  padding: 10px 12px; margin: 10px 0 0; font-size: 13px; color: #92400e; line-height: 1.4;
}
.acts-svc { margin-top: 10px; font-size: 13px; font-weight: 700; color: #15803d; }
.acts-svc.bad { color: var(--red); }
.acts-hint { font-size: 11px; margin-top: 3px; color: var(--muted); line-height: 1.35; }
.acts-hint.dup { color: #b45309; font-weight: 600; }
.acts-hint.bad { color: var(--red); }
.pay-subnav { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; padding: 12px 16px 0; width: 100%; }
.pay-input:disabled { opacity: 0.6; cursor: not-allowed; }
tr.pay-held td { background: #f8fafc; }
tr.pay-held .pay-sticky { box-shadow: inset 3px 0 0 #16a34a, 4px 0 8px rgba(0,0,0,0.04); }
[data-theme="dark"] tr.pay-held td { background: rgba(22,163,74,0.06); }
.pay-legend { font-size: 11px; color: var(--muted); margin-top: 8px; line-height: 1.45; }
.pay-cards { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-bottom: 12px; }
.pay-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 12px 14px; }
.pay-card .k { font-size: 11px; color: var(--muted); margin-bottom: 4px; }
.pay-card .v { font-size: 20px; font-weight: 700; }
.pay-groups { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 8px; }
.pay-group { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 14px 16px; }
.pay-group-title { font-size: 12px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; color: var(--muted); margin-bottom: 4px; }
.pay-group .pay-cards { margin: 0; grid-template-columns: 1fr 1fr; }
.pay-hero { font-size: 32px; font-weight: 800; letter-spacing: -0.6px; color: #047857; line-height: 1.15; }
[data-theme="dark"] .pay-hero { color: #6ee7b7; }
.pay-hero-sub { font-size: 13px; color: var(--muted); margin-top: 6px; }
.pay-break { margin-bottom: 12px; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 0 14px; }
.pay-break summary { cursor: pointer; list-style: none; font-size: 13px; font-weight: 600; color: var(--muted); padding: 10px 0; user-select: none; display: flex; align-items: center; gap: 6px; }
.pay-break summary::-webkit-details-marker, .pay-break summary::marker { display: none; content: ""; }
.pay-break summary::before { content: "▸"; font-size: 11px; }
.pay-break[open] summary::before { content: "▾"; }
.pay-break-table { width: 100%; border-collapse: collapse; font-size: 13px; margin: 0 0 10px; }
.pay-break-table th { text-align: right; font-size: 11px; color: var(--muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; padding: 0 8px 8px; }
.pay-break-table th:first-child, .pay-break-table td:first-child { text-align: left; padding-left: 0; font-weight: 500; }
.pay-break-table td { padding: 7px 8px; border-top: 1px solid var(--border); }
.pay-break-table td:not(:first-child) { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
.pay-break-table tr.pay-break-main td:not(:first-child) { color: #047857; }
[data-theme="dark"] .pay-break-table tr.pay-break-main td:not(:first-child) { color: #6ee7b7; }
.pay-sec td { background: var(--bg) !important; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted); padding-top: 10px !important; }
@media (max-width: 860px) { .pay-cards { grid-template-columns: 1fr 1fr; } .pay-groups { grid-template-columns: 1fr; } .pay-hero { font-size: 26px; } }
[data-theme="dark"] .pay-input:focus { background: var(--surface); }
[data-theme="dark"] .pay-pink { background: rgba(236, 72, 153, 0.16); }
[data-theme="dark"] .pay-green { background: rgba(16, 185, 129, 0.16); color: #6ee7b7; }
[data-theme="dark"] .pay-sticky, [data-theme="dark"] .pay-right { background: var(--surface); }
[data-theme="dark"] .pay-issue.on { background: rgba(22,163,74,0.12); }
.tc-invoices { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.tc-invoices span { font-size: 11px; color: var(--muted); background: var(--bg); border: 1px solid var(--border); border-radius: 4px; padding: 1px 6px; }

`;

function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return <>{text.slice(0, idx)}<mark>{text.slice(idx, idx + query.length)}</mark>{text.slice(idx + query.length)}</>;
}

// ── App ───────────────────────────────────────────────────────────────────────
function WarBg() {
  const box = useRef(null);
  const actors = useRef(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    const width = () => box.current?.clientWidth || window.innerWidth;
    actors.current = ["vc", "gi", "vc", "gi", "vc"].map((kind, i) => ({
      id: i, kind, dir: kind === "vc" ? 1 : -1,
      x: 80 + i * 140, state: "walk", t: 0,
      speed: 26 + i * 6, cool: 0.4 + i * 0.35, bullet: null, lane: 8 + (i % 3) * 8,
    }));
    let last = performance.now();
    let acc = 0;
    let raf = 0;
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const w = width();
      const list = actors.current;
      for (const a of list) {
        if (a.state === "walk") {
          a.x += a.dir * a.speed * dt;
          if (a.dir > 0 && a.x > w + 24) a.x = -24;
          if (a.dir < 0 && a.x < -24) a.x = w + 24;
          a.cool -= dt;
          if (a.cool <= 0) {
            a.cool = 0.7 + Math.random() * 1.3;
            a.bullet = { x: a.x + a.dir * 18, dir: a.dir };
          }
          if (a.bullet) {
            a.bullet.x += a.bullet.dir * 260 * dt;
            for (const b of list) {
              if (b.state !== "walk" || b.kind === a.kind) continue;
              if (Math.abs(b.x - a.bullet.x) < 16) {
                b.state = "fall";
                b.t = 0;
                a.bullet = null;
                break;
              }
            }
            if (a.bullet && (a.bullet.x < -40 || a.bullet.x > w + 40)) a.bullet = null;
          }
        } else if (a.state === "fall") {
          a.t += dt;
          if (a.t > 0.75) { a.state = "gone"; a.t = 0; a.bullet = null; }
        } else {
          a.t += dt;
          if (a.t > 1.2) {
            a.state = "walk";
            a.t = 0;
            a.x = a.dir > 0 ? -24 : w + 24;
          }
        }
      }
      acc += dt;
      if (acc > 0.04) { acc = 0; setTick(n => n + 1); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  const list = actors.current || [];
  return (
    <div className="war-bg" ref={box} aria-hidden="true">
      <i className="boom b1" /><i className="boom b2" /><i className="boom b3" />
      <i className="huey" />
      {list.map(a => (
        <i key={a.id} className={"walker " + a.kind + " " + a.state} style={{
          left: a.x, bottom: a.lane,
          opacity: a.state === "gone" ? 0 : a.state === "fall" ? Math.max(0, 1 - a.t * 1.35) : 0.9,
          transform: `scaleX(${a.dir}) rotate(${a.state === "fall" ? Math.min(92, a.t * 140) : 0}deg)`,
        }}>
          <i className="bob"><i className="hat" /><i className="body" /><i className="gun" /></i>
        </i>
      ))}
      {list.filter(a => a.bullet && a.state === "walk").map(a => (
        <i key={"b" + a.id} className="tracer" style={{ left: a.bullet.x }} />
      ))}
      <i className="treeline" />
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  });
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [tickets, setTickets] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [tab, setTab] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user"));
      if (!u) return "unassigned";
      if (u.role === "parts") return "parts";
      if (u.role === "acts") return "acts-current";
      if (u.role === "payroll") return "payroll";
      if (u.role === "admin" || u.role === "superadmin") return "unassigned";
      return "active";
    } catch { return "unassigned"; }
  });
  const [modal, setModal] = useState(null);
  const [banging, setBanging] = useState(false);
  const [bangLine, setBangLine] = useState("");
  const [sel, setSel] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [ticketsLoadedOnce, setTicketsLoadedOnce] = useState(false);
  const [ticketNotFoundMsg, setTicketNotFoundMsg] = useState(false);
  const [hdrNarrow, setHdrNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 600);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 599px)");
    const apply = () => setHdrNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "light" ? "dark" : "light");

  // Фильтры и сортировка — отдельные для каждой вкладки И пользователя
  // (общий ключ scTabFilters раньше протекал с админа на механика на одном ПК)
  const FILTER_STORAGE_KEY = `scTabFilters:${user?.id || "anon"}`;
  const loadTabFilters = (tabId) => {
    try { return (JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || "{}"))[tabId] || {}; }
    catch { return {}; }
  };
  const saveTabFilters = (tabId, patch) => {
    try {
      const all = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || "{}");
      all[tabId] = { ...(all[tabId] || {}), ...patch };
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(all));
    } catch {}
  };
  const [filterMechanic, setFilterMechanicRaw] = useState("all");
  const [filterStatus, setFilterStatusRaw] = useState("all");
  const [sortBy, setSortByRaw] = useState("status_changed");
  const [sortDir, setSortDirRaw] = useState("desc");
  const setFilterMechanic = (v) => { setFilterMechanicRaw(v); saveTabFilters(tab, { mechanic: v }); };
  const setFilterStatus = (v) => { setFilterStatusRaw(v); saveTabFilters(tab, { status: v }); };
  const setSortBy = (v) => { setSortByRaw(v); saveTabFilters(tab, { sortBy: v }); };
  const setSortDir = (v) => { setSortDirRaw(v); saveTabFilters(tab, { sortDir: v }); };
  // При смене вкладки или пользователя — подхватываем сохранённые фильтры
  useEffect(() => {
    if (user?.role === "mechanic") {
      setFilterMechanicRaw("all");
      setFilterStatusRaw("all");
      const saved = loadTabFilters(tab);
      setSortByRaw(saved.sortBy || "status_changed");
      setSortDirRaw(saved.sortDir || "desc");
      return;
    }
    const saved = loadTabFilters(tab);
    setFilterMechanicRaw(saved.mechanic || "all");
    setFilterStatusRaw(saved.status || "all");
    setSortByRaw(saved.sortBy || "status_changed");
    setSortDirRaw(saved.sortDir || "desc");
  }, [tab, user?.id, user?.role]);
  const loadingRef = useRef(false);

  // Открытие заявки по клику на push-уведомление (?ticket=123 в адресе)
  useEffect(() => {
    if (!ticketsLoadedOnce) return;
    const params = new URLSearchParams(window.location.search);
    const ticketId = params.get("ticket");
    if (!ticketId) return;

    const found = tickets.find(t => String(t.id) === String(ticketId));
    if (found) {
      setSel(found);
      setModal("detail");
    } else {
      setTicketNotFoundMsg(true);
      setTimeout(() => setTicketNotFoundMsg(false), 4000);
    }
    // Убираем параметр из адреса, чтобы при обновлении страницы это не повторялось
    window.history.replaceState({}, "", window.location.pathname);
  }, [tickets, ticketsLoadedOnce]);

  const loadTickets = useCallback(async (silent = false) => {
    if (!user) return;
    if (loadingRef.current) return; // защита от накладывающихся запросов
    loadingRef.current = true;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/api/tickets");
      setTickets(data);
      setTicketsLoadedOnce(true);
      // Обновляем badge — не блокируем загрузку заявок
      (async () => {
        try {
          let badgeCount = 0;
          if (user.role === "mechanic") {
            badgeCount = data.filter(t => t.status === "in_work").length;
          } else if (["admin", "superadmin"].includes(user.role)) {
            badgeCount = data.filter(t => t.status === "unassigned").length;
          }
          if ("setAppBadge" in navigator) {
            if (badgeCount > 0) await navigator.setAppBadge(badgeCount);
            else await navigator.clearAppBadge();
          }
          if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: "set-badge", count: badgeCount });
          }
        } catch (e) { /* badge не критичен */ }
      })();
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      if (!silent) setLoading(false);
      loadingRef.current = false;
    }
  }, [user]);

  const loadMechanics = useCallback(async () => {
    if (user?.role === "admin" || user?.role === "superadmin" || user?.role === "acts" || user?.role === "payroll") {
      const r = await api.get("/api/mechanics");
      setMechanics(r.data);
    }
  }, [user]);

  // ── Push-уведомления ──────────────────────────────────────────────────────
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  };

  const setupPushSubscription = useCallback(async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (Notification.permission === "denied") return;

      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      if (permission !== "granted") return;

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data } = await api.get("/api/push/vapid-public-key");
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }

      await api.post("/api/push/subscribe", sub.toJSON());
    } catch (e) {
      console.warn("Push subscription failed:", e.message);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadMechanics();
    setupPushSubscription();
    if (user.role === "acts" || user.role === "payroll") return;
    loadTickets();
    const interval = setInterval(() => loadTickets(true), 30000);
    return () => clearInterval(interval);
  }, [user, loadTickets, loadMechanics, setupPushSubscription]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setTickets([]);
  };
  const bangApp = () => {
    if (banging) return;
    const lines = [
      "Не бей блять",
      "Руки прочь сука",
      "Эй полегче",
      "Щас сам тебе пропишу",
      "Братишка полегче",
      "Я щас Даню позову",
      "Я щас тебе заявки перемешаю козел",
    ];
    setBangLine(lines[Math.floor(Math.random() * lines.length)]);
    setBanging(true);
    setTimeout(() => window.location.reload(), 1300);
  };

  const onLogin = (u) => {
    setUser(u);
    setTab(u.role === "parts" ? "parts" : u.role === "acts" ? "acts-current" : u.role === "payroll" ? "payroll" : (u.role === "admin" || u.role === "superadmin") ? "unassigned" : "active");
  };

  const PARTS_STAGE = ["awaiting_parts", "parts_clarification", "parts_ordered"];
  const APPROVAL_STAGE = ["approval", "approval_sent", "approval_approved"];

  const tabsForRole = () => {
    if (!user) return [];
    if (user.role === "admin" || user.role === "superadmin") {
      const base = [
        { id: "unassigned", label: "Нераспределённые", accent: "red",
          count: tickets.filter(t => t.status === "unassigned").length },
        { id: "active",  label: "В работе",  accent: "blue",
          count: tickets.filter(t => t.status === "in_work").length },
        { id: "parts",   label: "Запчасти",  accent: "blue",
          count: tickets.filter(t => PARTS_STAGE.includes(t.status)).length },
        { id: "approval", label: "Согласование", accent: "red",
          count: tickets.filter(t => APPROVAL_STAGE.includes(t.status)).length },
        { id: "service_center", label: "🏢 Сервис центр", accent: "blue",
          count: tickets.filter(t => t.status === "service_center").length },
        { id: "closed",  label: "Закрытые заявки", accent: "muted",
          count: Math.min(tickets.filter(t => t.status === "closed").length, 300) },
        { id: "all",     label: "Все", accent: "muted", count: tickets.length },
        { id: "cash",    label: "💵 Наличка", accent: "muted", count: null },
        { id: "users",   label: "👥 Сотрудники", accent: "muted", count: null },
      ];
      if (user.role === "superadmin") {
        base.push({ id: "reports", label: "📊 Отчёты", accent: "muted", count: null });
        base.push({ id: "payroll", label: "💰 Зарплата", accent: "muted", count: null });
      }
      return base;
    }
    if (user.role === "mechanic") {
      const MECH_PARTS_TAB = ["awaiting_parts", "parts_ordered", ...APPROVAL_STAGE];
      return [
        { id: "active", label: "Мои заявки", accent: "blue",
          count: tickets.filter(t => t.status === "in_work" || t.status === "parts_clarification").length },
        { id: "parts",  label: "Ожидаем запчасти", accent: "blue",
          count: tickets.filter(t => MECH_PARTS_TAB.includes(t.status)).length },
        { id: "myclosed", label: "Мои закрытые заявки", accent: "muted",
          count: Math.min(tickets.filter(t => t.status === "closed").length, 50) },
        { id: "cash", label: "💵 Наличка", accent: "muted", count: null },
      ];
    }
    if (user.role === "acts") {
      return [
        { id: "acts-current", label: "Текущий период", accent: "blue", count: null },
        { id: "acts-archive", label: "Архив", accent: "muted", count: null },
      ];
    }
    if (user.role === "payroll") {
      return [
        { id: "payroll", label: "Зарплата", accent: "blue", count: null },
        { id: "payroll-members", label: "Участники", accent: "muted", count: null },
      ];
    }
    // parts role
    return [
      { id: "parts", label: "Запчасти", accent: "blue",
        count: tickets.filter(t => PARTS_STAGE.includes(t.status)).length },
      { id: "approval", label: "Согласование", accent: "red",
        count: tickets.filter(t => APPROVAL_STAGE.includes(t.status)).length },
      { id: "service_center", label: "🏢 Сервис центр", accent: "blue",
        count: tickets.filter(t => t.status === "service_center").length },
      { id: "all", label: "Все заявки", accent: "muted", count: tickets.length },
    ];
  };

  const baseList = () => {
    if (user?.role === "admin" || user?.role === "superadmin") {
      if (tab === "unassigned") return tickets.filter(t => t.status === "unassigned");
      if (tab === "active")     return tickets.filter(t => t.status === "in_work");
      if (tab === "parts")      return tickets.filter(t => PARTS_STAGE.includes(t.status));
      if (tab === "approval")   return tickets.filter(t => APPROVAL_STAGE.includes(t.status));
      if (tab === "service_center") return tickets.filter(t => t.status === "service_center");
      if (tab === "closed")     return tickets.filter(t => t.status === "closed").slice(0, 300);
      if (tab === "users")      return [];
      if (tab === "reports")    return [];
      if (tab === "cash")       return [];
      return tickets;
    }
    if (user?.role === "mechanic") {
      if (tab === "active") return tickets.filter(t => t.status === "in_work" || t.status === "parts_clarification");
      if (tab === "cash") return [];
      if (tab === "myclosed") {
        // Сортировка применяется ниже в filteredList(); здесь только фильтр и лимит
        return tickets.filter(t => t.status === "closed").slice(0, 50);
      }
      const MECH_PARTS_TAB = ["awaiting_parts", "parts_ordered", ...APPROVAL_STAGE];
      return tickets.filter(t => MECH_PARTS_TAB.includes(t.status));
    }
    // parts role
    if (tab === "approval") return tickets.filter(t => APPROVAL_STAGE.includes(t.status));
    if (tab === "service_center") return tickets.filter(t => t.status === "service_center");
    if (tab === "all") return tickets;
    return tickets.filter(t => PARTS_STAGE.includes(t.status));
  };


  const filteredList = () => {
    let list = baseList();
    const applyMechanicFilter = ((user?.role === "admin" || user?.role === "superadmin") && (tab === "active" || tab === "all" || tab === "closed"))
      || (user?.role === "parts" && tab === "all");
    const applyStatusFilter = ((tab === "parts" || tab === "approval") &&
      (user?.role === "parts" || user?.role === "admin" || user?.role === "superadmin"))
      || (user?.role === "parts" && tab === "all");
    if (applyMechanicFilter && filterMechanic !== "all") list = list.filter(t => String(t.assigned_to) === filterMechanic);
    if (applyStatusFilter && filterStatus !== "all") list = list.filter(t => t.status === filterStatus);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t =>
        `#${t.id}`.includes(q) ||
        (t.text || "").toLowerCase().includes(q) ||
        (t.assigned_name || "").toLowerCase().includes(q) ||
        (t.manager_comment || "").toLowerCase().includes(q) ||
        (t.approval_comment || "").toLowerCase().includes(q) ||
        (t.parts_comments || []).some(c => (c.text || "").toLowerCase().includes(q)) ||
        (t.mechanic_comments || []).some(c => (c.text || "").toLowerCase().includes(q)) ||
        getActionComments(t).some(c => (c.text || "").toLowerCase().includes(q))
      );
    }
    // Сортировка: по времени, когда заявка попала в текущий статус (по умолчанию),
    // либо по дате создания заявки — переключается в панели фильтров.
    // Если у заявок совпадает время (например, тестовые данные создавались почти
    // одновременно) — используем id как понятный tie-breaker, иначе направление
    // сортировки визуально ничего не меняло бы для таких заявок.
    const sortField = sortBy === "created" ? "created_at" : "status_changed_at";
    list = [...list].sort((a, b) => {
      const diff = new Date(b[sortField] || b.created_at) - new Date(a[sortField] || a.created_at);
      if (diff !== 0) return sortDir === "asc" ? -diff : diff;
      const idDiff = b.id - a.id;
      return sortDir === "asc" ? -idDiff : idDiff;
    });
    // Срочные заявки — наверх списка (только в "Нераспределённые" и "В работе"),
    // при этом внутри "срочно"/"не срочно" сохраняется сортировка по дате выше
    const urgentFirstTabs = (user?.role === "admin" || user?.role === "superadmin")
      ? ["unassigned", "active"]
      : user?.role === "mechanic" ? ["active"] : [];
    if (urgentFirstTabs.includes(tab)) {
      list = [...list].sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0));
    }
    if (user?.role === "mechanic" && tab === "active") {
      list = [...list].sort((a, b) =>
        (b.status === "parts_clarification" ? 1 : 0) - (a.status === "parts_clarification" ? 1 : 0));
    }
    return list;
  };

  const createTicket = async (text, files, urgent, onFileProgress) => {
    const result = await api.post("/api/tickets", { text, urgent: !!urgent });
    const ticketId = result.data.id;
    if (files?.length && ticketId) {
      try {
        await uploadPhotosSequential(ticketId, files, "ticket", onFileProgress);
      } catch (e) {
        alert(`Заявка #${ticketId} создана, но не все фото загрузились. Откройте заявку в списке и прикрепите оставшиеся фото вручную.`);
      }
    }
    setModal(null);
    loadTickets(true);
  };

  // Механик создаёт заявку прямо из своего кабинета — сразу в "Сервис центр"
  const createServiceCenterTicket = async (text, files, urgent, onFileProgress) => {
    const result = await api.post("/api/tickets/service-center", { text, urgent: !!urgent });
    const ticketId = result.data.id;
    if (files?.length && ticketId) {
      try {
        await uploadPhotosSequential(ticketId, files, "ticket", onFileProgress);
      } catch (e) {
        alert(`Заявка #${ticketId} создана, но не все фото загрузились. Откройте заявку в списке и прикрепите оставшиеся фото вручную.`);
      }
    }
    setModal(null);
    loadTickets(true);
  };

  // Механик создаёт заявку на запчасти прямо из своего кабинета — сразу в "Ожидаем запчасти"
  const createPartsRequestTicket = async (text, files, urgent, onFileProgress) => {
    const result = await api.post("/api/tickets/parts-request", { text, urgent: !!urgent });
    const ticketId = result.data.id;
    if (files?.length && ticketId) {
      try {
        await uploadPhotosSequential(ticketId, files, "parts", onFileProgress);
      } catch (e) {
        alert(`Заявка #${ticketId} создана, но не все фото загрузились. Откройте заявку в списке и прикрепите оставшиеся фото вручную.`);
      }
    }
    setModal(null);
    loadTickets(true);
  };

  const assignTicket = async (id, mechanic_id) => {
    await api.patch(`/api/tickets/${id}/assign`, { mechanic_id });
    loadTickets(true);
  };

  const unassignTicket = async (id) => {
    if (!window.confirm("Вернуть заявку в нераспределённые? Назначение механику будет снято.")) return;
    await api.patch(`/api/tickets/${id}/unassign`);
    loadTickets(true);
  };

  const sendToParts = async (id, files, onFileProgress) => {
    if (files?.length) {
      await uploadPhotosSequential(id, files, "parts", onFileProgress);
    }
    await api.patch(`/api/tickets/${id}/to-parts`);
    loadTickets(true);
  };

  const editTicket = async (id, text) => {
    await api.patch(`/api/tickets/${id}`, { text });
    loadTickets(true);
  };

  const deleteTicket = async (id) => {
    await api.delete(`/api/tickets/${id}`);
    loadTickets(true);
  };

  const toggleUrgent = async (id, urgent) => {
    // Оптимистичное обновление — сразу отражаем в интерфейсе, не дожидаясь ответа сервера
    setTickets(prev => prev.map(t => t.id === id ? { ...t, urgent } : t));
    try {
      await api.patch(`/api/tickets/${id}/urgent`, { urgent });
    } catch (e) {
      // Откат при ошибке
      setTickets(prev => prev.map(t => t.id === id ? { ...t, urgent: !urgent } : t));
    }
  };

  const acceptTicket = async (id) => {
    const prevVal = tickets.find(t => t.id === id)?.accepted_at;
    setTickets(prev => prev.map(t => t.id === id ? { ...t, accepted_at: new Date().toISOString() } : t));
    try {
      await api.patch(`/api/tickets/${id}/accept`);
    } catch (e) {
      setTickets(prev => prev.map(t => t.id === id ? { ...t, accepted_at: prevVal } : t));
      alert(e.response?.data?.error || "Не удалось принять заявку");
    }
  };

  const reopenTicket = async (id, mode, comment) => {
    await api.patch(`/api/tickets/${id}/reopen`, { mode, comment });
    loadTickets(true);
  };

  const softDeleteTicket = async (id, reason) => {
    await api.patch(`/api/tickets/${id}/soft-delete`, { reason });
    loadTickets(true);
  };

  const mechanicAction = async (id, action, comment, files, photoType, cashData, onFileProgress) => {
    // Сначала загружаем фото — статус меняем только после подтверждённой загрузки.
    // Это защищает от ситуации с плохим интернетом: если фото не загрузилось,
    // заявка НЕ должна улетать в "закрыта"/"ожидаем запчасти" без фото.
    if (files?.length) {
      await uploadPhotosSequential(id, files, photoType || "parts", onFileProgress);
    }
    await api.patch(`/api/tickets/${id}/action`, {
      action, comment,
      is_cash: cashData?.isCash || false,
      cash_amount: cashData?.amount || null,
    });
    loadTickets(true);
  };

  const partsReady = async (id) => {
    await api.patch(`/api/tickets/${id}/parts-ready`);
    loadTickets(true);
  };

  const partsAction = async (id, action) => {
    await api.patch(`/api/tickets/${id}/parts-action`, { action });
    loadTickets(true);
  };

  const returnFromClarification = async (id) => {
    await api.patch(`/api/tickets/${id}/return-from-clarification`);
    loadTickets(true);
  };

  const approvalAction = async (id, action) => {
    await api.patch(`/api/tickets/${id}/approval-action`, { action });
    loadTickets(true);
  };

  const saveManagerComment = async (id, comment) => {
    await api.patch(`/api/tickets/${id}/manager-comment`, { comment });
    loadTickets(true);
  };

  const saveApprovalComment = async (id, comment) => {
    await api.patch(`/api/tickets/${id}/approval-comment`, { comment });
    loadTickets(true);
  };

  if (!user) return (
    <>
      <style>{css}</style>
      <LoginScreen onLogin={onLogin} />
    </>
  );

  const tabs = tabsForRole();
  const rawList = baseList();
  const list = filteredList();
  const filtersOn = !!(search || filterMechanic !== "all" || filterStatus !== "all");
  const showMechanicFilter = ((user.role === "admin" || user.role === "superadmin") && (tab === "active" || tab === "all" || tab === "closed"))
    || (user.role === "parts" && tab === "all");
  const showStatusFilter = ((tab === "parts" || tab === "approval") &&
    (user.role === "parts" || user.role === "admin" || user.role === "superadmin"))
    || (user.role === "parts" && tab === "all");
  // Сортировка полезна везде, где показывается список заявок (не в "Сотрудники"/"Отчёты"/"Наличка")
  const showSortControl = !["users", "reports", "cash", "acts-current", "acts-archive", "payroll", "payroll-current", "payroll-archive", "payroll-members"].includes(tab);
  const STATUS_FILTER_OPTIONS = {
    parts: [
      ["awaiting_parts", "Ожидаем запчасти"],
      ["parts_clarification", "Уточнение запчастей"],
      ["parts_ordered", "Запчасти заказаны"],
    ],
    approval: [
      ["approval", "Согласование"],
      ["approval_sent", "Отправлено на согласование"],
      ["approval_approved", "Согласовано"],
    ],
    all: Object.entries(STATUS_META).map(([val, m]) => [val, m.label]),
  };
  const roleLabel = user.role === "superadmin" ? "Супер Админ"
    : user.role === "admin" ? "Администратор"
    : user.role === "parts" ? "Менеджер запчастей"
    : user.role === "acts" ? "Акты"
    : user.role === "payroll" ? "Зарплата"
    : `Механик · ${user.name?.split(" ")[1] || user.name}`;

  return (
    <>
      <style>{css}</style>
      <div className={"app" + (banging ? " bang" : "")}>
        {user.role === "parts" && <WarBg />}
        <div className="app-chrome">
        <div className="topbar">
          <button type="button" className={"topbar-logo" + (banging ? " bang" : "")} onClick={bangApp} disabled={banging} title="Если зависло — стукнуть">
            Сервис<span className="dot">.</span>Контроль <span style={{ fontSize: 9, color: "var(--muted2)", fontWeight: 500 }}>v06</span>
            {banging && <span className="bang-tag">{bangLine}</span>}
          </button>
          <div className="topbar-role">{roleLabel}</div>
          <div className="topbar-right">
            <button className="topbar-switch" onClick={toggleTheme} title="Сменить тему" style={{ marginRight: 4 }}>
              {theme === "light" ? "🌙" : "☀️"}
            </button>
            <button className="topbar-switch" onClick={logout}>Выйти ↗</button>
          </div>
        </div>

        <div className="nav-tabs">
          {tabs.map(t => (
            <button key={t.id} className={`nav-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => { setTab(t.id); setSearch(""); }}>
              {t.label}
              {t.count > 0 && <span className={`nav-count ${t.accent}`}>{t.count}</span>}
            </button>
          ))}
        </div>
        </div>

        {tab === "users" ? (
          <UsersPage currentUserId={user.id} onUsersChange={loadMechanics} />
        ) : tab === "reports" ? (
          <ReportsPage tickets={tickets} mechanics={mechanics} user={user} />
        ) : tab === "cash" ? (
          <CashPage mechanics={mechanics} user={user} />
        ) : tab === "acts-current" || tab === "acts-archive" ? (
          <ActsPage mechanics={mechanics} mode={tab === "acts-archive" ? "archive" : "current"} />
        ) : tab === "payroll" || tab === "payroll-current" || tab === "payroll-archive" || tab === "payroll-members" ? (
          <PayrollShell mechanics={mechanics} user={user} start={tab === "payroll-members" ? "members" : "sheet"} />
        ) : (
          <>
            <div className="filter-bar">
              <div className="search-wrap">
                <span className="search-icon">🔍</span>
                <input className="search-input" placeholder="Поиск по тексту, номеру, механику..."
                  value={search} onChange={e => setSearch(e.target.value)} />
                {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
              </div>
              {showMechanicFilter && (
                <select className="filter-select" value={filterMechanic} onChange={e => setFilterMechanic(e.target.value)}>
                  <option value="all">Все механики</option>
                  {mechanics.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                </select>
              )}
              {showStatusFilter && (
                <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="all">Все статусы</option>
                  {STATUS_FILTER_OPTIONS[tab].map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              )}
              {showSortControl && (
                <>
                  <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)} title="Сортировка">
                    <option value="status_changed">По времени в статусе</option>
                    <option value="created">По дате создания</option>
                  </select>
                  <button type="button" className="filter-select" style={{ cursor: "pointer" }}
                    onClick={() => setSortDir(sortDir === "desc" ? "asc" : "desc")}
                    title={sortDir === "desc" ? "Сначала новые" : "Сначала старые"}>
                    {sortDir === "desc" ? "↓ Сначала новые" : "↑ Сначала старые"}
                  </button>
                </>
              )}
              {(search || filterMechanic !== "all" || filterStatus !== "all") && (
                <span className="filter-results">{list.length} зап.</span>
              )}
            </div>

            <div className="content">
              <div className="page-hdr">
                <div className="page-hdr-title">{tabs.find(t => t.id === tab)?.label}
                  {user.role === "parts" && tab === "all" && (
                    <span style={{ fontWeight: 400, fontSize: 12, color: "var(--muted)", marginLeft: 8 }}>только просмотр</span>
                  )}
                </div>
                {(user.role === "admin" || user.role === "superadmin") && (
                  <div className="page-hdr-actions admin-hdr">
                    {hdrNarrow ? (
                      <div className="hdr-more">
                        <input type="checkbox" id="hdr-more-toggle" className="hdr-more-toggle" />
                        <label htmlFor="hdr-more-toggle" className="btn btn-outline btn-sm hdr-more-label">Ещё ▾</label>
                        <label htmlFor="hdr-more-toggle" className="hdr-more-backdrop" />
                        <div className="hdr-more-menu" onClick={() => { const el = document.getElementById("hdr-more-toggle"); if (el) el.checked = false; }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setModal("maintenance")}>🔧 +ТО</button>
                          <button className="btn btn-outline btn-sm" onClick={() => setModal("create-parts")}>📦 Заявка на запчасти</button>
                          <button className="btn btn-outline btn-sm" onClick={() => setModal("create-sc")}>🏢 Заявка в Сервис центр</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button className="btn btn-outline btn-sm" onClick={() => setModal("maintenance")}>🔧 +ТО</button>
                        <button className="btn btn-outline btn-sm" onClick={() => setModal("create-parts")}>📦 Заявка на запчасти</button>
                        <button className="btn btn-outline btn-sm" onClick={() => setModal("create-sc")}>🏢 Заявка в Сервис центр</button>
                      </>
                    )}
                    <button className="btn btn-primary btn-sm" onClick={() => setModal("create")}>+ Заявка</button>
                  </div>
                )}
                {user.role === "mechanic" && (
                  <div className="page-hdr-actions">
                    <button className="btn btn-outline btn-sm" onClick={() => setModal("create-parts")}>
                      📦 Заявка на запчасти
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => setModal("create-sc")}>
                      🏢 + Заявка в Сервис центр
                    </button>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="empty"><div className="empty-icon">⏳</div><div className="empty-text">Загрузка...</div></div>
              ) : list.length === 0 ? (
                <div className="empty">
                  <div className="empty-icon">{filtersOn ? "🔍" : "📭"}</div>
                  <div className="empty-text">
                    {filtersOn && rawList.length > 0
                      ? `По фильтру пусто, всего на вкладке ${rawList.length}`
                      : search ? "Ничего не найдено" : "Заявок нет"}
                  </div>
                  {filtersOn && rawList.length > 0 && (
                    <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 12 }}
                      onClick={() => { setSearch(""); setFilterMechanic("all"); setFilterStatus("all"); }}>
                      Сбросить фильтр
                    </button>
                  )}
                </div>
              ) : (
                <div className="ticket-list">
                  {list.map(t => (
                    <TicketCard key={t.id} ticket={t} search={search} userRole={user.role}
                      currentUserId={user.id}
                      onClick={() => { setSel(t); setModal("detail"); }}
                      onToggleUrgent={toggleUrgent}
                      onAccept={acceptTicket}
                      onUnassign={unassignTicket} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {modal === "create" && <CreateModal onClose={() => setModal(null)} onCreate={createTicket} />}
        {modal === "create-sc" && <CreateModal onClose={() => setModal(null)} onCreate={createServiceCenterTicket} />}
        {modal === "create-parts" && (
          <CreateModal
            onClose={() => setModal(null)}
            onCreate={createPartsRequestTicket}
            title="Заявка на запчасти"
            placeholder="Введите наименование запчастей, объект/адрес, количество и всё необходимое Никите для заказа"
            requirePhoto
            photoLabel="Фото запчастей"
          />
        )}
        {modal === "maintenance" && <MaintenanceModal onClose={() => setModal(null)} />}
        {modal === "detail" && sel && (
          <DetailModal
            ticket={tickets.find(t => t.id === sel.id) || sel}
            user={user}
            mechanics={mechanics}
            onClose={() => { setModal(null); setSel(null); }}
            onAssign={assignTicket}
            onUnassign={unassignTicket}
            onEdit={editTicket}
            onDelete={deleteTicket}
            onToggleUrgent={toggleUrgent}
            onAccept={acceptTicket}
            onReopen={reopenTicket}
            onSoftDelete={softDeleteTicket}
            onSendToParts={sendToParts}
            onMechanicAction={mechanicAction}
            onPartsReady={partsReady}
            onPartsAction={partsAction}
            onReturnFromClarification={returnFromClarification}
            onApprovalAction={approvalAction}
            onManagerComment={saveManagerComment}
            onApprovalComment={saveApprovalComment}
            onRefresh={() => loadTickets(true)}
            onLightbox={src => setLightbox(src)}
            readOnly={user.role === "parts" && tab === "all"}
          />
        )}
        {lightbox && <Lightbox key={lightbox} src={lightbox} onClose={() => setLightbox(null)} />}

        {ticketNotFoundMsg && (
          <div style={{
            position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 500,
            background: "var(--surface)", color: "var(--text)", border: "1.5px solid var(--red)",
            borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          }}>
            ⚠ Заявка недоступна
          </div>
        )}
      </div>
    </>
  );
}

// ── Users Page ────────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  admin:    { bg: "#eff6ff", color: "#2563eb" },
  mechanic: { bg: "#f0fdf4", color: "#15803d" },
  parts:    { bg: "#faf5ff", color: "#7c3aed" },
  acts:     { bg: "#fff7ed", color: "#c2410c" },
  payroll:  { bg: "#ecfdf5", color: "#047857" },
};
const ROLE_AVATARS = { admin: "👑", mechanic: "🔧", parts: "📦", acts: "🧾", payroll: "💰" };

function UsersPage({ currentUserId, onUsersChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "add" | { type: "password", user }
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/users");
      setUsers(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteUser = async (u) => {
    if (!window.confirm(`Убрать «${u.name}» из рабочих списков?\nЗаявки и история не изменятся.`)) return;
    try {
      await api.delete(`/api/users/${u.id}`);
      await load();
      onUsersChange();
    } catch(e) {
      setError(e.response?.data?.error || "Ошибка удаления");
    }
  };
  const restoreUser = async (u) => {
    if (!window.confirm(`Вернуть «${u.name}» в рабочие списки?`)) return;
    try {
      await api.post(`/api/users/${u.id}/restore`);
      await load();
      onUsersChange();
    } catch(e) {
      setError(e.response?.data?.error || "Ошибка");
    }
  };

  const sortedUsers = [...users].sort((a, b) => (a.active === false ? 1 : 0) - (b.active === false ? 1 : 0));

  return (
    <div className="content">
      <div className="page-hdr">
        <div className="page-hdr-title">Сотрудники</div>
        <button className="btn btn-primary btn-sm" onClick={() => setModal("add")}>+ Добавить</button>
      </div>
      {error && <div className="form-error">{error} <button style={{float:"right",background:"none",border:"none",cursor:"pointer",color:"inherit"}} onClick={() => setError("")}>✕</button></div>}
      {loading ? (
        <div className="empty"><div className="empty-icon">⏳</div><div className="empty-text">Загрузка...</div></div>
      ) : (
        <div className="user-list">
          {sortedUsers.map(u => {
            const rc = ROLE_COLORS[u.role] || ROLE_COLORS.mechanic;
            const isMe = u.id === currentUserId;
            const gone = u.active === false;
            return (
              <div key={u.id} className={"user-card" + (gone ? " archived" : "")}>
                <div className="user-avatar" style={{ background: rc.bg, color: rc.color }}>
                  {ROLE_AVATARS[u.role] || "👤"}
                </div>
                <div className="user-info">
                  <div className="user-name">{u.name} {isMe && <span style={{fontSize:10,color:"var(--muted)"}}>— это вы</span>}</div>
                  <div className="user-login">@{u.login}</div>
                </div>
                {gone
                  ? <span className="user-role-badge" style={{ background: "var(--bg)", color: "var(--muted)", border: "1px solid var(--border)" }}>скрыт</span>
                  : <span className="user-role-badge" style={{ background: rc.bg, color: rc.color }}>{ROLE_LABELS[u.role]}</span>}
                <div className="user-actions">
                  {!gone && (
                    <button className="btn-icon" title="Сменить пароль"
                      onClick={() => setModal({ type: "password", user: u })}>🔑</button>
                  )}
                  {gone ? (
                    <button className="btn-icon" title="Вернуть" onClick={() => restoreUser(u)}>↩</button>
                  ) : (
                    <button className="btn-icon danger" title="Убрать из списков" disabled={isMe}
                      onClick={() => deleteUser(u)}>🗑</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal === "add" && (
        <AddUserModal
          onClose={() => setModal(null)}
          onCreated={() => { load(); onUsersChange(); }}
        />
      )}
      {modal?.type === "password" && (
        <ChangePasswordModal
          user={modal.user}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ── Add User Modal ────────────────────────────────────────────────────────────
function AddUserModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("mechanic");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!name.trim() || !login.trim() || !password.trim()) {
      setError("Заполните все поля"); return;
    }
    setLoading(true); setError("");
    try {
      await api.post("/api/users", { name: name.trim(), login: login.trim(), password, role });
      onCreated();
      onClose();
    } catch(e) {
      setError(e.response?.data?.error || "Ошибка создания");
    } finally { setLoading(false); }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">Новый сотрудник</div>
        {error && <div className="form-error">{error}</div>}
        <div className="form-group">
          <label className="form-label">Полное имя</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="Иван Иванов" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Логин</label>
          <input className="form-input" value={login} onChange={e => setLogin(e.target.value)}
            placeholder="ivanov" />
        </div>
        <div className="form-group">
          <label className="form-label">Пароль</label>
          <input className="form-input" type="password" value={password}
            onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <div className="form-group">
          <label className="form-label">Роль</label>
          <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
            <option value="mechanic">Механик</option>
            <option value="admin">Администратор</option>
            <option value="parts">Менеджер запчастей</option>
            <option value="acts">Акты</option>
            <option value="payroll">Зарплата</option>
          </select>
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" disabled={loading} onClick={submit}>
            {loading ? "Создаём..." : "Создать"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Change Password Modal ─────────────────────────────────────────────────────
function ChangePasswordModal({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (password.length < 4) { setError("Минимум 4 символа"); return; }
    setLoading(true); setError("");
    try {
      await api.patch(`/api/users/${user.id}/password`, { password });
      setSuccess(true);
      setTimeout(onClose, 1200);
    } catch(e) {
      setError(e.response?.data?.error || "Ошибка");
    } finally { setLoading(false); }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">Смена пароля</div>
        <div style={{fontSize:13,color:"var(--muted)",marginBottom:14}}>Сотрудник: <b>{user.name}</b></div>
        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">✓ Пароль изменён</div>}
        <div className="form-group">
          <label className="form-label">Новый пароль</label>
          <input className="form-input" type="password" value={password} autoFocus
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submit()}
            placeholder="Минимум 4 символа" />
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" disabled={loading || success} onClick={submit}>
            {loading ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockUntil, setLockUntil] = useState(0);
  const [lockLogin, setLockLogin] = useState("");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!lockUntil) return;
    const t = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= lockUntil) {
        setLockUntil(0);
        setLockLogin("");
        setError("");
      }
    }, 1000);
    return () => clearInterval(t);
  }, [lockUntil]);

  const lockLeft = lockUntil && login.trim().toLowerCase() === lockLogin
    ? Math.max(0, Math.ceil((lockUntil - now) / 1000)) : 0;
  const locked = lockLeft > 0;
  const fmtLock = (sec) => {
    const m = Math.floor(sec / 60), s = sec % 60;
    return m > 0 ? `${m} мин. ${String(s).padStart(2, "0")} сек.` : `${s} сек.`;
  };

  const submit = async () => {
    if (!login || !password || locked) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/api/login", { login, password });
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      onLogin(data.user);
    } catch (e) {
      const data = e.response?.data || {};
      const retry = Number(data.retryAfter) || 0;
      if (e.response?.status === 429 || retry > 0) {
        setLockLogin(login.trim().toLowerCase());
        setLockUntil(Date.now() + retry * 1000);
        setError(data.error || "Слишком много попыток входа");
      } else {
        setError(data.error || "Ошибка входа");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="login-screen">
      <div className="login-box">
        <div className="login-logo">Сервис<span className="dot">.</span>Контроль</div>
        <div className="login-sub">Управление заявками на ремонт оборудования</div>
        {error && <div className="login-error">{locked ? `Слишком много попыток входа. Повторите через ${fmtLock(lockLeft)}` : error}</div>}
        <div className="form-group">
          <label className="form-label">Логин</label>
          <input className="form-input" value={login} onChange={e => setLogin(e.target.value)}
            placeholder="admin" onKeyDown={e => e.key === "Enter" && submit()} />
        </div>
        <div className="form-group">
          <label className="form-label">Пароль</label>
          <input className="form-input" type="password" value={password}
            onChange={e => setPassword(e.target.value)} placeholder="••••••••"
            onKeyDown={e => e.key === "Enter" && submit()} />
        </div>
        <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}
          disabled={loading || locked || !login || !password} onClick={submit}>
          {locked ? `Подождите ${fmtLock(lockLeft)}` : loading ? "Вход..." : "Войти"}
        </button>
      </div>
    </div>
  );
}

// ── Copy Button ───────────────────────────────────────────────────────────────
function CopyButton({ text, small }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { alert("Нет доступа к буферу обмена"); }
  };
  return (
    <button className={`btn-icon${copied ? " copied" : ""}`}
      style={{ height: small ? 24 : 30, fontSize: small ? 12 : 14, padding: "0 7px" }}
      title="Копировать" onClick={handleCopy}>
      {copied ? "✓" : "📋"}
    </button>
  );
}

// ── Ticket Card ───────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const lastDist = useRef(0);
  const dragging = useRef(false);

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      lastDist.current = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    } else if (pointers.current.size === 1 && scale > 1) {
      dragging.current = true;
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const prev = pointers.current.get(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (lastDist.current > 0) {
        const delta = dist / lastDist.current;
        setScale(s => clamp(s * delta, 1, 5));
      }
      lastDist.current = dist;
    } else if (pointers.current.size === 1 && dragging.current && scale > 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setPos(p => ({ x: p.x + dx, y: p.y + dy }));
    }
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) lastDist.current = 0;
    if (pointers.current.size === 0) dragging.current = false;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setScale(s => {
      const next = clamp(s + delta * s, 1, 5);
      if (next <= 1) setPos({ x: 0, y: 0 });
      return next;
    });
  };

  const toggleZoom = () => {
    if (scale > 1) { setScale(1); setPos({ x: 0, y: 0 }); }
    else { setScale(2.5); }
  };

  return (
    <div className="lightbox" onClick={e => { if (e.target === e.currentTarget && scale === 1) onClose(); }}>
      <button className="lightbox-close" onClick={onClose}>✕</button>
      <img
        src={src}
        alt=""
        draggable={false}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={onWheel}
        onDoubleClick={toggleZoom}
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transition: pointers.current.size ? "none" : "transform 0.15s ease-out",
          cursor: scale > 1 ? "grab" : "zoom-in",
          touchAction: "none",
        }}
      />
    </div>
  );
}

function TicketCard({ ticket, search, onClick, userRole, currentUserId, onToggleUrgent, onAccept, onUnassign }) {
  const sm = getStatusMeta(ticket);
  const isTransfer = ticket.transferred && ticket.status === "unassigned";
  const isUrgent = !!ticket.urgent;
  const isClarification = ticket.status === "parts_clarification";
  const preview = (ticket.text || "").slice(0, 120) + ((ticket.text || "").length > 120 ? "…" : "");
  const partsComments = ticket.parts_comments || [];
  const isMechanicView = userRole === "mechanic";
  const canToggleUrgent = userRole === "admin" || userRole === "superadmin";
  const canUnassign = (userRole === "admin" || userRole === "superadmin") &&
    (ticket.status === "in_work" || ticket.status === "service_center");
  const isOwnMechanic = isMechanicView && ticket.assigned_to === currentUserId;
  const showAccept = ticket.status === "in_work";
  const isAccepted = !!ticket.accepted_at;

  const APPROVAL_BORDER = {
    approval: "#dc2626", approval_sent: "#ca8a04", approval_approved: "#16a34a",
  };
  const approvalBorder = APPROVAL_BORDER[ticket.status];

  return (
    <div className={`ticket-card ${isTransfer ? "transferred" : ""} ${(isUrgent || isClarification) ? "urgent" : ""}`} onClick={onClick}
      style={!isUrgent && approvalBorder ? { borderLeft: `4px solid ${approvalBorder}`, background: approvalBorder + "0a" } : undefined}>
      <div className="tc-top">
        <span className="tc-id">#{ticket.id}</span>
        <span className="tc-text"><Highlight text={preview} query={search} /></span>
        <div className="tc-badges">
          {canToggleUrgent && (
            <button type="button" className={`urgent-toggle ${isUrgent ? "active" : ""}`}
              title={isUrgent ? "Снять пометку «Срочно»" : "Отметить как «Срочно»"}
              onClick={e => { e.stopPropagation(); onToggleUrgent(ticket.id, !isUrgent); }}>❗</button>
          )}
          {isClarification && (
            <span className="badge" style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>❗ Уточнение</span>
          )}
          {isTransfer && <span className="badge badge-transfer">Перенос</span>}
          {ticket.reopened && <span className="badge" style={{ background: "#cffafe", color: "#0891b2" }}>♻ Переоткрыта</span>}
          {ticket.cash_total > 0 && <span className="badge" style={{ background: "#dcfce7", color: "#16a34a" }}>💵 {ticket.cash_total}₽</span>}
          {ticket.sent_to_service_center && <span className="badge" style={{ background: "#dbeafe", color: "#2563eb" }}>🏢 Сервис центр</span>}
          {ticket.is_maintenance && <span className="badge" style={{ background: "#fef9c3", color: "#a16207" }}>🔧 ТО</span>}
          <span className="badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
          {showAccept && isAccepted && (
            <span className="badge-accepted" title={`${ticket.assigned_name || "Механик"} принял заявку в работу`}>✅</span>
          )}
          {showAccept && !isAccepted && isOwnMechanic && (
            <button type="button" className="accept-btn"
              onClick={e => { e.stopPropagation(); onAccept(ticket.id); }}>✅ Принять</button>
          )}
          {canUnassign && (
            <button type="button" className="unassign-btn" title="Вернуть в нераспределённые"
              onClick={e => { e.stopPropagation(); onUnassign(ticket.id); }}>↩</button>
          )}
        </div>
      </div>
      <div className="tc-meta">
        {ticket.assigned_name
          ? <span className="tc-meta-item">👷 <Highlight text={ticket.assigned_name} query={search} /></span>
          : <span className="tc-meta-item" style={{ color: "var(--muted2)" }}>Не назначен</span>}
        <span className="tc-meta-item">🕐 {fmt(ticket.created_at)}</span>
        {ticket.status !== "closed" && (() => {
          const days = Math.floor((Date.now() - new Date(ticket.created_at).getTime()) / 86400000);
          const color = days >= 7 ? "#ef4444" : days >= 4 ? "#f59e0b" : "#10b981";
          return <span className="tc-meta-item" style={{ color, fontWeight: 600 }}>⏱ {daysAgo(ticket.created_at)}</span>;
        })()}
        {ticket.photos?.length > 0 && <span className="tc-meta-item">📷 {ticket.photos.length}</span>}
      </div>
      {partsComments.length > 0 && (
        <div className="tc-parts-comments">
          <span className="tc-parts-label">📦 К запчастям:</span>
          {partsComments.map((c, i) => (
            <div key={i} className="tc-parts-comment">
              <Highlight text={c.text} query={search} />
            </div>
          ))}
        </div>
      )}
      {getActionComments(ticket).map((c, i) => {
        const m = actionCommentMeta(c.type);
        return (
          <div key={`ac-${i}`} className="tc-parts-comments" style={{ borderTopColor: m.border }}>
            <span className="tc-parts-label" style={{ color: m.color }}>{m.label}:</span>
            <div className="tc-parts-comment"><Highlight text={c.text} query={search} /></div>
          </div>
        );
      })}
      {(!isMechanicView || ticket.status === "parts_clarification") && ticket.manager_comment && (
        <div className="tc-parts-comments" style={{ borderTopColor: "#0891b2" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span className="tc-parts-label" style={{ color: "#0891b2" }}>
              {ticket.status === "parts_clarification" ? "❓ Уточнение:" : "🛠 Менеджер:"}
            </span>
            <CopyButton text={ticket.manager_comment} small />
          </div>
          <div className="tc-parts-comment"><Highlight text={ticket.manager_comment} query={search} /></div>
        </div>
      )}
      {!isMechanicView && ticket.approval_comment && (
        <div className="tc-parts-comments" style={{ borderTopColor: "#dc2626" }}>
          <span className="tc-parts-label" style={{ color: "#dc2626" }}>📝 Согласование:</span>
          <div className="tc-parts-comment"><Highlight text={ticket.approval_comment} query={search} /></div>
        </div>
      )}
      {ticket.invoices?.length > 0 && (
        <div className="tc-invoices">
          {ticket.invoices.filter(i => i.invoice || i.cash).map((i, idx) => (
            <span key={i.id || idx}>{i.cash && !i.invoice ? "нал" : `счет #${i.invoice}`}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create Modal ──────────────────────────────────────────────────────────────
function CreateModal({ onClose, onCreate, title = "Новая заявка",
  placeholder = "Опишите задачу, адрес, оборудование, проблему...",
  requirePhoto = false, photoLabel = "Фото / PDF к заявке" }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [fileStatus, setFileStatus] = useState([]);
  const [urgent, setUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const valid = text.trim().length > 0 && (!requirePhoto || files.length > 0);

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setFiles(prev => [...prev, ...arr]);
    setPreviews(prev => [...prev, ...arr.map(f => f.type.startsWith("image/") ? URL.createObjectURL(f) : null)]);
    setFileStatus(prev => [...prev, ...arr.map(() => ({ status: "queued", percent: 0 }))]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
    setFileStatus(prev => prev.filter((_, i) => i !== idx));
  };

  const [submitError, setSubmitError] = useState("");

  const submit = async () => {
    if (!valid) return;
    setLoading(true);
    setSubmitError("");
    setFileStatus(files.map(() => ({ status: "queued", percent: 0 })));
    try {
      await onCreate(text.trim(), files, urgent,
        (i, status, percent) => setFileStatus(prev => { const next = [...prev]; next[i] = { status, percent }; return next; }));
    } catch (e) {
      setSubmitError("Не удалось создать заявку. Проверьте интернет-соединение и попробуйте ещё раз.");
      setLoading(false);
    }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">{title}</div>
        <div className="form-group">
          <label className="form-label">Текст заявки</label>
          <textarea className="form-textarea" style={{ minHeight: 140 }} value={text}
            onChange={e => setText(e.target.value)} autoFocus
            placeholder={placeholder} />
        </div>
        <div className="form-group">
          <button type="button" className={`urgent-switch ${urgent ? "active" : ""}`}
            onClick={() => setUrgent(u => !u)}>
            <span className="urgent-switch-icon">❗</span>
            <span>Срочная заявка</span>
          </button>
        </div>
        <div className="form-group">
          <label className="form-label">{photoLabel} <span style={{fontWeight:400,textTransform:"none"}}>
            {requirePhoto ? "(обязательно)" : "(необязательно)"}
          </span></label>
          <div className="upload-zone" onClick={() => fileRef.current?.click()}>
            <input ref={fileRef} type="file" accept="image/*,.pdf" multiple
              onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
            📎 Прикрепить фото или PDF
          </div>
          {files.length > 0 && (
            <div className="photo-grid" style={{ marginTop: 7 }}>
              {files.map((f, i) => (
                <div key={i} className="photo-thumb-wrap">
                  {previews[i]
                    ? <img className="photo-thumb" src={previews[i]} alt="" />
                    : <div className="photo-thumb pdf-chip" title={f.name}>📄<span>{f.name}</span></div>
                  }
                  {loading && <span className="upload-status-badge">{uploadLabel(fileStatus[i]?.status, fileStatus[i]?.percent)}</span>}
                  {!loading && <button type="button" className="photo-remove-btn" title="Удалить файл"
                    onClick={() => removeFile(i)}>✕</button>}
                </div>
              ))}
            </div>
          )}
          {loading && files.length > 0 && (
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)", marginTop: 6 }}>
              Загружено {fileStatus.filter(s => s?.status === "done").length} из {files.length}
            </div>
          )}
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-icon" title="Вставить из буфера обмена"
            onClick={async () => {
              try {
                const t = await navigator.clipboard.readText();
                if (t) setText(prev => prev ? prev + "\n" + t : t);
              } catch { alert("Нет доступа к буферу обмена"); }
            }}>📋</button>
          <button className="btn btn-primary" disabled={!valid || loading} onClick={submit}>
            {loading
              ? (files.length > 0 ? `Загрузка... (${fileStatus.filter(s => s?.status === "done").length}/${files.length})` : "Создаём...")
              : "Создать"}
          </button>
        </div>
        {submitError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 8, textAlign: "right" }}>⚠ {submitError}</div>}
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function MaintenanceModal({ onClose }) {
  const [schedules, setSchedules] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [text, setText] = useState("");
  const [firstDate, setFirstDate] = useState(new Date().toISOString().slice(0, 10));
  const [interval, setInterval_] = useState(1);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoadingList(true);
    try {
      const res = await api.get("/api/maintenance-schedules");
      setSchedules(res.data || []);
    } catch (e) {
      // тихо игнорируем, список просто останется пустым
    }
    setLoadingList(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!text.trim()) { setError("Укажите текст заявки"); return; }
    if (!firstDate) { setError("Укажите первую дату"); return; }
    setError("");
    setSaving(true);
    try {
      await api.post("/api/maintenance-schedules", {
        text: text.trim(), first_date: firstDate, interval_months: Number(interval),
      });
      setText(""); setFirstDate(new Date().toISOString().slice(0, 10)); setInterval_(1);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось сохранить расписание");
    }
    setSaving(false);
  };

  const toggleActive = async (sch) => {
    setSchedules(prev => prev.map(s => s.id === sch.id ? { ...s, active: !s.active } : s));
    try {
      await api.patch(`/api/maintenance-schedules/${sch.id}`, { active: !sch.active });
    } catch (e) {
      load();
    }
  };

  const removeSchedule = async (id) => {
    if (!window.confirm("Удалить расписание ТО? Уже созданные заявки останутся.")) return;
    setSchedules(prev => prev.filter(s => s.id !== id));
    try {
      await api.delete(`/api/maintenance-schedules/${id}`);
    } catch (e) {
      load();
    }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">🔧 Расписания ТО</div>
        <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -6, marginBottom: 14, lineHeight: 1.5 }}>
          Заявка появится в «Нераспределённых» автоматически, когда наступит указанная дата, и будет повторяться с заданной периодичностью.
        </p>

        <div className="action-panel">
          <div className="action-panel-label">Новое расписание</div>
          <div className="form-group">
            <label className="form-label">Текст заявки</label>
            <textarea className="form-textarea" style={{ minHeight: 70 }} value={text}
              onChange={e => { setText(e.target.value); setError(""); }}
              placeholder="Например: плановое ТО конвектомата, кафе «Ромашка»..." />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
              <label className="form-label">Первая дата</label>
              <input type="date" className="form-input" value={firstDate}
                onChange={e => { setFirstDate(e.target.value); setError(""); }} />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
              <label className="form-label">Периодичность</label>
              <select className="form-select" value={interval} onChange={e => setInterval_(e.target.value)}>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>Каждые {n} {n === 1 ? "месяц" : n < 5 ? "месяца" : "месяцев"}</option>
                ))}
              </select>
            </div>
          </div>
          {error && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 8 }}>⚠ {error}</div>}
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={submit}>
            {saving ? "Сохранение..." : "+ Добавить расписание"}
          </button>
        </div>

        <hr className="divider" />

        <div className="sec-label">Существующие расписания ({schedules.length})</div>
        {loadingList ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>Загрузка...</div>
        ) : schedules.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>Расписаний пока нет</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {schedules.map(sch => (
              <div key={sch.id} style={{
                background: "var(--bg)", borderRadius: 8, padding: "10px 12px",
                opacity: sch.active ? 1 : 0.5,
              }}>
                <div style={{ fontSize: 13, marginBottom: 4 }}>{sch.text}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 11.5, color: "var(--muted)" }}>
                  <span>📅 Следующая: {sch.next_date?.slice(0, 10)}</span>
                  <span>🔁 Каждые {sch.interval_months} мес.</span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" style={{ padding: "3px 8px", fontSize: 11.5 }}
                      onClick={() => toggleActive(sch)}>
                      {sch.active ? "⏸ Приостановить" : "▶ Возобновить"}
                    </button>
                    <button className="btn-icon danger" title="Удалить расписание"
                      onClick={() => removeSchedule(sch.id)}>🗑</button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PHOTO_TYPE_LABELS = { ticket: "📷 Заявка", parts: "📦 Запчасти", act: "📋 АВР", service_center: "🏢 Сервис центр", extra: "📎 Доп. фото" };

function SuperEditModal({ ticket, onClose, onRefresh }) {
  const photoBaseUrl = window.location.origin;
  const [text, setText] = useState(ticket.text || "");
  const [status, setStatus] = useState(ticket.status);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]); // [{name, status}]
  const fileRefs = { ticket: useRef(), parts: useRef(), act: useRef(), service_center: useRef(), extra: useRef() };

  const photosByType = (type) => (ticket.photos_typed || []).filter(p => p.type === type);

  const save = async () => {
    if (!text.trim()) { setError("Текст не может быть пустым"); return; }
    setError("");
    setSaving(true);
    try {
      await api.patch(`/api/tickets/${ticket.id}/superadmin-edit`, { text: text.trim(), status, comment: comment.trim() });
      await onRefresh();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(e.response?.data?.error || "Не удалось сохранить изменения");
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Удалить это фото безвозвратно?")) return;
    try {
      await api.delete(`/api/tickets/${ticket.id}/photos/${photoId}`);
      onRefresh();
    } catch (e) {
      alert("Не удалось удалить фото");
    }
  };

  const uploadPhotos = async (type, fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setUploadingType(type);
    setUploadQueue(files.map(f => ({ name: f.name, status: "queued", percent: 0 })));
    try {
      await uploadPhotosSequential(ticket.id, files, type,
        (i, status, percent) => setUploadQueue(prev => { const next = [...prev]; next[i] = { ...next[i], status, percent }; return next; }));
    } catch (e) {
      alert("Не все фото удалось загрузить — уже отправленные сохранены, попробуйте прикрепить оставшиеся ещё раз");
    }
    await onRefresh();
    setUploadingType(null);
    setUploadQueue([]);
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">⚙️ Полное редактирование — заявка #{ticket.id}</div>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -6, marginBottom: 14, lineHeight: 1.4 }}>
          Доступно только суперадминистратору. Меняет статус на любой без ограничений обычного процесса — используйте аккуратно.
        </p>

        <div className="form-group">
          <label className="form-label">Статус заявки</label>
          <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
            {Object.entries(STATUS_LABELS_ALL).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Текст заявки</label>
          <textarea className="form-textarea" style={{ minHeight: 100 }} value={text} onChange={e => setText(e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Комментарий (необязательно)</label>
          <textarea className="form-textarea" style={{ minHeight: 50 }} value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Например: причина ручного изменения..." />
        </div>

        {error && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 10 }}>⚠ {error}</div>}

        <button className="btn btn-primary btn-sm" disabled={saving} onClick={save} style={{ width: "100%", marginBottom: 18 }}>
          {saving ? "Сохранение..." : "Сохранить изменения"}
        </button>

        <hr className="divider" />
        <div className="sec-label">Управление фото</div>

        {Object.entries(PHOTO_TYPE_LABELS).map(([type, label]) => {
          const photos = photosByType(type);
          return (
            <div key={type} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 6 }}>{label}</div>
              {photos.length > 0 && (
                <div className="photo-grid" style={{ marginBottom: 7 }}>
                  {photos.map(ph => (
                    <div key={ph.id} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={`${photoBaseUrl}${ph.url}`} alt="" />
                      <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => deletePhoto(ph.id)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="upload-zone" onClick={() => fileRefs[type].current?.click()}>
                <input ref={fileRefs[type]} type="file" accept="image/*" multiple
                  onChange={e => { uploadPhotos(type, e.target.files); e.target.value = ""; }} />
                {uploadingType === type ? "Загрузка..." : `📎 Добавить фото (${label})`}
              </div>
              {uploadingType === type && uploadQueue.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {uploadQueue.map((f, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--muted)" }}>
                        <span style={{ width: 28, textAlign: "center" }}>{uploadLabel(f.status, f.percent)}</span>
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--blue)", marginTop: 3 }}>
                    Загружено {uploadQueue.filter(f => f.status === "done").length} из {uploadQueue.length}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailModal({ ticket, user, mechanics, onClose, onAssign, onUnassign, onEdit, onDelete, onToggleUrgent, onAccept, onReopen, onSoftDelete, onSendToParts, onMechanicAction, onPartsReady, onPartsAction, onReturnFromClarification, onApprovalAction, onManagerComment, onApprovalComment, onRefresh, onLightbox, readOnly = false }) {
  const [assignTo, setAssignTo] = useState(ticket.assigned_to || mechanics[0]?.id || "");
  const [bypassConfirm, setBypassConfirm] = useState(false);
  const [toPartsConfirm, setToPartsConfirm] = useState(false);
  const [toPartsLoading, setToPartsLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [showTransferReason, setShowTransferReason] = useState(false);
  const [transferReason, setTransferReason] = useState("");
  const [transferError, setTransferError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(ticket.text || "");
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [managerComment, setManagerComment] = useState(ticket.manager_comment || "");
  const [managerCommentSaved, setManagerCommentSaved] = useState(true);
  const [partsActionLoading, setPartsActionLoading] = useState(false);
  const [returnClarLoading, setReturnClarLoading] = useState(false);
  const [approvalComment, setApprovalComment] = useState(ticket.approval_comment || "");
  const [approvalCommentSaved, setApprovalCommentSaved] = useState(true);
  const [approvalActionLoading, setApprovalActionLoading] = useState(false);
  const [partsFiles, setPartsFiles] = useState([]);
  const [partsPreviews, setPartsPreviews] = useState([]);
  const [partsFileStatus, setPartsFileStatus] = useState([]);
  const [actFiles, setActFiles] = useState([]);
  const [actPreviews, setActPreviews] = useState([]);
  const [actFileStatus, setActFileStatus] = useState([]);
  const [actError, setActError] = useState("");
  const [isCash, setIsCash] = useState(false);
  const [cashAmount, setCashAmount] = useState("");
  const [cashError, setCashError] = useState("");
  const [partsError, setPartsError] = useState("");
  const [actionError, setActionError] = useState("");
  const [reopenComment, setReopenComment] = useState("");
  const [reopenError, setReopenError] = useState("");
  const [reopenLoading, setReopenLoading] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [delError, setDelError] = useState("");
  const [delLoading, setDelLoading] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [showSuperEdit, setShowSuperEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const partsFileRef = useRef();
  const actFileRef = useRef();
  const scFileRef = useRef();
  const [scFiles, setScFiles] = useState([]);
  const [scPreviews, setScPreviews] = useState([]);
  const [scFileStatus, setScFileStatus] = useState([]);
  const [scError, setScError] = useState("");

  const sm = getStatusMeta(ticket);
  const isAdmin = user.role === "admin" || user.role === "superadmin";
  const isMechanic = user.role === "mechanic";
  const isParts = user.role === "parts";

  const canAssign   = isAdmin && (ticket.status === "unassigned" || ticket.status === "in_work" || ticket.status === "service_center");
  const canMechanic = isMechanic && ticket.status === "in_work";
  // Механик может оставить доп. комментарий/фото к своей же заявке на всём пути
  // "запчасти → согласование → закрыта" — статус меняется, но заявка остаётся за ним
  const canMechanicComment = isMechanic && ticket.assigned_to === user.id && [
    "closed", "awaiting_parts", "parts_clarification", "parts_ordered",
    "approval", "approval_sent", "approval_approved",
  ].includes(ticket.status);
  const [mechExtraComment, setMechExtraComment] = useState("");
  const [mechExtraError, setMechExtraError] = useState("");
  const [mechExtraLoading, setMechExtraLoading] = useState(false);
  const [mechExtraSent, setMechExtraSent] = useState(false);
  const [mechPhotoUploading, setMechPhotoUploading] = useState(false);
  const [mechPhotoQueue, setMechPhotoQueue] = useState([]); // [{name, status, percent}]
  const mechPhotoRef = useRef();

  const uploadMechanicExtraPhotos = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setMechPhotoUploading(true);
    setMechPhotoQueue(files.map(f => ({ name: f.name, status: "queued", percent: 0 })));
    try {
      await uploadPhotosSequential(ticket.id, files, "extra",
        (i, status, percent) => setMechPhotoQueue(prev => { const next = [...prev]; next[i] = { ...next[i], status, percent }; return next; }));
    } catch (e) {
      alert("Не все фото удалось загрузить — уже отправленные сохранены, попробуйте прикрепить оставшиеся ещё раз");
    }
    await onRefresh();
    setMechPhotoUploading(false);
    setMechPhotoQueue([]);
  };

  const sendMechanicComment = async () => {
    if (!mechExtraComment.trim()) { setMechExtraError("Введите текст комментария"); return; }
    setMechExtraError("");
    setMechExtraLoading(true);
    try {
      await api.post(`/api/tickets/${ticket.id}/mechanic-comment`, { comment: mechExtraComment.trim() });
      setMechExtraComment("");
      setMechExtraSent(true);
      await onRefresh();
    } catch (e) {
      setMechExtraError(e.response?.data?.error || "Не удалось отправить комментарий");
    } finally {
      setMechExtraLoading(false);
    }
  };
  const PARTS_STAGE_STATUSES = ["awaiting_parts", "parts_clarification", "parts_ordered"];
  const APPROVAL_STAGE_STATUSES = ["approval", "approval_sent", "approval_approved"];
  // Admin может назначить механика напрямую из статусов «запчасти»/«согласование» — в обход менеджера запчастей
  const canBypassAssign = isAdmin && (PARTS_STAGE_STATUSES.includes(ticket.status) || APPROVAL_STAGE_STATUSES.includes(ticket.status));
  const canParts    = !readOnly && isParts && (PARTS_STAGE_STATUSES.includes(ticket.status) || ticket.status === "service_center");
  const canReturnFromClarification = isMechanic && ticket.assigned_to === user.id && ticket.status === "parts_clarification";
  const canApproval = !readOnly && (isParts || isAdmin) && APPROVAL_STAGE_STATUSES.includes(ticket.status);
  const canEditDelete = isAdmin && ticket.status === "unassigned";
  const canReopen = isAdmin && ticket.status === "closed";
  const isSuperadmin = user.role === "superadmin";

  const doSoftDelete = async () => {
    if (!delReason.trim()) {
      setDelError("Укажите причину удаления заявки");
      return;
    }
    if (!delConfirm) { setDelConfirm(true); return; }
    setDelError("");
    setDelLoading(true);
    try {
      await onSoftDelete(ticket.id, delReason.trim());
      onClose();
    } catch (e) {
      setDelLoading(false);
      setDelError(e.response?.data?.error || "Не удалось удалить заявку. Попробуйте ещё раз.");
    }
  };

  const doReopen = async (mode) => {
    if (!reopenComment.trim()) {
      setReopenError("Укажите причину переоткрытия заявки");
      return;
    }
    setReopenError("");
    setReopenLoading(true);
    try {
      await onReopen(ticket.id, mode, reopenComment.trim());
      onClose();
    } catch (e) {
      setReopenLoading(false);
      setReopenError(e.response?.data?.error || "Не удалось переоткрыть заявку. Попробуйте ещё раз.");
    }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setEditSaving(true);
    try {
      await onEdit(ticket.id, editText.trim());
      setEditMode(false);
    } finally { setEditSaving(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Удалить заявку #${ticket.id}? Это действие необратимо.`)) return;
    setDeleting(true);
    try {
      await onDelete(ticket.id);
      onClose();
    } finally { setDeleting(false); }
  };

  const handlePartsFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setPartsFiles(prev => [...prev, ...arr]);
    setPartsPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setPartsFileStatus(prev => [...prev, ...arr.map(() => ({ status: "queued", percent: 0 }))]);
    setPartsError("");
    setToPartsConfirm(false);
  };

  const removePartsFile = (idx) => {
    setPartsFiles(prev => prev.filter((_, i) => i !== idx));
    setPartsPreviews(prev => prev.filter((_, i) => i !== idx));
    setPartsFileStatus(prev => prev.filter((_, i) => i !== idx));
    setToPartsConfirm(false);
  };

  const doSendToParts = async () => {
    if (partsFiles.length === 0 && !toPartsConfirm) {
      setToPartsConfirm(true);
      setPartsError("Нет фото запчастей. Нажмите красную кнопку, если отправляете без фото.");
      return;
    }
    setPartsError("");
    setToPartsLoading(true);
    if (partsFiles.length) {
      setPartsFileStatus(partsFiles.map(() => ({ status: "queued", percent: 0 })));
    }
    try {
      await onSendToParts(
        ticket.id,
        partsFiles,
        (i, status, percent) => setPartsFileStatus(prev => { const next = [...prev]; next[i] = { status, percent }; return next; })
      );
      onClose();
    } catch (e) {
      setToPartsLoading(false);
      setPartsError(e.response?.data?.error || e.message || "Не удалось отправить заявку");
    }
  };

  const handleActFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setActFiles(prev => [...prev, ...arr]);
    setActPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setActFileStatus(prev => [...prev, ...arr.map(() => ({ status: "queued", percent: 0 }))]);
    setActError("");
  };

  const removeActFile = (idx) => {
    setActFiles(prev => prev.filter((_, i) => i !== idx));
    setActPreviews(prev => prev.filter((_, i) => i !== idx));
    setActFileStatus(prev => prev.filter((_, i) => i !== idx));
  };

  const handleScFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setScFiles(prev => [...prev, ...arr]);
    setScPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setScFileStatus(prev => [...prev, ...arr.map(() => ({ status: "queued", percent: 0 }))]);
    setScError("");
  };

  const removeScFile = (idx) => {
    setScFiles(prev => prev.filter((_, i) => i !== idx));
    setScPreviews(prev => prev.filter((_, i) => i !== idx));
    setScFileStatus(prev => prev.filter((_, i) => i !== idx));
  };

  const doAction = async (action) => {
    setActionError("");

    // Наличка может быть отмечена на любом из трёх действий — проверяем сумму один раз
    let cashData = null;
    if (isCash && ["close", "parts", "service_center"].includes(action)) {
      const amountNum = Number(cashAmount);
      if (!cashAmount || isNaN(amountNum) || amountNum <= 0) {
        setCashError("Укажите корректную сумму");
        return;
      }
      cashData = { isCash: true, amount: amountNum };
    }
    setCashError("");

    if (action === "close") {
      if (actFiles.length === 0) {
        setActError("Приложите фото акта выполненных работ");
        return;
      }
      setLoading(true);
      setActFileStatus(actFiles.map(() => ({ status: "queued", percent: 0 })));
      try {
        await onMechanicAction(ticket.id, action, comment, actFiles, "act", cashData,
          (i, status, percent) => setActFileStatus(prev => { const next = [...prev]; next[i] = { status, percent }; return next; }));
        onClose();
      } catch (e) {
        setLoading(false);
        setActionError(
          e.response?.data?.error ||
          "Не удалось загрузить фото или сохранить статус. Проверьте интернет-соединение и нажмите «Закрыть заявку» ещё раз — уже загруженные фото не потеряны, отправятся только оставшиеся."
        );
      }
      return;
    }
    if (action === "parts") {
      let blocked = false;
      if (partsFiles.length === 0) {
        setPartsError("Приложите фото запчастей/АВР");
        blocked = true;
      }
      if (!comment.trim()) {
        setCommentError("Оставьте комментарий");
        blocked = true;
      }
      if (blocked) return;
      setPartsError("");
      setCommentError("");
      setLoading(true);
      setPartsFileStatus(partsFiles.map(() => ({ status: "queued", percent: 0 })));
      try {
        await onMechanicAction(ticket.id, action, comment, partsFiles, "parts", cashData,
          (i, status, percent) => setPartsFileStatus(prev => { const next = [...prev]; next[i] = { status, percent }; return next; }));
        onClose();
      } catch (e) {
        setLoading(false);
        setActionError(
          e.response?.data?.error ||
          "Не удалось загрузить фото или сохранить статус. Проверьте интернет-соединение и попробуйте ещё раз — уже загруженные фото не потеряны, отправятся только оставшиеся."
        );
      }
      return;
    }
    if (action === "service_center") {
      if (scFiles.length === 0) {
        setScError("Приложите фото для передачи в Сервис центр");
        return;
      }
      setLoading(true);
      setScFileStatus(scFiles.map(() => ({ status: "queued", percent: 0 })));
      try {
        await onMechanicAction(ticket.id, action, comment, scFiles, "service_center", cashData,
          (i, status, percent) => setScFileStatus(prev => { const next = [...prev]; next[i] = { status, percent }; return next; }));
        onClose();
      } catch (e) {
        setLoading(false);
        setActionError(
          e.response?.data?.error ||
          "Не удалось загрузить фото или сохранить статус. Проверьте интернет-соединение и попробуйте ещё раз — уже загруженные фото не потеряны, отправятся только оставшиеся."
        );
      }
      return;
    }
    if (action === "transfer") {
      if (!showTransferReason) { setShowTransferReason(true); return; }
      if (!transferReason.trim()) {
        setTransferError("Укажите причину переноса");
        return;
      }
      setTransferError("");
      setLoading(true);
      try {
        await onMechanicAction(ticket.id, action, transferReason.trim(), [], "parts");
        onClose();
      } catch (e) {
        setLoading(false);
        setActionError("Не удалось сохранить изменения. Проверьте интернет-соединение и попробуйте ещё раз.");
      }
      return;
    }
    setLoading(true);
    try {
      await onMechanicAction(ticket.id, action, comment, [], "parts");
      onClose();
    } catch (e) {
      setLoading(false);
      setActionError("Не удалось сохранить изменения. Проверьте интернет-соединение и попробуйте ещё раз.");
    }
  };

  const photoBaseUrl = window.location.origin;

  // Разбиваем фото по типам
  const ticketPhotos = (ticket.photos_typed || []).filter(p => p.type === "ticket");
  const partsPhotos  = (ticket.photos_typed || []).filter(p => p.type === "parts");
  const actPhotos    = (ticket.photos_typed || []).filter(p => p.type === "act");
  const scTypedPhotos = (ticket.photos_typed || []).filter(p => p.type === "service_center");
  const extraPhotos  = (ticket.photos_typed || []).filter(p => p.type === "extra");
  // Обратная совместимость: если photos_typed нет, показываем все в старом порядке
  const legacyPhotos = ticket.photos_typed ? [] : (ticket.photos || []);

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap", paddingRight: 34 }}>
          <div className="modal-title" style={{ margin: 0 }}>Заявка #{ticket.id}</div>
          <span className="badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
          {ticket.status === "in_work" && ticket.accepted_at && (
            <span className="badge-accepted" title={`${ticket.assigned_name || "Механик"} принял в работу: ${fmt(ticket.accepted_at)}`}>✅</span>
          )}
          {ticket.status === "in_work" && !ticket.accepted_at && isMechanic && ticket.assigned_to === user.id && (
            <button type="button" className="accept-btn" onClick={() => onAccept(ticket.id)}>✅ Принять в работу</button>
          )}
          {ticket.transferred && ticket.status === "unassigned" &&
            <span className="badge badge-transfer">Перенос</span>}
          {ticket.urgent && <span className="badge" style={{ background: "var(--red-bg)", color: "var(--red)" }}>❗ Срочно</span>}
          {ticket.reopened && <span className="badge" style={{ background: "#cffafe", color: "#0891b2" }}>♻ Переоткрыта</span>}
          {ticket.cash_total > 0 && <span className="badge" style={{ background: "#dcfce7", color: "#16a34a" }}>💵 {ticket.cash_total}₽</span>}
          {ticket.sent_to_service_center && <span className="badge" style={{ background: "#dbeafe", color: "#2563eb" }}>🏢 Сервис центр</span>}
          {ticket.is_maintenance && <span className="badge" style={{ background: "#fef9c3", color: "#a16207" }}>🔧 ТО</span>}
          {isAdmin && (
            <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
              {canEditDelete && !editMode && (<>
                <button className="btn-icon" title="Редактировать текст заявки"
                  onClick={() => { setEditText(ticket.text || ""); setEditMode(true); }}>✏️</button>
                <button className="btn-icon danger" title="Удалить заявку" disabled={deleting}
                  onClick={doDelete}>🗑</button>
              </>)}
              <button className={`urgent-toggle ${ticket.urgent ? "active" : ""}`}
                title={ticket.urgent ? "Снять пометку «Срочно»" : "Отметить как «Срочно»"}
                onClick={() => onToggleUrgent(ticket.id, !ticket.urgent)}>❗</button>
              {(isSuperadmin || (isAdmin && ticket.status === "service_center")) && (
                <button className="btn-icon" title="Полное редактирование"
                  onClick={() => setShowSuperEdit(true)}>⚙️</button>
              )}
            </div>
          )}
        </div>

        {showSuperEdit && (
          <SuperEditModal ticket={ticket} onClose={() => setShowSuperEdit(false)} onRefresh={onRefresh} />
        )}

        {ticket.assigned_name && (
          <div className="kv-row"><label>Механик</label><value>{ticket.assigned_name}</value></div>
        )}
        <div className="kv-row"><label>Создана</label><value>{fmt(ticket.created_at)}</value></div>

        {editMode ? (
          <div className="form-group" style={{ marginTop: 10 }}>
            <textarea className="form-textarea" style={{ minHeight: 120 }} value={editText}
              onChange={e => setEditText(e.target.value)} autoFocus />
            <div className="form-actions" style={{ marginTop: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setEditMode(false); setEditText(ticket.text || ""); }}>
                Отмена
              </button>
              <button className="btn btn-primary btn-sm" disabled={editSaving || !editText.trim()} onClick={saveEdit}>
                {editSaving ? "Сохраняем..." : "Сохранить"}
              </button>
            </div>
          </div>
        ) : (
          <div className="desc-box" style={{ marginTop: 10 }}>{linkifyPhones(ticket.text) || "—"}</div>
        )}

        {getActionComments(ticket).length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 12 }}>
            {getActionComments(ticket).map((c, i) => {
              const m = actionCommentMeta(c.type);
              return (
                <div key={i} style={{ background: m.bg, borderRadius: 7, padding: "8px 12px", fontSize: 13, color: m.color, borderLeft: `3px solid ${m.border}` }}>
                  <div style={{ fontWeight: 600, fontSize: 11.5, marginBottom: 2 }}>{m.label}</div>
                  <div style={{ lineHeight: 1.4 }}>{c.text}</div>
                  {c.at && <div style={{ fontSize: 11, color: m.color, opacity: 0.65, marginTop: 3 }}>{fmt(c.at)}</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Доп. комментарии механика — видны всем, кто открывает заявку, прямо под текстом */}
        {ticket.mechanic_comments?.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 12 }}>
            {ticket.mechanic_comments.map((c, i) => (
              <div key={i} style={{ background: "#fffbeb", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#92400e", borderLeft: "3px solid #d97706" }}>
                <div style={{ fontWeight: 600, fontSize: 11.5, marginBottom: 2 }}>💬 Доп. комментарий{c.author ? ` от ${c.author}` : ""}</div>
                <div style={{ lineHeight: 1.4 }}>{c.text}</div>
                <div style={{ fontSize: 11, color: "#d9770699", marginTop: 3 }}>{fmt(c.at)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Доп. фото механика — видны всем, кто открывает заявку */}
        {extraPhotos.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: "#92400e", marginBottom: 6 }}>📎 Доп. фото от механика</div>
            <div className="photo-grid">
              {extraPhotos.map((p, i) => (
                <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                  onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
              ))}
            </div>
          </div>
        )}

        {/* Фото заявки */}
        {ticketPhotos.length > 0 && <>
          <div className="sec-label">📎 Фото заявки</div>
          <div className="photo-grid">
            {ticketPhotos.map((p, i) => (
              p.url.endsWith(".pdf")
                ? <a key={i} href={`${photoBaseUrl}${p.url}`} target="_blank" rel="noreferrer"
                    className="pdf-link">📄 PDF документ</a>
                : <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                    onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {/* Фото запчастей + комментарии */}
        {(partsPhotos.length > 0 || ticket.parts_comments?.length > 0) && <>
          <div className="sec-label">📦 Запчасти</div>
          {partsPhotos.length > 0 && (
            <div className="photo-grid" style={{ marginBottom: 8 }}>
              {partsPhotos.map((p, i) => (
                <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                  onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
              ))}
            </div>
          )}
          {ticket.parts_comments?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {ticket.parts_comments.map((c, i) => (
                <div key={i} style={{ background: "#faf5ff", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#4c1d95", borderLeft: "3px solid #7c3aed" }}>
                  <div style={{ lineHeight: 1.4 }}>{c.text}</div>
                  <div style={{ fontSize: 11, color: "#7c3aed99", marginTop: 3 }}>{fmt(c.at)}</div>
                </div>
              ))}
            </div>
          )}
          <hr className="divider" />
        </>}

        {/* Комментарий менеджера: механик видит только на уточнении, иначе конфиденциально */}
        {!canParts && ticket.manager_comment && (!isMechanic || ticket.status === "parts_clarification") && (
          <>
            <div className="sec-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>{ticket.status === "parts_clarification" ? "❓ Уточнение от менеджера" : "🛠 Комментарий менеджера"}</span>
              <CopyButton text={ticket.manager_comment} />
            </div>
            <div style={{ background: "#ecfeff", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#164e63", borderLeft: "3px solid #0891b2", marginBottom: 12 }}>
              {ticket.manager_comment}
            </div>
            <hr className="divider" />
          </>
        )}

        {/* Комментарий администратора по согласованию (просмотр; скрыт от механика — конфиденциально) */}
        {!canApproval && !isMechanic && ticket.approval_comment && (
          <>
            <div className="sec-label">📝 Комментарий по согласованию</div>
            <div style={{ background: "#fef2f2", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#7f1d1d", borderLeft: "3px solid #dc2626", marginBottom: 12 }}>
              {ticket.approval_comment}
            </div>
            <hr className="divider" />
          </>
        )}

        {/* Фото АВР */}
        {actPhotos.length > 0 && <>
          <div className="sec-label">📋 Фото АВР / наклейка</div>
          <div className="photo-grid">
            {actPhotos.map((p, i) => (
              <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {scTypedPhotos.length > 0 && <>
          <div className="sec-label">🏢 Фото для Сервис центра</div>
          <div className="photo-grid">
            {scTypedPhotos.map((p, i) => (
              <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {/* Обратная совместимость — старые фото без типа */}
        {legacyPhotos.length > 0 && <>
          <div className="sec-label">📷 Фото</div>
          <div className="photo-grid">
            {legacyPhotos.map((src, i) => (
              <img key={i} className="photo-thumb" src={`${photoBaseUrl}${src}`} alt=""
                onClick={() => onLightbox(`${photoBaseUrl}${src}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {canAssign && (
          <div className="action-panel">
            <div className="action-panel-label">
              {ticket.status === "in_work" ? "Переназначить механика" : "Назначить механика"}
            </div>
            <div className="assign-row">
              <select className="form-select" value={assignTo} onChange={e => setAssignTo(e.target.value)}>
                {mechanics.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                await onAssign(ticket.id, assignTo); onClose();
              }}>
                {ticket.status === "in_work" ? "Переназначить" : "Назначить"}
              </button>
            </div>
            {ticket.status === "unassigned" && (
              <div style={{ marginTop: 8 }}>
                {!(toPartsConfirm || partsFiles.length > 0 || toPartsLoading) ? (
                  <button type="button" className="btn btn-outline btn-sm" style={{ width: "100%" }}
                    onClick={doSendToParts}>
                    📦 Отправить сразу в «Ожидаем запчасти»
                  </button>
                ) : (
                  <>
                    <label className="form-label" style={{ color: partsFiles.length === 0 ? "var(--red)" : "var(--muted)" }}>
                      📦 Фото запчастей <span style={{ fontWeight: 400, textTransform: "none" }}>(нужно для отправки)</span>
                    </label>
                    <div className="upload-zone" style={{ borderColor: partsError ? "var(--red)" : undefined }}
                      onClick={() => partsFileRef.current?.click()}>
                      <input ref={partsFileRef} type="file" accept="image/*" multiple
                        onChange={e => { handlePartsFiles(e.target.files); e.target.value = ""; }} />
                      📎 Прикрепить фото запчастей
                    </div>
                    {partsPreviews.length > 0 && (
                      <div className="photo-grid" style={{ marginTop: 7 }}>
                        {partsPreviews.map((s, i) => (
                          <div key={i} className="photo-thumb-wrap">
                            <img className="photo-thumb" src={s} alt="" />
                            {toPartsLoading && <span className="upload-status-badge">{uploadLabel(partsFileStatus[i]?.status, partsFileStatus[i]?.percent)}</span>}
                            {!toPartsLoading && <button type="button" className="photo-remove-btn" title="Удалить фото"
                              onClick={() => removePartsFile(i)}>✕</button>}
                          </div>
                        ))}
                      </div>
                    )}
                    {toPartsLoading && partsFiles.length > 0 && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)", marginTop: 5 }}>
                        Загружено {partsFileStatus.filter(s => s?.status === "done").length} из {partsFiles.length}
                      </div>
                    )}
                    {partsError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {partsError}</div>}
                    <p style={{ fontSize: 12, color: "var(--muted)", margin: "8px 0 6px" }}>
                      Заявка уйдёт менеджеру запчастей, минуя механика.
                    </p>
                    <button type="button" className="btn btn-sm" disabled={toPartsLoading}
                      style={partsFiles.length === 0
                        ? { width: "100%", background: "var(--red)", color: "#fff", border: "1.5px solid var(--red)" }
                        : { width: "100%", background: "var(--blue)", color: "#fff", border: "1.5px solid var(--blue)" }}
                      onClick={doSendToParts}>
                      {toPartsLoading
                        ? "Отправка..."
                        : partsFiles.length === 0
                          ? "⚠ Отправить без фото"
                          : "📦 Отправить с фото"}
                    </button>
                  </>
                )}
              </div>
            )}
            {ticket.status === "service_center" && (
              <button className="btn btn-outline btn-sm" style={{ marginTop: 8, width: "100%" }}
                onClick={async () => { await onUnassign(ticket.id); onClose(); }}>
                ↩ Вернуть в нераспределённые
              </button>
            )}
          </div>
        )}

        {canBypassAssign && (
          <div className="action-panel" style={{ borderColor: "var(--red)" }}>
            <div className="action-panel-label" style={{ color: "var(--red)" }}>Назначить механика в обход</div>
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -4, marginBottom: 10 }}>
              Заявка сейчас на стадии «{PARTS_STAGE_STATUSES.includes(ticket.status) ? "запчасти" : "согласование"}».
              Прямое назначение переведёт её в «В работе» в обход менеджера запчастей — используйте, только если это осознанное решение.
            </p>
            <div className="assign-row">
              <select className="form-select" value={assignTo} onChange={e => { setAssignTo(e.target.value); setBypassConfirm(false); }}>
                {mechanics.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button className="btn btn-sm" disabled={loading}
                style={{ background: bypassConfirm ? "var(--red)" : "transparent", color: bypassConfirm ? "#fff" : "var(--red)", border: "1.5px solid var(--red)" }}
                onClick={async () => {
                  if (!bypassConfirm) { setBypassConfirm(true); return; }
                  await onAssign(ticket.id, assignTo); onClose();
                }}>
                {bypassConfirm ? "⚠ Нажмите ещё раз для подтверждения" : "Назначить в обход"}
              </button>
            </div>
          </div>
        )}

        {canMechanic && (
          <div className="action-panel">
            <div className="action-panel-label">Действия</div>
            <div className="form-group">
              <label className="form-label" style={{ color: commentError ? "var(--red)" : undefined }}>Комментарий</label>
              <textarea className="form-textarea" style={{ minHeight: 52, borderColor: commentError ? "var(--red)" : undefined }} value={comment}
                onChange={e => { setComment(e.target.value); setCommentError(""); }} />
              {commentError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {commentError}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: partsFiles.length === 0 ? "var(--red)" : "var(--muted)" }}>
                📦 Фото запчастей/АВР <span style={{fontWeight:400,textTransform:"none"}}>(обязательно для «Ожидаем запчасти»)</span>
              </label>
              <div className="upload-zone" style={{ borderColor: partsError ? "var(--red)" : undefined }}
                onClick={() => partsFileRef.current?.click()}>
                <input ref={partsFileRef} type="file" accept="image/*" multiple
                  onChange={e => { handlePartsFiles(e.target.files); e.target.value = ""; }} />
                📎 Выбрать фото запчастей/АВР
              </div>
              {partsPreviews.length > 0 && (
                <div className="photo-grid" style={{ marginTop: 7 }}>
                  {partsPreviews.map((s, i) => (
                    <div key={i} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={s} alt="" />
                      {loading && <span className="upload-status-badge">{uploadLabel(partsFileStatus[i]?.status, partsFileStatus[i]?.percent)}</span>}
                      {!loading && <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => removePartsFile(i)}>✕</button>}
                    </div>
                  ))}
                </div>
              )}
              {loading && partsFiles.length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)", marginTop: 5 }}>
                  Загружено {partsFileStatus.filter(s => s?.status === "done").length} из {partsFiles.length}
                </div>
              )}
              {partsError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {partsError}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: actFiles.length === 0 ? "var(--red)" : "var(--muted)" }}>
                📋 Фото АВР / наклейка <span style={{fontWeight:400,textTransform:"none"}}>(обязательно для закрытия)</span>
              </label>
              <div className="upload-zone" style={{ borderColor: actError ? "var(--red)" : undefined }}
                onClick={() => actFileRef.current?.click()}>
                <input ref={actFileRef} type="file" accept="image/*" multiple
                  onChange={e => { handleActFiles(e.target.files); e.target.value = ""; }} />
                📎 Выбрать фото АВР
              </div>
              {actPreviews.length > 0 && (
                <div className="photo-grid" style={{ marginTop: 7 }}>
                  {actPreviews.map((s, i) => (
                    <div key={i} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={s} alt="" />
                      {loading && <span className="upload-status-badge">{uploadLabel(actFileStatus[i]?.status, actFileStatus[i]?.percent)}</span>}
                      {!loading && <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => removeActFile(i)}>✕</button>}
                    </div>
                  ))}
                </div>
              )}
              {loading && actFiles.length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)", marginTop: 5 }}>
                  Загружено {actFileStatus.filter(s => s?.status === "done").length} из {actFiles.length}
                </div>
              )}
              {actError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {actError}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: scFiles.length === 0 ? "var(--red)" : "var(--muted)" }}>
                🏢 Фото для Сервис центра <span style={{fontWeight:400,textTransform:"none"}}>(обязательно для отправки)</span>
              </label>
              <div className="upload-zone" style={{ borderColor: scError ? "var(--red)" : undefined }}
                onClick={() => scFileRef.current?.click()}>
                <input ref={scFileRef} type="file" accept="image/*" multiple
                  onChange={e => { handleScFiles(e.target.files); e.target.value = ""; }} />
                📎 Выбрать фото
              </div>
              {scPreviews.length > 0 && (
                <div className="photo-grid" style={{ marginTop: 7 }}>
                  {scPreviews.map((s, i) => (
                    <div key={i} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={s} alt="" />
                      {loading && <span className="upload-status-badge">{uploadLabel(scFileStatus[i]?.status, scFileStatus[i]?.percent)}</span>}
                      {!loading && <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => removeScFile(i)}>✕</button>}
                    </div>
                  ))}
                </div>
              )}
              {loading && scFiles.length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)", marginTop: 5 }}>
                  Загружено {scFileStatus.filter(s => s?.status === "done").length} из {scFiles.length}
                </div>
              )}
              {scError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {scError}</div>}
            </div>

            <div className="form-group">
              <button type="button" className={`cash-switch ${isCash ? "active" : ""}`}
                onClick={() => { setIsCash(v => !v); setCashError(""); }}>
                <span className="cash-switch-icon">💵</span>
                <span>Оплата наличными</span>
              </button>
              {isCash && (
                <div style={{ marginTop: 8 }}>
                  <input type="number" inputMode="decimal" className="form-input"
                    placeholder="Сумма, ₽" value={cashAmount}
                    onChange={e => { setCashAmount(e.target.value); setCashError(""); }}
                    style={{ borderColor: cashError ? "var(--red)" : undefined }} />
                  {cashError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {cashError}</div>}
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                    Сумма будет записана при нажатии любой из кнопок ниже — «Ожидаем запчасти», «Сервис центр» или «Закрыть»
                  </div>
                </div>
              )}
            </div>

            {actionError && (
              <div style={{
                background: "var(--red-bg)", color: "var(--red)", fontSize: 13, fontWeight: 600,
                padding: "10px 12px", borderRadius: 8, marginBottom: 10, lineHeight: 1.4,
              }}>
                ⚠ {actionError}
              </div>
            )}

            {showTransferReason && (
              <div className="form-group">
                <label className="form-label" style={{ color: transferError ? "var(--red)" : undefined }}>Причина переноса</label>
                <textarea className="form-textarea" style={{ minHeight: 44, borderColor: transferError ? "var(--red)" : undefined }}
                  value={transferReason} autoFocus
                  onChange={e => { setTransferReason(e.target.value); setTransferError(""); }}
                  placeholder="Например: нет доступа на объект, перенос по просьбе клиента..." />
                {transferError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {transferError}</div>}
              </div>
            )}

            <div className="action-btns">
              <button className="btn btn-outline btn-sm" disabled={loading} onClick={() => doAction("parts")}>
                {loading ? "Отправка..." : "📦 Ожидаем запчасти"}
              </button>
              <button className="btn btn-ghost btn-sm" disabled={loading} onClick={() => doAction("transfer")}>
                {showTransferReason ? "🔄 Подтвердить перенос" : "🔄 Перенести"}
              </button>
              <button className="btn btn-outline btn-sm" disabled={loading}
                style={{ borderColor: "#2563eb", color: "#2563eb" }} onClick={() => doAction("service_center")}>
                {loading ? "Отправка..." : "🏢 Сервис центр"}
              </button>
              <button className="btn btn-red btn-sm" disabled={loading} onClick={() => doAction("close")}>
                {loading ? "Отправка..." : "✓ Закрыть"}
              </button>
            </div>
          </div>
        )}

        {canParts && (
          <div className="action-panel">
            <div className="action-panel-label">Менеджер запчастей</div>

            <div className="action-btns" style={{ marginBottom: 12 }}>
              <button className="btn btn-outline btn-sm" disabled={partsActionLoading}
                onClick={async () => { setPartsActionLoading(true); await onPartsAction(ticket.id, "clarification"); setPartsActionLoading(false); }}>
                ❓ Уточнение
              </button>
              <button className="btn btn-outline btn-sm" disabled={partsActionLoading}
                onClick={async () => { setPartsActionLoading(true); await onPartsAction(ticket.id, "ordered"); setPartsActionLoading(false); }}>
                🛒 Запчасти заказаны
              </button>
              <button className="btn btn-outline btn-sm" disabled={partsActionLoading}
                style={{ borderColor: "#dc2626", color: "#dc2626" }}
                onClick={async () => { setPartsActionLoading(true); await onPartsAction(ticket.id, "to_approval"); setPartsActionLoading(false); onClose(); }}>
                📨 Согласование
              </button>
              <button className="btn btn-green btn-sm" disabled={partsActionLoading}
                onClick={async () => { setPartsActionLoading(true); await onPartsReady(ticket.id); setPartsActionLoading(false); onClose(); }}>
                ✓ Запчасть готова
              </button>
              <button className="btn btn-ghost btn-sm" disabled={partsActionLoading}
                style={{ color: "#78716c" }}
                onClick={async () => {
                  if (!window.confirm("Списать заявку? Запчасти не найдены — заявка будет закрыта.")) return;
                  setPartsActionLoading(true); await onPartsAction(ticket.id, "write_off"); setPartsActionLoading(false); onClose();
                }}>
                🗑 Пшлнх
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Комментарий менеджера</label>
              <textarea className="form-textarea" style={{ minHeight: 70 }} value={managerComment}
                onChange={e => { setManagerComment(e.target.value); setManagerCommentSaved(false); }}
                placeholder="Заметки по запчастям, поставщику, срокам..." />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, gap: 8, alignItems: "center" }}>
                {managerCommentSaved && managerComment && <span style={{ fontSize: 11, color: "var(--muted2)" }}>✓ сохранено</span>}
                <button className="btn btn-primary btn-sm" disabled={managerCommentSaved}
                  onClick={async () => { await onManagerComment(ticket.id, managerComment); setManagerCommentSaved(true); }}>
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        )}

        {canApproval && (
          <div className="action-panel">
            <div className="action-panel-label">Согласование</div>

            <div className="action-btns" style={{ marginBottom: 12 }}>
              {isAdmin && (
                <>
                  <button className="btn btn-outline btn-sm" disabled={approvalActionLoading}
                    style={{ borderColor: "#ca8a04", color: "#ca8a04" }}
                    onClick={async () => { setApprovalActionLoading(true); await onApprovalAction(ticket.id, "sent"); setApprovalActionLoading(false); }}>
                    📤 Отправлено на согласование
                  </button>
                  <button className="btn btn-outline btn-sm" disabled={approvalActionLoading}
                    style={{ borderColor: "#16a34a", color: "#16a34a" }}
                    onClick={async () => { setApprovalActionLoading(true); await onApprovalAction(ticket.id, "approved"); setApprovalActionLoading(false); }}>
                    ✅ Согласовано
                  </button>
                  <button className="btn btn-outline btn-sm" disabled={approvalActionLoading}
                    style={{ borderColor: "#dc2626", color: "#dc2626" }}
                    onClick={async () => {
                      if (!window.confirm("Отметить как «не согласовано»? Заявка будет закрыта.")) return;
                      setApprovalActionLoading(true); await onApprovalAction(ticket.id, "not_approved"); setApprovalActionLoading(false); onClose();
                    }}>
                    ❌ Не согласовано
                  </button>
                </>
              )}
              {isParts && (
                <button className="btn btn-green btn-sm" disabled={approvalActionLoading}
                  onClick={async () => { setApprovalActionLoading(true); await onApprovalAction(ticket.id, "ordered"); setApprovalActionLoading(false); onClose(); }}>
                  🛒 Запчасть заказана
                </button>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Комментарий администратора</label>
              {isAdmin ? (
                <>
                  <textarea className="form-textarea" style={{ minHeight: 70 }} value={approvalComment}
                    onChange={e => { setApprovalComment(e.target.value); setApprovalCommentSaved(false); }}
                    placeholder="Заметки по согласованию..." />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, gap: 8, alignItems: "center" }}>
                    {approvalCommentSaved && approvalComment && <span style={{ fontSize: 11, color: "var(--muted2)" }}>✓ сохранено</span>}
                    <button className="btn btn-primary btn-sm" disabled={approvalCommentSaved}
                      onClick={async () => { await onApprovalComment(ticket.id, approvalComment); setApprovalCommentSaved(true); }}>
                      Сохранить
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ background: "#fef2f2", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#7f1d1d", borderLeft: "3px solid #dc2626" }}>
                  {ticket.approval_comment || <span style={{ color: "#7f1d1d99" }}>Комментарий администратора отсутствует</span>}
                </div>
              )}
            </div>
          </div>
        )}

        {canReopen && (
          <div className="action-panel">
            <div className="action-panel-label">Переоткрыть заявку</div>
            <div className="form-group">
              <label className="form-label">Причина переоткрытия</label>
              <textarea className="form-textarea" style={{ minHeight: 70, borderColor: reopenError ? "var(--red)" : undefined }}
                value={reopenComment}
                onChange={e => { setReopenComment(e.target.value); setReopenError(""); }}
                placeholder="Например: клиент сообщил о повторной поломке..." />
              {reopenError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {reopenError}</div>}
            </div>
            <div className="action-btns">
              <button className="btn btn-outline btn-sm" disabled={reopenLoading} onClick={() => doReopen("new")}>
                🔄 Открыть заново
              </button>
              <button className="btn btn-outline btn-sm" disabled={reopenLoading}
                style={{ borderColor: "#0891b2", color: "#0891b2" }} onClick={() => doReopen("parts")}>
                📦 В «Ожидаем запчасти»
              </button>
            </div>
          </div>
        )}

        {canReturnFromClarification && (
          <div className="action-panel">
            <div className="action-panel-label">Ответ на уточнение</div>
            {ticket.manager_comment ? (
              <>
                <div style={{ background: "#ecfeff", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#164e63", borderLeft: "3px solid #0891b2", marginBottom: 10 }}>
                  {ticket.manager_comment}
                </div>
                <p style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
                  Можно добавить комментарий или фото ниже, затем вернуть заявку.
                </p>
              </>
            ) : (
              <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -4, marginBottom: 10 }}>
                Менеджер запчастей просит уточнение. Можно добавить комментарий или фото ниже, затем вернуть заявку. Других действий нет.
              </p>
            )}
            <button className="btn btn-primary btn-sm" disabled={returnClarLoading} style={{ width: "100%", justifyContent: "center" }}
              onClick={async () => {
                setReturnClarLoading(true);
                try {
                  await onReturnFromClarification(ticket.id);
                  onClose();
                } catch (e) {
                  setReturnClarLoading(false);
                  alert(e.response?.data?.error || "Не удалось вернуть заявку");
                }
              }}>
              {returnClarLoading ? "Отправка..." : "📦 Вернуть в «Ожидаем запчасти»"}
            </button>
          </div>
        )}

        {canMechanicComment && (
          <div className="action-panel">
            <div className="action-panel-label">Дополнительный комментарий и фото</div>
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -4, marginBottom: 10 }}>
              Заявка сейчас в статусе «{STATUS_LABELS_ALL[ticket.status] || ticket.status}» — если нужно что-то уточнить, дополнить текстом или фото, администраторы и менеджер запчастей это увидят.
            </p>
            {extraPhotos.length > 0 && (
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 8 }}>
                Уже прикреплено доп. фото: {extraPhotos.length} шт. (видно выше, над текстом заявки)
              </div>
            )}
            <div className="upload-zone" style={{ marginBottom: 12 }} onClick={() => mechPhotoRef.current?.click()}>
              <input ref={mechPhotoRef} type="file" accept="image/*" multiple
                onChange={e => { uploadMechanicExtraPhotos(e.target.files); e.target.value = ""; }} />
              {mechPhotoUploading ? "Загрузка..." : "📎 Добавить доп. фото"}
            </div>
            {mechPhotoQueue.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {mechPhotoQueue.map((f, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--muted)" }}>
                      <span style={{ width: 28, textAlign: "center" }}>{uploadLabel(f.status, f.percent)}</span>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)", marginTop: 4 }}>
                  Загружено {mechPhotoQueue.filter(f => f.status === "done").length} из {mechPhotoQueue.length}
                </div>
              </div>
            )}
            <div className="form-group">
              <textarea className="form-textarea" style={{ minHeight: 52, borderColor: mechExtraError ? "var(--red)" : undefined }}
                value={mechExtraComment}
                onChange={e => { setMechExtraComment(e.target.value); setMechExtraError(""); setMechExtraSent(false); }}
                placeholder="Например: забыл указать, что..." />
              {mechExtraError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {mechExtraError}</div>}
              {mechExtraSent && <div style={{ color: "var(--blue)", fontSize: 12, marginTop: 5 }}>✓ Комментарий отправлен</div>}
            </div>
            <button className="btn btn-primary btn-sm" disabled={mechExtraLoading} onClick={sendMechanicComment}>
              {mechExtraLoading ? "Отправка..." : "Отправить комментарий"}
            </button>
          </div>
        )}

        {isSuperadmin && (
          <div className="action-panel" style={{ borderColor: "var(--red)" }}>
            <div className="action-panel-label" style={{ color: "var(--red)" }}>Удаление заявки (суперадминистратор)</div>
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -4, marginBottom: 10 }}>
              Заявка исчезнет из всех рабочих списков, но останется в «Журнале удалённых» в отчётах — ничего не теряется безвозвратно.
            </p>
            <div className="form-group">
              <label className="form-label">Причина удаления</label>
              <textarea className="form-textarea" style={{ minHeight: 60, borderColor: delError ? "var(--red)" : undefined }}
                value={delReason}
                onChange={e => { setDelReason(e.target.value); setDelError(""); setDelConfirm(false); }}
                placeholder="Например: дублирующая заявка, создана по ошибке..." />
              {delError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {delError}</div>}
            </div>
            <button className="btn btn-sm" disabled={delLoading}
              style={{ background: delConfirm ? "var(--red)" : "transparent", color: delConfirm ? "#fff" : "var(--red)", border: "1.5px solid var(--red)" }}
              onClick={doSoftDelete}>
              {delLoading ? "Удаление..." : delConfirm ? "⚠ Нажмите ещё раз для подтверждения" : "🗑 Удалить заявку"}
            </button>
          </div>
        )}

        <hr className="divider" />
        <div className="sec-label">История</div>
        <div className="history">
          {(ticket.history || []).filter(h => !ACTION_COMMENT_TYPES.has(h.type)).slice().reverse().map((h, i) => (
            <div key={i} className="h-item">
              <div className="h-dot" style={{ background: H_COLORS[h.type] || "#9ca3af" }} />
              <div className="h-text">{h.text}</div>
              <div className="h-time">{fmt(h.at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Reports Page ──────────────────────────────────────────────────────────────
const STATUS_LABELS_ALL = {
  unassigned:          "Нераспределена",
  in_work:             "В работе",
  awaiting_parts:      "Ожидаем запчасти",
  parts_clarification: "Уточнение запчастей",
  parts_ordered:       "Запчасти заказаны",
  approval:            "Согласование",
  approval_sent:       "Отправлено на согласование",
  approval_approved:   "Согласовано",
  closed:              "Закрыта",
  service_center:      "Сервис центр",
};

const CASH_ACTION_LABELS = {
  close: "Закрытие", parts: "Ожидаем запчасти", service_center: "Сервис центр",
};

function monthLabel(year, month) {
  const names = ["", "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
  return `${names[month] || month} ${year}`;
}
function nextMonthLabel(year, month) {
  return Number(month) === 12 ? monthLabel(Number(year) + 1, 1) : monthLabel(Number(year), Number(month) + 1);
}
function fmtActDate(d) {
  const s = String(d || "").slice(0, 10);
  const [y, m, day] = s.split("-");
  return y && m && day ? `${day}.${m}.${y}` : s;
}
function pad2(n) { return String(n).padStart(2, "0"); }
function parseDayInput(text, year, month) {
  const s = String(text || "").trim();
  if (!s) return { empty: true, ok: false };
  let dd, mm, yy;
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const dmy3 = s.match(/^(\d{1,2})[.\/](\d{1,2})[.\/](\d{2,4})$/);
  const dmy2 = s.match(/^(\d{1,2})[.\/](\d{1,2})$/);
  const dayOnly = s.match(/^(\d{1,2})$/);
  if (iso) { yy = +iso[1]; mm = +iso[2]; dd = +iso[3]; }
  else if (dmy3) { dd = +dmy3[1]; mm = +dmy3[2]; yy = +dmy3[3]; if (yy < 100) yy += 2000; }
  else if (dmy2) { dd = +dmy2[1]; mm = +dmy2[2]; yy = Number(year) || new Date().getFullYear(); }
  else if (dayOnly) {
    if (!year || !month) return { ok: false, error: "Сначала откройте период" };
    dd = +dayOnly[1]; mm = Number(month); yy = Number(year);
  }
  else return { ok: false, error: "Число дня, 05.10 или 05.10.2026" };
  if (!yy || mm < 1 || mm > 12 || dd < 1) return { ok: false, error: "Нет такой даты" };
  const dim = new Date(yy, mm, 0).getDate();
  if (dd > dim) return { ok: false, error: `В этом месяце нет ${dd}-го` };
  const out = `${yy}-${pad2(mm)}-${pad2(dd)}`;
  const off = year && month ? (mm !== Number(month) || yy !== Number(year)) : false;
  return { ok: true, iso: out, off };
}
function money(n) {
  return Number(n || 0).toLocaleString("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " ₽";
}
function fmtIssuedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
function DriftBox({ drift }) {
  if (!drift) return null;
  const actLine = (a) => `${fmtActDate(a.act_date)} · ${a.object_name || "акт"} · услуги ${money(a.amount_services)}${a.is_cash ? " · нал" : ""}`;
  return (
    <div className="pay-drift-box">
      <b>После выдачи{drift.issued_at ? ` · ${fmtIssuedAt(drift.issued_at)}` : ""}</b>
      {drift.added?.map(a => <div key={"a"+a.id}>+ {actLine(a)}</div>)}
      {drift.removed?.map(a => <div key={"r"+a.id}>− {actLine(a)}</div>)}
      {drift.changed_acts?.map(c => (
        <div key={"c"+c.after.id}>~ {fmtActDate(c.after.act_date)} · {c.after.object_name || "акт"} · услуги {money(c.before.amount_services)} → {money(c.after.amount_services)}</div>
      ))}
      {drift.paid.work_share !== drift.now.work_share && <div>% {money(drift.paid.work_share)} → {money(drift.now.work_share)}</div>}
      {drift.paid.cash_on_hand !== drift.now.cash_on_hand && <div>нал {money(drift.paid.cash_on_hand)} → {money(drift.now.cash_on_hand)}</div>}
      {drift.paid.total_salary !== drift.now.total_salary && <div>Итого ЗП {money(drift.paid.total_salary)} → {money(drift.now.total_salary)}</div>}
      {drift.paid.total_payout !== drift.now.total_payout && <div>На руки {money(drift.paid.total_payout)} → {money(drift.now.total_payout)}</div>}
      <div style={{ marginTop: 4, opacity: 0.85 }}>В таблице — выданные суммы. Чтобы пересчитать, снимите «Выдано».</div>
    </div>
  );
}

function ActsPage({ mechanics, mode }) {
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(null);
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [unlockClosed, setUnlockClosed] = useState(false);
  const [unlockAsk, setUnlockAsk] = useState(false);
  const [newPeriodAsk, setNewPeriodAsk] = useState(false);
  const [groupBy, setGroupBy] = useState("mechanic");
  const [filterMech, setFilterMech] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterQ, setFilterQ] = useState("");
  const [issuedMechs, setIssuedMechs] = useState([]);
  const ticketRef = useRef();
  const today = new Date().toISOString().slice(0, 10);
  const emptyForm = () => ({
    mechanic_id: mechanics[0]?.id || "",
    ticket_id: "",
    is_cash: false,
    act_date: "",
    invoice_1c: "",
    object_name: "",
    amount_total: "",
    amount_parts: "",
  });
  const [form, setForm] = useState(emptyForm);
  const [ticketHint, setTicketHint] = useState(null);
  const [dateText, setDateText] = useState("");
  const [invoiceHint, setInvoiceHint] = useState(null);
  const [dupAsk, setDupAsk] = useState(false);

  useEffect(() => {
    setForm(f => (!f.mechanic_id && mechanics[0] ? { ...f, mechanic_id: mechanics[0].id } : f));
  }, [mechanics]);

  const isArchive = mode === "archive";
  const periodClosed = period?.status === "closed";
  const canWrite = period && (!periodClosed || unlockClosed);

  const loadPeriods = async () => {
    const { data } = await api.get("/api/act-periods");
    setPeriods(data || []);
    return data || [];
  };

  const loadPeriod = async (id) => {
    if (!id) { setPeriod(null); setActs([]); setIssuedMechs([]); return; }
    const { data } = await api.get(`/api/act-periods/${id}/acts`);
    setPeriod(data.period);
    setActs(data.acts || []);
    setIssuedMechs(data.issued_mechanics || []);
  };

  useEffect(() => {
    if (!period) return;
    const now = new Date();
    const inPeriod = now.getFullYear() === Number(period.year) && (now.getMonth() + 1) === Number(period.month);
    setDateText(inPeriod ? String(now.getDate()) : "");
  }, [period?.id]);

  useEffect(() => {
    (async () => {
      setLoading(true); setError(""); setUnlockClosed(false); setUnlockAsk(false);
      try {
        const list = await loadPeriods();
        if (isArchive) {
          const closed = list.filter(p => p.status === "closed");
          await loadPeriod(closed[0]?.id);
        } else {
          const open = list.find(p => p.status === "open") || list[0];
          await loadPeriod(open?.id);
        }
      } catch (e) {
        setError(e.response?.data?.error || "Не удалось загрузить периоды");
      } finally { setLoading(false); }
    })();
  }, [mode]);

  useEffect(() => {
    const id = String(form.ticket_id || "").trim();
    if (!id) { setTicketHint(null); return; }
    if (!/^\d+$/.test(id)) { setTicketHint({ ok: false, text: "Только номер заявки" }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/api/acts/ticket/${id}`);
        setTicketHint({ ok: true, text: `#${data.id} · ${(data.text || "").slice(0, 80)}${(data.text || "").length > 80 ? "…" : ""}` + (data.assigned_name ? ` · ${data.assigned_name}` : "") });
        if (data.assigned_to) {
          setForm(prev => String(prev.ticket_id) === String(id) ? { ...prev, mechanic_id: data.assigned_to } : prev);
        }
      } catch {
        setTicketHint({ ok: false, text: "Заявка не найдена" });
      }
    }, 280);
    return () => clearTimeout(t);
  }, [form.ticket_id]);

  useEffect(() => { setDupAsk(false); }, [form.invoice_1c, form.is_cash]);

  useEffect(() => {
    if (form.is_cash) { setInvoiceHint(null); return; }
    const inv = String(form.invoice_1c || "").trim();
    if (!inv) { setInvoiceHint(null); return; }
    const t = setTimeout(async () => {
      const local = acts.filter(a => !a.is_cash && String(a.invoice_1c || "").trim().toLowerCase() === inv.toLowerCase() && a.id !== editingId);
      if (local.length) {
        const a = local[0];
        setInvoiceHint({ dup: true, text: `счёт ${inv} уже есть — ${a.mechanic_name || "механик"}, ${fmtActDate(a.act_date)}, ${money(a.amount_total)}` });
        return;
      }
      try {
        const { data } = await api.get("/api/acts/invoice", { params: { q: inv, exclude: editingId || "" } });
        const list = (data.acts || []).filter(a => a.id !== editingId);
        if (list.length) {
          const a = list[0];
          const per = a.period_label ? ` (${a.period_label})` : "";
          setInvoiceHint({ dup: true, text: `счёт ${inv} уже есть${per} — ${a.mechanic_name || "механик"}, ${fmtActDate(a.act_date)}, ${money(a.amount_total)}` });
        } else setInvoiceHint(null);
      } catch {
        setInvoiceHint(null);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [form.invoice_1c, form.is_cash, acts, editingId]);

  const services = () => {
    const t = Number(form.amount_total);
    const p = Number(form.amount_parts || 0);
    if (form.amount_total === "" || !Number.isFinite(t) || !Number.isFinite(p)) return null;
    return Math.round((t - p) * 100) / 100;
  };
  const objectNames = [...new Set(acts.map(a => a.object_name).filter(Boolean))];
  const dateParsed = parseDayInput(dateText, period?.year, period?.month);
  const ready = !!(
    form.mechanic_id && dateParsed.ok
    && String(form.object_name).trim()
    && form.amount_total !== "" && Number.isFinite(Number(form.amount_total))
    && Number(form.amount_parts || 0) >= 0
    && (form.is_cash || String(form.invoice_1c).trim())
    && (!form.ticket_id || !(ticketHint && !ticketHint.ok))
    && (services() == null || services() >= 0)
  );

  const payload = () => ({
    mechanic_id: form.mechanic_id,
    ticket_id: form.ticket_id || null,
    is_cash: !!form.is_cash,
    act_date: dateParsed.iso,
    invoice_1c: form.is_cash ? "" : form.invoice_1c,
    object_name: form.object_name,
    amount_total: Number(form.amount_total),
    amount_parts: Number(form.amount_parts || 0),
    confirmClosed: unlockClosed,
    confirmDuplicate: !!(invoiceHint && invoiceHint.dup),
  });

  const submit = async () => {
    if (!period || !dateParsed.ok) return;
    if (invoiceHint?.dup && !dupAsk) { setDupAsk(true); return; }
    setSaving(true); setError("");
    try {
      if (editingId) {
        const { data } = await api.patch(`/api/acts/${editingId}`, payload());
        setActs(prev => prev.map(a => a.id === data.id ? data : a));
        setEditingId(null);
      } else {
        const { data } = await api.post(`/api/act-periods/${period.id}/acts`, payload());
        setActs(prev => [data, ...prev]);
      }
      const keepMech = form.mechanic_id;
      setForm({ ...emptyForm(), mechanic_id: keepMech });
      setTicketHint(null);
      setInvoiceHint(null);
      setDupAsk(false);
      setTimeout(() => ticketRef.current?.focus(), 50);
    } catch (e) {
      if (e.response?.data?.code === "closed_period") setUnlockAsk(true);
      if (e.response?.data?.code === "duplicate_invoice") {
        const a = (e.response.data.matches || [])[0];
        setInvoiceHint({
          dup: true,
          text: a ? `счёт уже есть — ${a.mechanic_name || "механик"}, ${fmtActDate(a.act_date)}, ${money(a.amount_total)}` : "Такой счёт 1С уже есть",
        });
        setDupAsk(true);
      }
      setError(e.response?.data?.error || "Не удалось сохранить акт");
    } finally { setSaving(false); }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setDupAsk(false);
    setDateText(fmtActDate(a.act_date));
    setForm({
      mechanic_id: a.mechanic_id,
      ticket_id: a.ticket_id ? String(a.ticket_id) : "",
      is_cash: !!a.is_cash,
      act_date: String(a.act_date).slice(0, 10),
      invoice_1c: a.invoice_1c || "",
      object_name: a.object_name,
      amount_total: String(a.amount_total),
      amount_parts: String(a.amount_parts || 0),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeAct = async (a) => {
    if (!window.confirm(`Удалить акт${a.invoice_1c ? ` по счёту ${a.invoice_1c}` : a.object_name ? ` «${a.object_name}»` : ""}?`)) return;
    try {
      await api.delete(`/api/acts/${a.id}`, { data: { confirmClosed: unlockClosed } });
      setActs(prev => prev.filter(x => x.id !== a.id));
      if (editingId === a.id) { setEditingId(null); setForm(emptyForm()); }
    } catch (e) {
      if (e.response?.data?.code === "closed_period") setUnlockAsk(true);
      setError(e.response?.data?.error || "Не удалось удалить");
    }
  };

  const startNextPeriod = async () => {
    setSaving(true); setError("");
    try {
      await api.post("/api/act-periods");
      setNewPeriodAsk(false);
      const list = await loadPeriods();
      const open = list.find(p => p.status === "open");
      await loadPeriod(open?.id);
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось открыть период");
    } finally { setSaving(false); }
  };

  const filtered = acts.filter(a => {
    if (filterMech !== "all" && String(a.mechanic_id) !== filterMech) return false;
    const d = String(a.act_date).slice(0, 10);
    if (filterFrom && d < filterFrom) return false;
    if (filterTo && d > filterTo) return false;
    const q = filterQ.trim().toLowerCase();
    if (q) {
      const hay = `${a.ticket_id} ${a.invoice_1c} ${a.object_name} ${a.mechanic_name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const groups = {};
  filtered.forEach(a => {
    const key = groupBy === "day" ? String(a.act_date).slice(0, 10)
      : groupBy === "object" ? (a.object_name || "—")
      : (a.mechanic_name || "—");
    if (!groups[key]) groups[key] = { key, total: 0, parts: 0, services: 0, count: 0 };
    groups[key].total += Number(a.amount_total) || 0;
    groups[key].parts += Number(a.amount_parts) || 0;
    groups[key].services += Number(a.amount_services) || 0;
    groups[key].count += 1;
  });
  const groupRows = Object.values(groups).sort((a, b) => b.total - a.total);
  const grand = groupRows.reduce((s, g) => ({
    total: s.total + g.total, parts: s.parts + g.parts, services: s.services + g.services, count: s.count + g.count,
  }), { total: 0, parts: 0, services: 0, count: 0 });

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const archiveList = periods.filter(p => p.status === "closed");

  if (loading) return <div className="content"><div className="empty"><div className="empty-icon">⏳</div><div className="empty-text">Загрузка...</div></div></div>;

  if (!period && !isArchive) {
    return (
      <div className="content" style={{ maxWidth: 720 }}>
        <div className="page-hdr"><div className="page-hdr-title">Акты выполненных работ</div></div>
        <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16, lineHeight: 1.5 }}>
          Регистр начинается с сентября 2026 и идёт по календарю. Откройте первый период, чтобы заносить счета.
        </p>
        {error && <div className="form-error">{error}</div>}
        <button className="btn btn-primary" disabled={saving} onClick={startNextPeriod}>
          {saving ? "Создание..." : "Начать период «Сентябрь 2026»"}
        </button>
      </div>
    );
  }

  if (isArchive && !archiveList.length) {
    return (
      <div className="content">
        <div className="page-hdr"><div className="page-hdr-title">Архив периодов</div></div>
        <div className="empty"><div className="empty-icon">📭</div><div className="empty-text">Закрытых периодов пока нет. Они появятся, когда начнёте следующий месяц.</div></div>
      </div>
    );
  }

  return (
    <div className="content" style={{ maxWidth: 1180 }}>
      <div className="page-hdr">
        <div className="page-hdr-title">
          {isArchive ? "Архив" : period?.label || "Текущий период"}
          {periodClosed && <span className="badge" style={{ marginLeft: 8, background: "var(--border)", color: "var(--muted)" }}>закрыт</span>}
          {period && !periodClosed && <span className="badge" style={{ marginLeft: 8, background: "#dcfce7", color: "#16a34a" }}>текущий</span>}
        </div>
        {!isArchive && period && (
          newPeriodAsk ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", maxWidth: 420 }}>
              <span style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.4 }}>
                Закрыть «{period.label}» и открыть «{nextMonthLabel(period.year, period.month)}»? Текущий уйдёт в архив.
              </span>
              <button className="btn btn-red btn-sm" disabled={saving} onClick={startNextPeriod}>Да, открыть {nextMonthLabel(period.year, period.month)}</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNewPeriodAsk(false)}>Отмена</button>
            </div>
          ) : (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setNewPeriodAsk(true)}>+ Новый период</button>
          )
        )}
      </div>

      {isArchive && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {archiveList.map(p => (
            <button key={p.id} type="button"
              className={`btn btn-sm ${period?.id === p.id ? "btn-primary" : "btn-outline"}`}
              onClick={() => { setUnlockClosed(false); setUnlockAsk(false); loadPeriod(p.id); }}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {periodClosed && !unlockClosed && (
        <div style={{ background: "#fff7ed", border: "1.5px solid #fed7aa", borderRadius: 10, padding: "10px 12px", marginBottom: 12, fontSize: 13, color: "#9a3412" }}>
          Период «{period.label}» закрыт. Просмотр доступен. Чтобы править акты — подтвердите.
          <div style={{ marginTop: 8 }}>
            {unlockAsk ? (
              <button type="button" className="btn btn-red btn-sm" onClick={() => { setUnlockClosed(true); setUnlockAsk(false); }}>
                Да, внести изменения в уже закрытый период
              </button>
            ) : (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setUnlockAsk(true)}>Изменить закрытый период</button>
            )}
          </div>
        </div>
      )}

      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      {canWrite && (
        <form className="acts-entry" onSubmit={e => { e.preventDefault(); if (!saving && ready) submit(); }} autoComplete="off">
          <div className="action-panel-label">{editingId ? `Редактирование акта #${editingId}` : "Новый акт"}</div>
          <div className="form-group acts-mech">
            <label className="form-label">Механик</label>
            <select className="form-select" value={form.mechanic_id} onChange={e => setF("mechanic_id", e.target.value)}>
              {mechanics.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className={`acts-line ${form.is_cash ? "cash" : ""}`}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Заявка</label>
              <div className="acts-ticket-row">
                <input ref={ticketRef} className="form-input" inputMode="numeric" value={form.ticket_id}
                  onChange={e => setF("ticket_id", e.target.value.replace(/[^\d]/g, ""))} placeholder="№" />
                <button type="button" className={`acts-nal ${form.is_cash ? "on" : ""}`}
                  title="Наличка — номер 1С не нужен"
                  onClick={() => setF("is_cash", !form.is_cash)}>
                  <span className="acts-nal-track"><span className="acts-nal-knob" /></span>
                  Нал
                </button>
              </div>
              {ticketHint && <div style={{ fontSize: 11, marginTop: 3, color: ticketHint.ok ? "#15803d" : "var(--red)" }}>{ticketHint.text}</div>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Дата</label>
              <input className="form-input" value={dateText}
                onChange={e => setDateText(e.target.value)} placeholder="5"
                title="Число дня — месяц и год периода. Полную дату можно вбить вручную." />
              {dateText && dateParsed.error && <div className="acts-hint bad">{dateParsed.error}</div>}
              {dateParsed.ok && dateParsed.off && (
                <div className="acts-hint dup">Не {period?.label}: будет {fmtActDate(dateParsed.iso)}. Можно оставить.</div>
              )}
            </div>
            <div className="form-group acts-obj" style={{ margin: 0 }}>
              <label className="form-label">Объект</label>
              <input className="form-input" list="acts-objects" value={form.object_name} onChange={e => setF("object_name", e.target.value)}
                placeholder="название объекта" />
              <datalist id="acts-objects">
                {objectNames.map(o => <option key={o} value={o} />)}
              </datalist>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Счёт, ₽</label>
              <input className="form-input" inputMode="decimal" value={form.amount_total}
                onChange={e => setF("amount_total", e.target.value.replace(",", "."))} placeholder="0" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Запчасти, ₽</label>
              <input className="form-input" inputMode="decimal" value={form.amount_parts}
                onChange={e => setF("amount_parts", e.target.value.replace(",", "."))} placeholder="0" />
            </div>
            {!form.is_cash && (
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Счёт 1С</label>
                <input className="form-input" value={form.invoice_1c} onChange={e => setF("invoice_1c", e.target.value)}
                  placeholder="номер 1С" />
                {invoiceHint?.dup && <div className="acts-hint dup">{invoiceHint.text}</div>}
              </div>
            )}
          </div>
          {issuedMechs.some(id => String(id) === String(form.mechanic_id)) && (
            <div className="acts-issued-warn">Зарплата этому механику уже выдана — в ведомости появится расхождение</div>
          )}
          {form.amount_total !== "" && Number.isFinite(Number(form.amount_total)) && (
            <div className={"acts-svc" + (services() != null && services() < 0 ? " bad" : "")}>
              {services() != null && services() < 0
                ? "Запчасти больше суммы счёта"
                : `Услуги ${money(services())}`}
            </div>
          )}
          <div className="form-actions" style={{ marginTop: 12 }}>
            {editingId && <button type="button" className="btn btn-ghost" onClick={() => {
              setEditingId(null); setForm(emptyForm()); setDupAsk(false);
              const now = new Date();
              const inPeriod = period && now.getFullYear() === Number(period.year) && (now.getMonth() + 1) === Number(period.month);
              setDateText(inPeriod ? String(now.getDate()) : "");
            }}>Отмена</button>}
            <button type="submit" className="btn btn-primary" disabled={saving || !ready}>
              {saving ? "Сохранение..." : dupAsk ? "Счёт уже есть — всё равно" : editingId ? "Сохранить изменения" : "Добавить акт"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>Enter — сохранить. Механик и дата остаются для следующего акта.</div>
        </form>
      )}

      <div style={{ overflowX: "auto", marginBottom: 16 }}>
        <input className="form-input" style={{ marginBottom: 10 }} value={filterQ}
          onChange={e => setFilterQ(e.target.value)} placeholder="Поиск по счёту, заявке, объекту, механику" />
        {filtered.length === 0 ? (
          <div className="empty" style={{ padding: 28 }}><div className="empty-text">Актов в этом периоде нет</div></div>
        ) : (
          <table className="acts-table">
            <thead>
              <tr>
                <th>Дата</th><th>Механик</th><th>Заявка</th><th>Счёт 1С</th><th>Объект</th>
                <th>Счёт</th><th>Запчасти</th><th>Услуги</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtActDate(a.act_date)}</td>
                  <td>{a.mechanic_name}</td>
                  <td>{a.ticket_id ? `#${a.ticket_id}` : "—"}</td>
                  <td>{a.is_cash ? <span className="acts-nal-tag">нал</span> : (a.invoice_1c || "—")}</td>
                  <td>{a.object_name}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{money(a.amount_total)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{money(a.amount_parts)}</td>
                  <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{money(a.amount_services)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {canWrite && (
                      <>
                        <button className="btn-icon" title="Изменить" onClick={() => startEdit(a)}>✏️</button>
                        <button className="btn-icon danger" title="Удалить" onClick={() => removeAct(a)}>🗑</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="acts-sum">
        <div className="sec-label" style={{ marginBottom: 10 }}>Итоги периода</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <select className="filter-select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="mechanic">По механикам</option>
            <option value="day">По дням</option>
            <option value="object">По объектам</option>
          </select>
          <select className="filter-select" value={filterMech} onChange={e => setFilterMech(e.target.value)}>
            <option value="all">Все механики</option>
            {mechanics.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
          </select>
          <input type="date" className="filter-select" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} title="С даты" />
          <input type="date" className="filter-select" value={filterTo} onChange={e => setFilterTo(e.target.value)} title="По дату" />
          <input className="filter-select" style={{ minWidth: 160 }} value={filterQ}
            onChange={e => setFilterQ(e.target.value)} placeholder="поиск: счёт, заявка, объект" />
          {(filterMech !== "all" || filterFrom || filterTo || filterQ) && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFilterMech("all"); setFilterFrom(""); setFilterTo(""); setFilterQ(""); }}>Сбросить</button>
          )}
        </div>
        <table className="acts-table">
          <thead>
            <tr>
              <th>{groupBy === "day" ? "Дата" : groupBy === "object" ? "Объект" : "Механик"}</th>
              <th>Актов</th><th>Счета</th><th>Запчасти</th><th>Услуги</th>
            </tr>
          </thead>
          <tbody>
            {groupRows.map(g => (
              <tr key={g.key}>
                <td>{groupBy === "day" ? fmtActDate(g.key) : g.key}</td>
                <td>{g.count}</td>
                <td>{money(g.total)}</td>
                <td>{money(g.parts)}</td>
                <td style={{ fontWeight: 600 }}>{money(g.services)}</td>
              </tr>
            ))}
            <tr style={{ background: "var(--bg)" }}>
              <td style={{ fontWeight: 700 }}>Итого</td>
              <td style={{ fontWeight: 700 }}>{grand.count}</td>
              <td style={{ fontWeight: 700 }}>{money(grand.total)}</td>
              <td style={{ fontWeight: 700 }}>{money(grand.parts)}</td>
              <td style={{ fontWeight: 700, color: "#15803d" }}>{money(grand.services)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayNum({ value, onCommit, className, by, locked }) {
  const show = (x) => (x == null || x === "" || Number(x) === 0) ? "" : String(x);
  const [v, setV] = useState(show(value));
  useEffect(() => { setV(show(value)); }, [value]);
  const commit = () => {
    if (locked) return;
    const n = v === "" ? 0 : Number(String(v).replace(",", "."));
    if (!Number.isFinite(n)) { setV(show(value)); return; }
    if (n !== Number(value || 0)) onCommit(n);
  };
  return (
    <div>
      <input className={"pay-input" + (className ? " " + className : "")} inputMode="decimal" value={v}
        disabled={!!locked}
        onChange={e => setV(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
        onFocus={e => e.target.select()} />
      {by === "superadmin" ? <span className="pay-who" title="Супер Админ">👑</span> : null}
      {by === "payroll" ? <span className="pay-who" title="Зарплата">🌹</span> : null}
    </div>
  );
}

let savedPayPeriodId = null;

function PayrollShell({ mechanics, user, start }) {
  const isSuper = user?.role === "superadmin";
  const [section, setSection] = useState(start === "members" ? "members" : "sheet");
  const [actsMode, setActsMode] = useState("current");
  if (!isSuper) {
    return <PayrollPage mechanics={mechanics} user={user} mode={start === "members" ? "members" : "sheet"} />;
  }
  return (
    <>
      <div className="pay-subnav">
        <button type="button" className={`btn btn-sm ${section === "sheet" ? "btn-primary" : "btn-outline"}`} onClick={() => setSection("sheet")}>Ведомость</button>
        <button type="button" className={`btn btn-sm ${section === "members" ? "btn-primary" : "btn-outline"}`} onClick={() => setSection("members")}>Участники</button>
        <button type="button" className={`btn btn-sm ${section === "acts" ? "btn-primary" : "btn-outline"}`} onClick={() => setSection("acts")}>Акты</button>
        {section === "acts" && (
          <>
            <span style={{ width: 1, alignSelf: "stretch", background: "var(--border)", margin: "0 4px" }} />
            <button type="button" className={`btn btn-sm ${actsMode === "current" ? "btn-primary" : "btn-outline"}`} onClick={() => setActsMode("current")}>Текущий</button>
            <button type="button" className={`btn btn-sm ${actsMode === "archive" ? "btn-primary" : "btn-outline"}`} onClick={() => setActsMode("archive")}>Архив</button>
          </>
        )}
      </div>
      {section === "acts"
        ? <ActsPage mechanics={mechanics} mode={actsMode} />
        : <PayrollPage mechanics={mechanics} user={user} mode={section === "members" ? "members" : "sheet"} />}
    </>
  );
}

function PayrollPage({ mechanics, mode, user }) {
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [askHide, setAskHide] = useState(null);
  const [askIssue, setAskIssue] = useState(null);
  const [openDrift, setOpenDrift] = useState(null);
  const [members, setMembers] = useState([]);
  const [memForm, setMemForm] = useState({ name: "", mechanic_id: "", pay_type: "salary", rate: "", work_percent: "", staff_group: "office" });
  const [editMem, setEditMem] = useState(null);

  const isMembers = mode === "members";
  const isSuper = user?.role === "superadmin";

  const loadPeriods = async () => {
    const { data } = await api.get("/api/payroll/periods");
    setPeriods(data || []);
    return data || [];
  };
  const loadSheet = async (id) => {
    if (!id) { setPeriod(null); setRows([]); return; }
    savedPayPeriodId = id;
    const { data } = await api.get(`/api/payroll/periods/${id}`);
    setPeriod(data.period);
    setRows(data.rows || []);
  };
  const loadMembers = async () => {
    const { data } = await api.get("/api/payroll/members");
    setMembers(data || []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true); setError(""); setAskHide(null); setAskIssue(null); setOpenDrift(null);
      try {
        if (isMembers) { await loadMembers(); setLoading(false); return; }
        const list = await loadPeriods();
        const prefer = savedPayPeriodId && list.find(p => p.id === savedPayPeriodId);
        const pick = prefer || list.find(p => p.status === "open") || list[0];
        await loadSheet(pick?.id);
      } catch (e) {
        setError(e.response?.data?.error || "Не удалось загрузить зарплату");
      } finally { setLoading(false); }
    })();
  }, [mode]);

  const patchRow = async (id, body) => {
    try {
      const { data } = await api.patch(`/api/payroll/rows/${id}`, body);
      setRows(prev => prev.map(r => r.id === data.id ? data : r));
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось сохранить");
    }
  };

  const hideRow = async (row) => {
    if (row.issued) return;
    if (askHide !== row.id) { setAskHide(row.id); setAskIssue(null); return; }
    try {
      await api.post(`/api/payroll/rows/${row.id}/hide`, { confirm: true });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, hidden: true } : r));
      setAskHide(null);
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось убрать");
    }
  };
  const restoreRow = async (row) => {
    try {
      await api.post(`/api/payroll/rows/${row.id}/restore`);
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, hidden: false } : r));
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось вернуть");
    }
  };
  const issueRow = async (row, want) => {
    if (!isSuper) return;
    if (!askIssue || askIssue.id !== row.id || askIssue.want !== want) {
      setAskIssue({ id: row.id, want });
      setAskHide(null);
      return;
    }
    try {
      const { data } = await api.post(`/api/payroll/rows/${row.id}/issue`, { confirm: true, issued: want });
      setRows(prev => prev.map(r => r.id === data.id ? data : r));
      setAskIssue(null);
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось изменить выдачу");
    }
  };

  const saveMember = async () => {
    const f = editMem || memForm;
    if (!String(f.name || "").trim()) { setError("Укажите ФИО"); return; }
    try {
      setError("");
      const payload = {
        name: String(f.name).trim(),
        mechanic_id: f.mechanic_id || null,
        pay_type: f.pay_type || "salary",
        staff_group: f.staff_group === "service" ? "service" : "office",
        rate: Number(String(f.rate).replace(",", ".")) || 0,
        work_percent: Number(String(f.work_percent).replace(",", ".")) || 0,
      };
      if (editMem) {
        await api.patch(`/api/payroll/members/${editMem.id}`, payload);
        setEditMem(null);
      } else {
        await api.post("/api/payroll/members", payload);
        setMemForm({ name: "", mechanic_id: "", pay_type: "salary", rate: "", work_percent: "", staff_group: "office" });
      }
      await loadMembers();
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось сохранить участника");
    }
  };
  const deleteMember = async (m) => {
    if (!window.confirm(`Удалить участника «${m.name}» из справочника? Строки в периодах останутся.`)) return;
    await api.delete(`/api/payroll/members/${m.id}`);
    await loadMembers();
  };

  if (loading) return <div className="content"><div className="empty"><div className="empty-icon">⏳</div><div className="empty-text">Загрузка...</div></div></div>;

  if (isMembers) {
    const form = editMem || memForm;
    const setForm = (k, v) => {
      if (editMem) setEditMem({ ...editMem, [k]: v });
      else setMemForm({ ...memForm, [k]: v });
    };
    const takenIds = new Set(members.filter(m => m.mechanic_id && (!editMem || Number(m.id) !== Number(editMem.id))).map(m => String(m.mechanic_id)));
    return (
      <div className="content" style={{ maxWidth: 820 }}>
        <div className="page-hdr"><div className="page-hdr-title">Участники зарплаты</div></div>
        <p style={{ fontSize: 13, color: "var(--muted)", marginTop: -6, marginBottom: 14, lineHeight: 1.45 }}>
          Здесь все, кто в зарплате, включая механиков. Оклад, смену и % от работ правят карандашом в строке.
          Новый месяц ведомости берёт тех же людей, ставки подтягивает отсюда. Дни, удержания и остальные суммы начинаются пустыми.
        </p>
        {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
        <div className="acts-entry">
          <div className="action-panel-label">{editMem ? "Редактирование" : "Новый участник"}</div>
          <div className="acts-line" style={{ gridTemplateColumns: "1.4fr 1.1fr 0.85fr 0.85fr" }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">ФИО</label>
              <input className="form-input" value={form.name} onChange={e => setForm("name", e.target.value)} placeholder="Иванов Иван" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Механик</label>
              <select className="form-select" value={form.mechanic_id || ""} onChange={e => {
                const v = e.target.value;
                if (editMem) setEditMem({ ...editMem, mechanic_id: v });
                else setMemForm({ ...memForm, mechanic_id: v, staff_group: v ? "service" : "office" });
              }}>
                <option value="">не привязан</option>
                {mechanics.map(m => (
                  <option key={m.id} value={m.id} disabled={takenIds.has(String(m.id))}>
                    {m.name}{takenIds.has(String(m.id)) ? " — уже привязан" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Группа</label>
              <select className="form-select" value={form.staff_group === "service" ? "service" : "office"} onChange={e => setForm("staff_group", e.target.value)}>
                <option value="office">Офис</option>
                <option value="service">Сервис</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Тип</label>
              <select className="form-select" value={form.pay_type} onChange={e => setForm("pay_type", e.target.value)}>
                <option value="salary">Оклад</option>
                <option value="shift">Смена</option>
              </select>
            </div>
          </div>
          <div className="acts-line" style={{ gridTemplateColumns: "1fr 1fr auto", marginTop: 10 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">{form.pay_type === "shift" ? "Ставка смены" : "Оклад"}</label>
              <input className="form-input" inputMode="decimal" value={form.rate}
                onChange={e => setForm("rate", e.target.value)} placeholder="0" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">% от работ</label>
              <input className="form-input" inputMode="decimal" value={form.work_percent}
                onChange={e => setForm("work_percent", e.target.value)} placeholder="0" />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              {editMem && <button type="button" className="btn btn-ghost" onClick={() => setEditMem(null)}>Отмена</button>}
              <button type="button" className="btn btn-primary" onClick={saveMember}>{editMem ? "Сохранить" : "Добавить"}</button>
            </div>
          </div>
        </div>
        {members.length === 0 ? (
          <div className="empty"><div className="empty-text">Пока нет участников</div></div>
        ) : (
          <table className="acts-table">
            <thead>
              <tr><th>ФИО</th><th>Группа</th><th>Механик</th><th>Тип</th><th>Ставка</th><th>% работ</th><th></th></tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>{m.staff_group === "service" ? "Сервис" : "Офис"}</td>
                  <td>{m.mechanic_name || "—"}</td>
                  <td>{m.pay_type === "shift" ? "Смена" : "Оклад"}</td>
                  <td>{money(m.rate)}</td>
                  <td>{m.work_percent}%</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn-icon" title="Изменить" onClick={() => setEditMem({ ...m, rate: String(m.rate), work_percent: String(m.work_percent), mechanic_id: m.mechanic_id || "", staff_group: m.staff_group === "service" ? "service" : "office" })}>✏️</button>
                    <button className="btn-icon danger" title="Удалить" onClick={() => deleteMember(m)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  if (!period) {
    return (
      <div className="content">
        <div className="page-hdr"><div className="page-hdr-title">Зарплата</div></div>
        <div className="empty"><div className="empty-icon">📭</div>
          <div className="empty-text">Месяц появится, когда в Актах откроют период.</div></div>
      </div>
    );
  }

  const visible = rows.filter(r => !r.hidden);
  const hidden = rows.filter(r => r.hidden);
  const groupOf = (r) => r.staff_group === "service" || (!r.staff_group && r.mechanic_id) ? "service" : "office";
  const serviceRows = visible.filter(r => groupOf(r) === "service");
  const officeRows = visible.filter(r => groupOf(r) === "office");
  const sumOf = (list, k) => list.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const totalCells = (list) => (
    <>
      <td></td><td></td>
      <td>{money(sumOf(list, "salary"))}</td>
      <td>{money(sumOf(list, "card_payout"))}</td>
      <td>{money(sumOf(list, "loading_work"))}</td>
      <td>{money(sumOf(list, "work_share"))}</td>
      <td>{money(sumOf(list, "overtime"))}</td>
      <td className="pay-pink">{money(sumOf(list, "deductions"))}</td>
      <td>{money(sumOf(list, "parking"))}</td>
      <td>{money(sumOf(list, "extra_receipts"))}</td>
      <td>{money(sumOf(list, "amortization"))}</td>
      <td>{money(sumOf(list, "mobile"))}</td>
      <td>{money(sumOf(list, "cash_on_hand"))}</td>
      <td>{money(sumOf(list, "total_salary"))}</td>
      <td className="pay-green">{money(sumOf(list, "total_payout"))}</td>
    </>
  );
  const payRow = (r) => (
              <tr key={r.id} className={r.issued ? "pay-held" : ""}>
                <td className="pay-sticky">
                  <div className="pay-name">{r.name}</div>
                  <div className="pay-sub">{r.pay_type === "shift" ? `смена ${money(r.rate)}` : `оклад ${money(r.rate)}`}{r.work_percent ? ` · ${r.work_percent}%` : ""}</div>
                  {isSuper ? (
                    askIssue && askIssue.id === r.id ? (
                      <button type="button" className="pay-issue warn" onClick={() => issueRow(r, askIssue.want)}>
                        {askIssue.want ? "Точно выдать?" : "Точно снять?"}
                      </button>
                    ) : (
                      <button type="button" className={"pay-issue" + (r.issued ? " on" : "")} onClick={() => issueRow(r, !r.issued)}>
                        <span className="acts-nal-track"><span className="acts-nal-knob" /></span>
                        Выдано
                      </button>
                    )
                  ) : r.issued ? (
                    <span className="pay-issued-tag">выдано</span>
                  ) : null}
                  {r.drift ? (
                    <>
                      <button type="button" className="pay-drift" onClick={() => setOpenDrift(openDrift === r.id ? null : r.id)}>
                        акты после выдачи
                      </button>
                      {openDrift === r.id && <DriftBox drift={r.drift} />}
                    </>
                  ) : null}
                  {!r.issued && (askHide === r.id ? (
                    <button type="button" className="pay-hide warn" onClick={() => hideRow(r)}>Точно убрать?</button>
                  ) : (
                    <button type="button" className="pay-hide" onClick={() => hideRow(r)}>убрать</button>
                  ))}
                </td>
                <td><PayNum value={r.work_days} locked={r.issued} by={r.edits?.work_days} onCommit={n => patchRow(r.id, { work_days: n })} /></td>
                <td><PayNum value={r.weekend_days} locked={r.issued} by={r.edits?.weekend_days} onCommit={n => patchRow(r.id, { weekend_days: n })} /></td>
                <td className="pay-ro">{money(r.salary)}</td>
                <td><PayNum value={r.card_payout} locked={r.issued} by={r.edits?.card_payout} onCommit={n => patchRow(r.id, { card_payout: n })} /></td>
                <td><PayNum value={r.loading_work} locked={r.issued} by={r.edits?.loading_work} onCommit={n => patchRow(r.id, { loading_work: n })} /></td>
                <td>
                  <PayNum value={r.work_share} locked={r.issued} by={r.edits?.work_share} onCommit={n => patchRow(r.id, { work_share: n })} />
                  {r.mechanic_id && !r.issued ? (
                    <button type="button" className={"pay-auto" + (r.work_share_manual ? " on" : "")}
                      title={r.work_share_manual ? "Сброс на авто из актов" : `авто: ${money(r.work_share_auto)}`}
                      onClick={() => r.work_share_manual && patchRow(r.id, { work_share_reset: true })}>
                      {r.work_share_manual ? "руч." : "авто"}
                    </button>
                  ) : r.mechanic_id ? <span className="pay-auto">{r.work_share_manual ? "руч." : "авто"}</span> : null}
                </td>
                <td><PayNum value={r.overtime} locked={r.issued} by={r.edits?.overtime} onCommit={n => patchRow(r.id, { overtime: n })} /></td>
                <td className="pay-pink"><PayNum value={r.deductions} locked={r.issued} by={r.edits?.deductions} onCommit={n => patchRow(r.id, { deductions: n })} /></td>
                <td><PayNum value={r.parking} locked={r.issued} by={r.edits?.parking} onCommit={n => patchRow(r.id, { parking: n })} /></td>
                <td><PayNum value={r.extra_receipts} locked={r.issued} by={r.edits?.extra_receipts} onCommit={n => patchRow(r.id, { extra_receipts: n })} /></td>
                <td><PayNum value={r.amortization} locked={r.issued} by={r.edits?.amortization} onCommit={n => patchRow(r.id, { amortization: n })} /></td>
                <td><PayNum value={r.mobile} locked={r.issued} by={r.edits?.mobile} onCommit={n => patchRow(r.id, { mobile: n })} /></td>
                <td>
                  <PayNum value={r.cash_on_hand} locked={r.issued} by={r.edits?.cash_on_hand} onCommit={n => patchRow(r.id, { cash_on_hand: n })} />
                  {r.mechanic_id && !r.issued ? (
                    <button type="button" className={"pay-auto" + (r.cash_manual ? " on" : "")}
                      title={r.cash_manual ? "Сброс на авто из актов (нал)" : `авто: ${money(r.cash_auto)}`}
                      onClick={() => r.cash_manual && patchRow(r.id, { cash_reset: true })}>
                      {r.cash_manual ? "руч." : "авто"}
                    </button>
                  ) : r.mechanic_id ? <span className="pay-auto">{r.cash_manual ? "руч." : "авто"}</span> : null}
                </td>
                <td className="pay-ro">{money(r.total_salary)}</td>
                <td className="pay-ro pay-green">{money(r.total_payout)}</td>
              </tr>
  );

  return (
    <div className="content" style={{ maxWidth: "100%" }}>
      <div className="page-hdr">
        <div className="page-hdr-title">{period?.month_label || period?.label || "Зарплата"}</div>
      </div>
      {periods.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {periods.map(p => (
            <button key={p.id} type="button"
              className={`btn btn-sm ${period?.id === p.id ? "btn-primary" : "btn-outline"}`}
              onClick={() => loadSheet(p.id)}>{p.month_label || p.label}</button>
          ))}
        </div>
      )}
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="pay-groups">
        {[["service", "Сервис", serviceRows], ["office", "Офис", officeRows]].map(([id, title, list]) => (
          <div key={id} className="pay-group">
            <div className="pay-group-title">{title}</div>
            <div className="pay-hero">{money(sumOf(list, "total_payout"))}</div>
            <div className="pay-hero-sub">итого ЗП {money(sumOf(list, "total_salary"))}</div>
          </div>
        ))}
      </div>
      <details className="pay-break">
        <summary>Разбивка</summary>
        <table className="pay-break-table">
          <thead>
            <tr><th></th><th>Сервис</th><th>Офис</th></tr>
          </thead>
          <tbody>
            {[
              ["total_salary", "Итого ЗП", ""],
              ["total_payout", "На руки", "pay-break-main"],
              ["card_payout", "На карты", ""],
              ["cash_on_hand", "Нал на руках", ""],
            ].map(([k, label, cls]) => (
              <tr key={k} className={cls || undefined}>
                <td>{label}</td>
                <td>{money(sumOf(serviceRows, k))}</td>
                <td>{money(sumOf(officeRows, k))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      <div className="pay-wrap">
        <table className="pay-table">
          <thead>
            <tr>
              <th className="pay-sticky">Сотрудник</th>
              <th title="Количество рабочих дней">Дней</th>
              <th title="Работа в выходные">Вых.</th>
              <th>Оклад</th>
              <th>На карту</th>
              <th>Погруз.</th>
              <th>% сотрудн.</th>
              <th>Урочные</th>
              <th className="pay-pink">Поборы</th>
              <th>Парковка</th>
              <th>Чеки</th>
              <th>Аморт.</th>
              <th>Связь</th>
              <th>Нал</th>
              <th>Итого ЗП</th>
              <th className="pay-green">На руки</th>
            </tr>
          </thead>
          <tbody>
            {serviceRows.length > 0 && (
              <>
                <tr className="pay-sec"><td className="pay-sticky">Сервис</td><td colSpan={15}></td></tr>
                {serviceRows.map(payRow)}
                <tr className="pay-total"><td className="pay-sticky">Итого сервис</td>{totalCells(serviceRows)}</tr>
              </>
            )}
            {officeRows.length > 0 && (
              <>
                <tr className="pay-sec"><td className="pay-sticky">Офис</td><td colSpan={15}></td></tr>
                {officeRows.map(payRow)}
                <tr className="pay-total"><td className="pay-sticky">Итого офис</td>{totalCells(officeRows)}</tr>
              </>
            )}
            {visible.length > 0 && serviceRows.length > 0 && officeRows.length > 0 && (
              <tr className="pay-total"><td className="pay-sticky">Итого</td>{totalCells(visible)}</tr>
            )}
          </tbody>
        </table>
      </div>
      {hidden.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div className="sec-label" style={{ marginBottom: 8 }}>Убранные из таблицы ({hidden.length})</div>
          {hidden.map(r => (
            <div key={r.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
              <span style={{ flex: 1 }}>{r.name}</span>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => restoreRow(r)}>Вернуть</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CashPage({ mechanics, user }) {
  const isAdminView = user.role === "admin" || user.role === "superadmin";
  const [mechFilter, setMechFilter] = useState("all");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/cash-payments");
      setPayments(res.data || []);
    } catch (e) {
      // тихо игнорируем — просто останется пусто
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = isAdminView && mechFilter !== "all"
    ? payments.filter(p => String(p.mechanic_id) === mechFilter)
    : payments;

  const groups = {};
  filtered.forEach(p => {
    const d = new Date(p.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    (groups[key] = groups[key] || []).push(p);
  });
  const monthKeys = Object.keys(groups).sort().reverse();
  const monthLabel = (key) => {
    const [y, m] = key.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
  };

  const totalAll = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const currentMonthTotal = (groups[currentMonthKey] || []).reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="content" style={{ maxWidth: 860 }}>
      <div className="page-hdr">
        <div className="page-hdr-title">💵 Наличка</div>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -8, marginBottom: 14 }}>
        Каждая выплата учитывается в том месяце, когда она реально получена — независимо от того, закрыта ли заявка.
        По одной заявке может быть несколько выплат (например, часть при отправке в запчасти, остаток при закрытии).
      </p>

      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14, flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Текущий месяц</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#16a34a" }}>{currentMonthTotal.toLocaleString("ru-RU")} ₽</div>
        </div>
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14, flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Всего за всё время</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{totalAll.toLocaleString("ru-RU")} ₽</div>
        </div>
      </div>

      {isAdminView && (
        <div style={{ marginBottom: 14 }}>
          <select className="form-select" value={mechFilter} onChange={e => setMechFilter(e.target.value)}>
            <option value="all">Все механики</option>
            {mechanics.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ color: "var(--muted)", padding: 30, textAlign: "center" }}>Загрузка...</div>
      ) : monthKeys.length === 0 ? (
        <div style={{ color: "var(--muted)", padding: 30, textAlign: "center" }}>Пока нет наличных выплат</div>
      ) : (
        monthKeys.map(key => {
          const items = groups[key].slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          const monthSum = items.reduce((s, p) => s + Number(p.amount || 0), 0);
          return (
            <div key={key} style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div className="sec-label" style={{ textTransform: "capitalize" }}>{monthLabel(key)}</div>
                <div style={{ fontWeight: 700, color: "#16a34a" }}>{monthSum.toLocaleString("ru-RU")} ₽</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {items.map(p => (
                  <div key={p.id} style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700 }}>#{p.ticket_id}</span>
                    <span style={{ flex: 1, fontSize: 13, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 100 }}>{p.ticket_text}</span>
                    <span style={{ fontSize: 11, color: "var(--muted)", background: "var(--bg)", padding: "2px 7px", borderRadius: 4 }}>
                      {CASH_ACTION_LABELS[p.action] || p.action}
                    </span>
                    {isAdminView && <span style={{ fontSize: 12, color: "var(--muted)" }}>👷 {p.mechanic_name || "—"}</span>}
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>{fmt(p.created_at)}</span>
                    <span style={{ fontWeight: 700, color: "#16a34a" }}>{Number(p.amount || 0).toLocaleString("ru-RU")} ₽</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function ReportsPage({ tickets, mechanics, user }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo]     = useState(today);
  const [mechFilter, setMechFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [format, setFormat] = useState("table"); // "table" | "cards"
  const [fields, setFields] = useState({
    id: true, date: true, text: true, mechanic: true,
    status: true, parts_comments: true, history: false,
  });
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [backing, setBacking] = useState(false);
  const [bakInfo, setBakInfo] = useState(null);
  const [bakStep, setBakStep] = useState("");
  const isSuper = user?.role === "superadmin";

  useEffect(() => {
    if (!isSuper) return;
    let live = true;
    api.get("/api/backup/status").then(r => { if (live) setBakInfo(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [isSuper]);

  const downloadBackup = async () => {
    if (!window.confirm("Скачать полный архив на этот компьютер: программа, база и все фото? Сборка займёт несколько минут, окно можно не закрывать.")) return;
    setBacking(true);
    setBakStep("Старт");
    try {
      await api.post("/api/backup/start");
      let job = { state: "running" };
      while (job.state === "running" || job.state === "idle") {
        await new Promise(r => setTimeout(r, 2000));
        const r = await api.get("/api/backup/job");
        job = r.data || {};
        if (job.step) setBakStep(job.step);
        if (job.state === "ready" || job.state === "error") break;
      }
      if (job.state === "error") throw new Error(job.error || "Сборка архива не удалась");
      if (job.state !== "ready" || !job.ticket) throw new Error("Архив не готов");
      const a = document.createElement("a");
      a.href = job.publicUrl || `/api/backup/file?ticket=${encodeURIComponent(job.ticket)}`;
      a.download = job.filename || "servis-kontrol-backup.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setBakStep("Скачивание началось");
    } catch (e) {
      let msg = e.message || "неизвестная ошибка";
      if (e.response?.data?.error) msg = e.response.data.error;
      alert("Не удалось скачать бэкап: " + msg);
    }
    setBacking(false);
  };

  const toggleField = (f) => setFields(prev => ({ ...prev, [f]: !prev[f] }));

  const filtered = tickets.filter(t => {
    const d = t.created_at?.slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo   && d > dateTo)   return false;
    if (mechFilter !== "all" && String(t.assigned_to) !== mechFilter) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    return true;
  });

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" })
      + " " + d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      let deletedTickets = [];
      if (includeDeleted && user?.role === "superadmin") {
        try {
          const res = await api.get("/api/tickets/deleted");
          deletedTickets = res.data || [];
        } catch (e) {
          console.error("Не удалось загрузить журнал удалённых заявок", e);
        }
      }

      const subtitle = [
        dateFrom && dateTo ? `Период: ${dateFrom} — ${dateTo}` : "",
        mechFilter !== "all" ? `Механик: ${mechanics.find(m => String(m.id) === mechFilter)?.name}` : "Все механики",
        statusFilter !== "all" ? `Статус: ${STATUS_LABELS_ALL[statusFilter]}` : "Все статусы",
        `Заявок: ${filtered.length}`,
        `Сформирован: ${fmtDate(new Date().toISOString())}`,
      ].filter(Boolean).join("  ·  ");

      let bodyHTML = "";

      if (format === "table") {
        const cols = [];
        if (fields.id)      cols.push(["№",         t => `#${t.id}`]);
        if (fields.date)    cols.push(["Дата",       t => fmtDate(t.created_at)]);
        if (fields.text)    cols.push(["Описание",   t => t.text || ""]);
        if (fields.mechanic) cols.push(["Механик",  t => t.assigned_name || "—"]);
        if (fields.status)  cols.push(["Статус",    t => STATUS_LABELS_ALL[t.status] || t.status]);
        if (fields.parts_comments) cols.push(["К запчастям", t => (t.parts_comments||[]).map(c=>c.text).join("; ") || "—"]);
        if (fields.history) cols.push(["История",   t => (t.history||[]).map(h=>`${fmtDate(h.at)}: ${h.text}`).join("<br/>")]);

        bodyHTML = `<table>
          <thead><tr>${cols.map(([h])=>`<th>${h}</th>`).join("")}</tr></thead>
          <tbody>${filtered.map((t,i)=>`<tr class="${i%2?"alt":""}">
            ${cols.map(([,fn])=>`<td>${fn(t)}</td>`).join("")}
          </tr>`).join("")}</tbody>
        </table>`;
      } else {
        bodyHTML = filtered.map(t => {
          const statusColor = { closed:"#6b7280", in_work:"#2563eb", awaiting_parts:"#7c3aed", unassigned:"#f59e0b", service_center:"#2563eb" };
          const sc = statusColor[t.status] || "#333";
          let card = `<div class="card">
            <div class="card-head">`;
          if (fields.id)      card += `<span class="card-id">#${t.id}</span>`;
          if (fields.date)    card += `<span class="card-date">${fmtDate(t.created_at)}</span>`;
          if (fields.mechanic && t.assigned_name) card += `<span class="card-mech">👷 ${t.assigned_name}</span>`;
          if (fields.status)  card += `<span class="card-status" style="color:${sc};border-color:${sc}">${STATUS_LABELS_ALL[t.status]}</span>`;
          card += `</div><div class="card-body">`;
          if (fields.text)    card += `<div class="card-text">${t.text || ""}</div>`;
          if (fields.parts_comments && t.parts_comments?.length) {
            card += `<div class="parts-block"><div class="parts-label">📦 К запчастям:</div>`;
            card += t.parts_comments.map(c=>`<div class="parts-item">— ${c.text}</div>`).join("");
            card += `</div>`;
          }
          if (fields.history && t.history?.length) {
            card += `<div class="history-block"><div class="history-label">ИСТОРИЯ</div>`;
            card += t.history.map(h=>`<div class="history-item"><span class="h-time">${fmtDate(h.at)}</span><span>${h.text}</span></div>`).join("");
            card += `</div>`;
          }
          card += `</div></div>`;
          return card;
        }).join("");
      }

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
        <title>Отчёт по заявкам</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 20px; }
          h1 { font-size: 16px; margin-bottom: 4px; }
          .sub { font-size: 10px; color: #666; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f3f4f6; padding: 6px 8px; text-align: left; border-bottom: 2px solid #d1d5db; font-size: 11px; }
          td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; font-size: 11px; }
          tr.alt td { background: #f9fafb; }
          .card { border: 1px solid #e5e7eb; border-radius: 6px; margin-bottom: 10px; page-break-inside: avoid; overflow: hidden; }
          .card-head { background: #f3f4f6; padding: 6px 10px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
          .card-id { font-weight: bold; }
          .card-date { color: #666; }
          .card-status { font-size: 10px; font-weight: bold; border: 1.5px solid; border-radius: 4px; padding: 1px 6px; margin-left: auto; }
          .card-body { padding: 8px 10px; }
          .card-text { margin-bottom: 6px; line-height: 1.5; }
          .parts-block { background: #faf5ff; border-left: 3px solid #7c3aed; padding: 5px 8px; border-radius: 0 4px 4px 0; margin-bottom: 6px; }
          .parts-label { font-weight: bold; color: #7c3aed; font-size: 10px; margin-bottom: 2px; }
          .parts-item { font-size: 11px; }
          .history-block { border-top: 1px solid #e5e7eb; padding-top: 6px; margin-top: 4px; }
          .history-label { font-size: 9px; font-weight: bold; color: #999; text-transform: uppercase; margin-bottom: 3px; }
          .history-item { display: flex; gap: 8px; font-size: 10px; margin-bottom: 2px; }
          .h-time { color: #999; white-space: nowrap; min-width: 100px; }
          .deleted-section { margin-top: 24px; border-top: 2px solid #dc2626; padding-top: 10px; }
          .deleted-title { font-size: 13px; font-weight: bold; color: #dc2626; margin-bottom: 8px; }
          .deleted-item { border: 1px solid #fecaca; background: #fef2f2; border-radius: 5px; padding: 6px 10px; margin-bottom: 6px; font-size: 11px; }
          .deleted-item .d-id { font-weight: bold; margin-right: 8px; }
          .deleted-item .d-meta { color: #7f1d1d; font-size: 10px; margin-top: 2px; }
          .footer { margin-top: 16px; font-size: 10px; color: #999; border-top: 1px solid #e5e7eb; padding-top: 8px; }
          @media print { @page { margin: 15mm; } }
        </style>
      </head><body>
        <h1>Конвектомат Сервис — Отчёт по заявкам</h1>
        <div class="sub">${subtitle}</div>
        ${bodyHTML}
        ${deletedTickets.length ? `
        <div class="deleted-section">
          <div class="deleted-title">🗑 Журнал удалённых заявок (${deletedTickets.length})</div>
          ${deletedTickets.map(t => `
            <div class="deleted-item">
              <span class="d-id">#${t.id}</span>${(t.text || "").slice(0, 150)}
              <div class="d-meta">
                Удалена: ${fmtDate(t.deleted_at)} · Кем: ${t.deleted_by_name || "—"} ·
                Причина: ${t.delete_reason || "—"} · Статус на момент удаления: ${STATUS_LABELS_ALL[t.status] || t.status}
              </div>
            </div>
          `).join("")}
        </div>` : ""}
        <div class="footer">Итого заявок: ${filtered.length} · Закрытых: ${filtered.filter(t=>t.status==="closed").length} · В работе: ${filtered.filter(t=>t.status==="in_work").length}${deletedTickets.length ? ` · Удалённых: ${deletedTickets.length}` : ""}</div>
      </body></html>`;

      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    } catch(e) {
      console.error(e);
      alert("Ошибка генерации PDF: " + e.message);
    }
    setGenerating(false);
  };

  const FIELD_LABELS = {
    id: "№ заявки", date: "Дата", text: "Описание", mechanic: "Механик",
    status: "Статус", parts_comments: "Комментарии к запчастям", history: "История изменений",
  };

  return (
    <div className="content" style={{ maxWidth: 860 }}>
      <div className="page-hdr">
        <div className="page-hdr-title">📊 Конструктор отчётов</div>
      </div>

      {isSuper && (
      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14, marginBottom: 14, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Полный бэкап</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, lineHeight: 1.4 }}>
            Один клик — на компьютер скачается архив: программа, база и все фото. На сервере живые данные не трогаются. Копия архива лежит 1 час, потом удаляется. Внутри файл «ЧИТАЙ_МЕНЯ».
          </div>
          {bakInfo && (
            <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 6, lineHeight: 1.45 }}>
              Фото: {bakInfo.uploads_files || 0} шт. ≈ {bakInfo.uploads_mb || 0} МБ
              {" · "}pg_dump: {bakInfo.pg_dump ? "есть" : "нет, будет запасной дамп"}
              {typeof bakInfo.tmp_free_mb === "number" ? ` · диск бэкапа: ${bakInfo.tmp_free_mb} МБ свободно` : ""}
              {bakInfo.warnings?.length ? (
                <div style={{ color: "#b45309", marginTop: 4 }}>{bakInfo.warnings.join(" · ")}</div>
              ) : null}
            </div>
          )}
        </div>
        <button className="btn btn-primary" disabled={backing} onClick={downloadBackup} style={{ whiteSpace: "nowrap" }}>
          {backing ? (bakStep ? bakStep : "Собираем архив…") : "⬇ Скачать всё"}
        </button>
      </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        {/* Период */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Период</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" className="form-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ flex: 1 }} />
            <span style={{ color: "var(--muted)", fontSize: 13 }}>—</span>
            <input type="date" className="form-input" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ flex: 1 }} />
          </div>
        </div>

        {/* Фильтры */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Фильтры</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select className="form-select" value={mechFilter} onChange={e => setMechFilter(e.target.value)} style={{ flex: 1 }}>
              <option value="all">Все механики</option>
              {mechanics.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
            </select>
            <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ flex: 1 }}>
              <option value="all">Все статусы</option>
              <option value="unassigned">Нераспределённые</option>
              <option value="in_work">В работе</option>
              <option value="awaiting_parts">Ожидаем запчасти</option>
              <option value="parts_clarification">Уточнение запчастей</option>
              <option value="parts_ordered">Запчасти заказаны</option>
              <option value="approval">Согласование</option>
              <option value="approval_sent">Отправлено на согласование</option>
              <option value="approval_approved">Согласовано</option>
              <option value="closed">Закрытые</option>
              <option value="service_center">Сервис центр</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        {/* Поля */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Что включить</div>
          {Object.entries(FIELD_LABELS).map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={fields[key]} onChange={() => toggleField(key)}
                style={{ width: 15, height: 15, accentColor: "var(--blue)" }} />
              {label}
            </label>
          ))}
          {user?.role === "superadmin" && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, paddingTop: 10,
              borderTop: "1px dashed var(--border)", cursor: "pointer", fontSize: 13, color: "var(--red)" }}>
              <input type="checkbox" checked={includeDeleted} onChange={() => setIncludeDeleted(v => !v)}
                style={{ width: 15, height: 15, accentColor: "var(--red)" }} />
              🗑 Журнал удалённых заявок
            </label>
          )}
        </div>

        {/* Формат */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Формат</div>
          {[["table", "📋 Таблица", "Компактно, одна строка = одна заявка"], ["cards", "🗂 Карточки", "Развёрнуто, каждая заявка с деталями"]].map(([val, title, desc]) => (
            <label key={val} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, cursor: "pointer",
              background: format === val ? "var(--blue-bg)" : "var(--bg)",
              border: `1.5px solid ${format === val ? "var(--blue)" : "var(--border)"}`,
              borderRadius: 8, padding: "10px 12px" }}>
              <input type="radio" name="format" value={val} checked={format === val} onChange={() => setFormat(val)}
                style={{ marginTop: 2, accentColor: "var(--blue)" }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{desc}</div>
              </div>
            </label>
          ))}

          <div style={{ marginTop: 12, padding: "10px 12px", background: "var(--bg)", borderRadius: 8, fontSize: 12, color: "var(--muted)" }}>
            Найдено заявок: <strong style={{ color: "var(--text)", fontSize: 14 }}>{filtered.length}</strong>
          </div>
        </div>
      </div>

      {/* Превью */}
      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14, marginBottom: 14 }}>
        <div className="sec-label" style={{ marginBottom: 10 }}>Превью (первые 3 заявки)</div>
        {filtered.length === 0 ? (
          <div className="empty" style={{ padding: "24px 0" }}>
            <div className="empty-icon">📭</div>
            <div className="empty-text">Нет заявок по выбранным фильтрам</div>
          </div>
        ) : format === "table" ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--bg)" }}>
                  {fields.id && <th style={thStyle}>№</th>}
                  {fields.date && <th style={thStyle}>Дата</th>}
                  {fields.text && <th style={thStyle}>Описание</th>}
                  {fields.mechanic && <th style={thStyle}>Механик</th>}
                  {fields.status && <th style={thStyle}>Статус</th>}
                  {fields.parts_comments && <th style={thStyle}>К запчастям</th>}
                  {fields.history && <th style={thStyle}>История</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 3).map((t, i) => (
                  <tr key={t.id} style={{ background: i % 2 ? "var(--bg)" : "var(--surface)" }}>
                    {fields.id && <td style={tdStyle}>#{t.id}</td>}
                    {fields.date && <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{fmtDate(t.created_at)}</td>}
                    {fields.text && <td style={tdStyle}>{(t.text || "").slice(0, 80)}{(t.text||"").length > 80 ? "…" : ""}</td>}
                    {fields.mechanic && <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{t.assigned_name || "—"}</td>}
                    {fields.status && <td style={tdStyle}>
                      <span style={{ background: (getStatusMeta(t).bg || "#eee"), color: (getStatusMeta(t).color || "#333"), padding: "2px 6px", borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {STATUS_LABELS_ALL[t.status]}
                      </span>
                    </td>}
                    {fields.parts_comments && <td style={tdStyle}>{(t.parts_comments || []).map(c => c.text).join("; ") || "—"}</td>}
                    {fields.history && <td style={tdStyle}>{(t.history || []).length} записей</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 3 && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>... и ещё {filtered.length - 3} заявок в PDF</div>}
          </div>
        ) : (
          <div>
            {filtered.slice(0, 2).map(t => (
              <div key={t.id} style={{ border: "1px solid var(--border)", borderRadius: 8, marginBottom: 10, overflow: "hidden" }}>
                <div style={{ background: "var(--bg)", padding: "7px 12px", display: "flex", gap: 10, alignItems: "center", fontSize: 12 }}>
                  {fields.id && <strong>#{t.id}</strong>}
                  {fields.date && <span style={{ color: "var(--muted)" }}>{fmtDate(t.created_at)}</span>}
                  {fields.mechanic && t.assigned_name && <span>👷 {t.assigned_name}</span>}
                  {fields.status && <span style={{ marginLeft: "auto", background: getStatusMeta(t).bg, color: getStatusMeta(t).color, padding: "2px 7px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{getStatusMeta(t).label}</span>}
                </div>
                <div style={{ padding: "10px 12px", fontSize: 12 }}>
                  {fields.text && <div style={{ marginBottom: 6, lineHeight: 1.5 }}>{t.text}</div>}
                  {fields.parts_comments && t.parts_comments?.length > 0 && (
                    <div style={{ background: "#faf5ff", borderLeft: "3px solid #7c3aed", padding: "5px 10px", borderRadius: "0 5px 5px 0", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 3 }}>📦 К запчастям:</div>
                      {t.parts_comments.map((c, i) => <div key={i} style={{ fontSize: 12 }}>— {c.text}</div>)}
                    </div>
                  )}
                  {fields.history && t.history?.length > 0 && (
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", marginBottom: 4, textTransform: "uppercase" }}>История</div>
                      {(t.history || []).map((h, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, fontSize: 11, marginBottom: 2 }}>
                          <span style={{ color: "var(--muted2)", whiteSpace: "nowrap" }}>{fmtDate(h.at)}</span>
                          <span>{h.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {filtered.length > 2 && <div style={{ fontSize: 12, color: "var(--muted)" }}>... и ещё {filtered.length - 2} заявок в PDF</div>}
          </div>
        )}
      </div>

      <button className="btn btn-primary" disabled={generating || filtered.length === 0} onClick={generatePDF}
        style={{ width: "100%", justifyContent: "center", padding: "12px 0", fontSize: 14 }}>
        {generating ? "⏳ Формируем отчёт..." : `📥 Открыть отчёт PDF (${filtered.length} заявок)`}
      </button>
    </div>
  );
}

const thStyle = { padding: "7px 10px", textAlign: "left", borderBottom: "2px solid var(--border2)", fontWeight: 700, fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" };
const tdStyle = { padding: "6px 10px", borderBottom: "1px solid var(--border)", fontSize: 12, verticalAlign: "top" };
